// @ts-nocheck
import { BuilderReportStage } from "./BuilderReportStage";
import { DatabookQnaStage } from "./DatabookQnaStage";
/**
 * Databook Builder — vendored pivot engine (prototype v54).
 * Dense, dynamically-typed prototype ported as an isolated module.
 * Runs under the TS/JSX build; @ts-nocheck matches how VendorPortal.jsx is handled.
 * Integration wrapper + stage shell around it are fully typed separately.
 */
import { AgentDatabookChat } from "./AgentDatabookChat";
import { useAgentDatabookChat } from "./useAgentDatabookChat";
import React, { useCallback, useMemo, useState, useRef, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { X, ChevronDown, ChevronRight, ChevronLeft, ChevronUp, Sigma, Check, Search, Filter, GripVertical, MoreHorizontal, LayoutTemplate, Plus, FileSpreadsheet, FileText, Sparkles, Eye, Download, Copy, RotateCcw } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { backendErrorMessage } from "@/lib/backendErrorMessage";
import { BackendError } from "@/lib/backend";
import { ProjectHeader } from "@/components/ProjectHeader";
import {
  deleteProjectDocument,
  documentBatchUploadFeedback,
  getDocumentDownloadUrl,
  listProjectDocuments,
  uploadProjectDocuments,
} from "@/lib/api/documents";
import { getProjectWithRfi, updateProject } from "@/lib/api/projects";
import {
  askBuilderSourceAnalysis,
  downloadBasisComponentSources,
  downloadDatabookVersion,
  listDatabookVersions,
  downloadPrebuildQuestionSource,
  getBuilderAnalysisSettings,
  getDatabookJobStatus,
  getDatabookVersionOutcome,
  getBuilderAgentRequest,
  getDatabookSheet,
  getDatabookSheets,
  getPrebuildReviewCoverage,
  getPrebuildReviewAskDillyHistory,
  getPrebuildReviewLegalEntityAttachmentStatus,
  getPrebuildReviewBasisOptions,
  getPrebuildReviewLegalStructure,
  getPrebuildReview,
  refreshPrebuildReviewBasis,
  retryPrebuildReview,
  startPrebuildReview,
  savePrebuildReviewLegalEntityAttachments,
  submitPrebuildReviewAnswers,
  updatePrebuildReviewLegalStructure,
  updateBuilderAnalysisSettings,
} from "@/lib/api/databook";
import { BasisOfPreparationStage } from "./BasisOfPreparationStage";
import {
  LegalEntitiesStage,
  type LegalEntitiesLoadState,
} from "./LegalEntitiesStage";
import { BuilderStageChat } from "./BuilderStageChat";
import {
  appendBuilderChatTurn,
  builderChatMessages,
  updateBuilderChatRequest,
} from "./builderChatHistory";
import {
  basisLoadState,
  canLoadBasisOptions,
  reconcileBasisSelection,
  shouldRefreshBasis,
  type BasisOptionsLoadState,
  type BasisSelectionState,
} from "./basisOptions";
import {
  DatabookUploadStage,
  type DatabookUploadDocument,
} from "./DatabookUploadStage";
import {
  analysisPeriodEndFiscalYear,
  analysisPeriodEndSelection,
  BUILDER_PERIOD_OPTIONS,
} from "./analysisPeriods";
import { BuilderStageNavigation } from "./BuilderStageNavigation";
import {
  connectedDatabookBuilderStages,
  loadLastBuilderStage,
  postAnalysisBuilderStage,
  saveLastBuilderStage,
  shouldShowQuestionsStage,
} from "./builderStages";
import { QuestionsStage } from "./QuestionsStage";
import {
  DatabookGenerationStage,
} from "./DatabookGenerationStage";
import { useBuilderDatabook } from "./useBuilderDatabook";
import { SourceCoverageStage } from "./SourceCoverageStage";
import { DatabookVersionManagement } from "./DatabookVersionManagement";
import {
  activeDatabookSheetRowOffset,
  DATABOOK_SHEET_STALE_TIME,
  databookSheetQueryKey,
  loadLastDatabookSheet,
  saveLastDatabookSheet,
  selectedDatabookSheet,
  toggleDatabookOutlineGroup,
} from "./databookPreviewState";
import {
  databookCellSelectionA1,
} from "./databookCellSelection";
import {
  activeBuilderAgentRequest,
  builderAgentPollingInterval,
} from "./builderAgentRequest";

const LOCAL_STAGE_CHATS = {
  upload: {
    title: "Ask Dilli, upload",
    intro: "Ask what to upload or how the data-room setup affects the review.",
    placeholder: "Ask about the upload",
  },
  coverage: {
    title: "Ask Dilli, source coverage",
    intro: "Ask about a missing period, source type, or coverage gap.",
    placeholder: "Ask about source coverage",
  },
  questions: {
    title: "Ask Dilli, questions",
    intro: "Ask why a question was raised or what source evidence is missing.",
    placeholder: "Ask about the review questions",
  },
  ents: {
    title: "Ask Dilli, entity chart",
    intro: (
      "Ask about the legal entity structure, transaction perimeter, "
      + "or business groupings."
    ),
    placeholder: "Type a change or question",
  },
  basis: {
    title: "Ask Dilli, basis of preparation",
    intro: "Ask why this Basis was selected or how its sources are combined.",
    placeholder: "Ask about the Basis",
  },
  book: {
    title: "Ask Dilli, databook",
    intro: "Ask how the reviewed sources and Basis feed into the databook.",
    placeholder: "Ask about the databook",
  },
  qna: {
    title: "Ask Dilli, Q&A",
    intro: "Ask about an open question, its source evidence, or the next response.",
    placeholder: "Ask about Q&A",
  },
};

const BASIS_REFRESH_POLL_INTERVAL_MS = 2000;

// ==================================================================
// Periods, entities, sites
// ==================================================================
const P = ["FY22", "FY23", "FY24", "FY25", "YTD26"];
const DEFAULT_BUILDER_SETTINGS = {
  financialYearEnd: "31 March",
  scopeFrom: P[0],
  scopeTo: "FY25",
  includeYtd: true,
  includeForecast: false,
  forecastYears: "2 years",
};
const FINANCIAL_YEAR_END_BY_MONTH = [
  "31 January",
  "28 February",
  "31 March",
  "30 April",
  "31 May",
  "30 June",
  "31 July",
  "31 August",
  "30 September",
  "31 October",
  "30 November",
  "31 December",
];
const financialYearEndMonth = (label) =>
  FINANCIAL_YEAR_END_BY_MONTH.indexOf(label) + 1;
const financialYearEndLabel = (month) =>
  FINANCIAL_YEAR_END_BY_MONTH[month - 1] || DEFAULT_BUILDER_SETTINGS.financialYearEnd;
const periodFiscalYear = (label) => {
  const digits = String(label || "").match(/\d+/)?.[0];
  if (!digits) return null;
  const value = Number(digits);
  return value < 100 ? 2000 + value : value;
};
const forecastYearCount = (label) => {
  const value = Number.parseInt(String(label || ""), 10);
  return Number.isInteger(value) ? value : null;
};
const NZ = "Start Demo NZ", AU = "Start Demo Australia", EX = "Start Demo Exports", EL = "Eliminations";
const ENTS = [NZ, AU, EX, EL];
const SITES = ["Hamilton", "Auckland", "Melbourne", "Export hub", "Head office"];
const SITEW = { [NZ]: { Hamilton: 0.6, Auckland: 0.4 }, [AU]: { Melbourne: 1 }, [EX]: { "Export hub": 1 }, [EL]: { "Head office": 1 } };

function spread(base, entW, vecs) {
  const out = [];
  Object.entries(entW).forEach(([ent, ef]) => {
    Object.entries(SITEW[ent]).forEach(([site, sf]) => {
      const v = {};
      Object.entries(vecs).forEach(([k, arr]) => { v[k] = arr.map((x) => x * ef * sf); });
      out.push({ ...base, entity: ent, site, v });
    });
  });
  return out;
}
const flat = (arr) => arr.reduce((a, b) => a.concat(b), []);

// ==================================================================
// Fact stores
// ==================================================================
const GM = { "Peach Teat": 0.45, "Milk Tankers": 0.3, Troughs: 0.28, Drinkers: 0.35, Feeders: 0.32, Traps: 0.5, Letterboxes: 0.38, "Animal Health": 0.4, Fencing: 0.3, Freight: 0, Intercompany: 0 };
const SW = {
  "Peach Teat": { [NZ]: 0.62, [AU]: 0.23, [EX]: 0.15 }, "Milk Tankers": { [NZ]: 0.78, [AU]: 0.22 },
  Troughs: { [NZ]: 1 }, Drinkers: { [NZ]: 0.85, [AU]: 0.15 }, Feeders: { [NZ]: 1 },
  Traps: { [NZ]: 0.9, [EX]: 0.1 }, Letterboxes: { [NZ]: 1 }, "Animal Health": { [NZ]: 1 },
  Fencing: { [NZ]: 1 }, Freight: { [NZ]: 0.55, [AU]: 0.2, [EX]: 0.25 },
};
const SKU = (name, group, colour, connection, pack, capacity, rev, vol) => ({ name, group, colour, connection, pack, capacity, rev, vol });
const SKUS = [
  SKU("Peach Teat Pink Thread - 50", "Peach Teat", "Pink", "Thread", 50, null, [0, 0, 96, 2439, 1775], [0, 0, 24900, 612000, 454881]),
  SKU("Peach Teat Black Threaded - 50", "Peach Teat", "Black", "Thread", 50, null, [0, 0, 49, 730, 761], [0, 0, 12200, 183000, 187254]),
  SKU("Peach Teat Black Pull Thru - 50", "Peach Teat", "Black", "Pull Thru", 50, null, [0, 0, 43, 1305, 526], [0, 0, 10500, 327000, 127950]),
  SKU("Peach Teat Pink Thread Bag 10", "Peach Teat", "Pink", "Thread", 10, null, [0, 0, 0, 192, 435], [0, 0, 0, 48000, 113810]),
  SKU("Peach Teat Black Pull Thru - 10", "Peach Teat", "Black", "Pull Thru", 10, null, [0, 0, 0, 175, 433], [0, 0, 0, 44000, 112560]),
  SKU("Peach Teat Blk Pull Thru - 5", "Peach Teat", "Black", "Pull Thru", 5, null, [0, 0, 0, 98, 243], [0, 0, 0, 25000, 63000]),
  SKU("Peach Teat Pink Thread - 10DL", "Peach Teat", "Pink", "Thread", 10, null, [0, 0, 0, 60, 144], [0, 0, 0, 1900, 4650]),
  SKU("Peach Teat Pink - Export", "Peach Teat", "Pink", "Thread", null, null, [443, 619, 780, 0, 0], [137400, 179000, 220900, 0, 0]),
  SKU("Bagged Pull Thru Peach Teats, A5034 DL", "Peach Teat", null, "Pull Thru", null, null, [523, 128, 408, 0, 0], [170100, 38400, 126100, 0, 0]),
  SKU("Threaded Peach Teat Cap, PCAP", "Peach Teat", null, "Thread", null, null, [246, 168, 163, 177, 246], [75987, 43641, 40400, 44000, 55430]),
  SKU("Teat Spanner", "Peach Teat", null, null, null, null, [12, 14, 15, 16, 18], [3000, 3400, 3600, 3800, 4200]),
  SKU("Milk Tanker Feeder 60 1000 L", "Milk Tankers", null, null, null, 1000, [260, 291, 195, 133, 411], [33, 35, 24, 16, 40]),
  SKU("Milk Tanker Feeder 80 1000 L", "Milk Tankers", null, null, null, 1000, [165, 162, 117, 115, 410], [15, 14, 10, 10, 31]),
  SKU("Milk Tanker Mixer 70 1000 L", "Milk Tankers", null, null, null, 1000, [0, 0, 45, 88, 198], [0, 0, 4, 8, 17]),
  SKU("Towable 250 L Tanker", "Milk Tankers", null, null, null, 250, [120, 140, 160, 180, 341], [40, 45, 50, 56, 107]),
  SKU("80 L Trough Incl. Cover and Val", "Troughs", null, null, null, 80, [255, 265, 234, 219, 134], [2011, 1994, 1750, 1600, 939]),
  SKU("Open Trough 60 Teat", "Troughs", null, null, null, null, [160, 216, 133, 118, 167], [33, 39, 25, 22, 27]),
  SKU("Open Trough 50 Teat 500 L", "Troughs", null, null, null, 500, [286, 198, 104, 99, 101], [98, 59, 30, 28, 26]),
  SKU("Trough Float Valve", "Troughs", null, null, null, null, [30, 32, 28, 35, 38], [2400, 2500, 2200, 2700, 2900]),
  SKU("Trough Cover 80 L", "Troughs", null, null, null, 80, [22, 24, 21, 26, 28], [1100, 1150, 1000, 1250, 1350]),
  SKU("Poly Drinker 8 L", "Drinkers", null, null, null, 8, [166, 154, 90, 128, 143], [1959, 1760, 900, 1300, 1450]),
  SKU("Replacement Valve Kit", "Drinkers", null, null, null, null, [18, 20, 22, 24, 26], [800, 880, 950, 1020, 1100]),
  SKU("Calf Feeder 6 Teat", "Feeders", null, null, null, null, [45, 48, 52, 55, 58], [420, 440, 460, 480, 500]),
  SKU("Meal Feeder 40 L", "Feeders", null, null, null, 40, [60, 64, 58, 66, 70], [300, 310, 280, 320, 340]),
  SKU("Possum Trap", "Traps", null, null, null, null, [349, 294, 255, 194, 176], [7853, 6335, 5100, 3900, 3500]),
  SKU("WP Rural Letterbox Grey Black", "Letterboxes", null, null, null, null, [95, 110, 125, 140, 170], [900, 1050, 1180, 1320, 1624]),
  SKU("Drench Gun 30 ml", "Animal Health", null, null, null, null, [40, 38, 36, 42, 44], [520, 500, 470, 540, 560]),
  SKU("Fence Standoff Pack 10", "Fencing", null, null, 10, null, [25, 27, 24, 28, 30], [1500, 1600, 1400, 1700, 1800]),
  SKU("Freight Out - Export", "Freight", null, null, null, null, [331, 273, 123, 149, 276], [0, 0, 0, 0, 0]),
];
const salesAtoms = flat(SKUS.map((m, i) =>
  spread({ name: m.name, idx: i, attrs: { group: m.group, colour: m.colour, connection: m.connection, pack: m.pack, capacity: m.capacity } },
    SW[m.group] || { [NZ]: 1 },
    { rev: m.rev, vol: m.vol, gp: m.rev.map((v) => v * (GM[m.group] || 0.3)) })
));
const ICV = [180, 190, 200, 210, 220];
const icAttrs = { group: "Intercompany", colour: null, connection: null, pack: null, capacity: null };
salesAtoms.push({ name: "Intercompany sales, NZ to Australia", idx: 99, attrs: icAttrs, entity: NZ, site: "Head office", v: { rev: ICV, vol: [0, 0, 0, 0, 0], gp: [0, 0, 0, 0, 0] } });
salesAtoms.push({ name: "Intercompany sales, NZ to Australia", idx: 99, attrs: icAttrs, entity: EL, site: "Head office", v: { rev: ICV.map((x) => -x), vol: [0, 0, 0, 0, 0], gp: [0, 0, 0, 0, 0] } });

const EW = { [NZ]: 0.82, [AU]: 0.13, [EX]: 0.05 };
const PNL = [
  ["Product revenue", "Revenue", "Variable", [9800, 9200, 8900, 11200, 12600]],
  ["Freight revenue", "Revenue", "Variable", [420, 395, 360, 410, 455]],
  ["Other income", "Revenue", "Fixed", [85, 60, 75, 90, 70]],
  ["Materials", "COGS", "Variable", [-4100, -3900, -3750, -4700, -5300]],
  ["Direct labour", "COGS", "Variable", [-1350, -1290, -1260, -1520, -1680]],
  ["Freight inwards", "COGS", "Variable", [-410, -380, -365, -460, -510]],
  ["Salaries and wages", "Overheads", "Fixed", [-1650, -1700, -1760, -1850, -1950]],
  ["Rent and utilities", "Overheads", "Fixed", [-380, -390, -400, -415, -430]],
  ["Marketing", "Overheads", "Fixed", [-210, -185, -160, -240, -280]],
  ["Repairs and maintenance", "Overheads", "Fixed", [-145, -160, -150, -170, -185]],
  ["Insurance", "Overheads", "Fixed", [-95, -102, -110, -121, -130]],
  ["Professional fees", "Overheads", "Fixed", [-120, -95, -140, -180, -155]],
  ["Other opex", "Overheads", "Fixed", [-230, -215, -205, -245, -260]],
  ["Depreciation", "Below EBITDA", "Fixed", [-420, -430, -445, -460, -475]],
  ["Interest expense", "Below EBITDA", "Fixed", [-180, -165, -150, -210, -235]],
];
const pnlAtoms = flat(PNL.map(([n, st, nat, amt], i) => spread({ name: n, idx: i, attrs: { statement: st, nature: nat } }, EW, { amt })));
pnlAtoms.push({ name: "Intercompany sales", idx: 50, attrs: { statement: "Revenue", nature: "Variable" }, entity: NZ, site: "Head office", v: { amt: ICV } });
pnlAtoms.push({ name: "Intercompany sales", idx: 50, attrs: { statement: "Revenue", nature: "Variable" }, entity: EL, site: "Head office", v: { amt: ICV.map((x) => -x) } });

const BS = [
  ["Cash and equivalents", "Current assets", "Non-trade", [820, 640, 910, 750, 1020]],
  ["Trade receivables", "Current assets", "Trade WC", [1450, 1320, 1280, 1690, 1840]],
  ["Inventory", "Current assets", "Trade WC", [2100, 2260, 2050, 2480, 2710]],
  ["Prepayments", "Current assets", "Trade WC", [140, 150, 135, 160, 175]],
  ["Property plant and equipment", "Non-current assets", "Non-trade", [3200, 3350, 3280, 3410, 3520]],
  ["Intangibles", "Non-current assets", "Non-trade", [260, 240, 220, 200, 180]],
  ["Trade payables", "Current liabilities", "Trade WC", [-980, -1040, -960, -1180, -1290]],
  ["Accrued expenses", "Current liabilities", "Trade WC", [-310, -290, -320, -355, -380]],
  ["Employee provisions", "Current liabilities", "Trade WC", [-260, -275, -290, -310, -330]],
  ["Borrowings", "Non-current liabilities", "Financing", [-2400, -2250, -2100, -2350, -2500]],
  ["Related party loan", "Non-current liabilities", "Financing", [-500, -500, -500, -500, -500]],
  ["Share capital and reserves", "Equity", "Financing", [-3520, -3605, -3705, -3995, -4445]],
];
const bsAtoms = flat(BS.map(([n, sec, nwc, bal], i) => spread({ name: n, idx: i, attrs: { section: sec, nwc } }, EW, { bal })));
bsAtoms.push({ name: "Intercompany loan receivable", idx: 40, attrs: { section: "Non-current assets", nwc: "Non-trade" }, entity: NZ, site: "Head office", v: { bal: [500, 500, 500, 500, 500] } });
bsAtoms.push({ name: "Intercompany loan payable", idx: 41, attrs: { section: "Non-current liabilities", nwc: "Non-trade" }, entity: EL, site: "Head office", v: { bal: [-500, -500, -500, -500, -500] } });

const CENT = { "North Island": { [NZ]: 1 }, "South Island": { [NZ]: 1 }, Australia: { [AU]: 1 }, Europe: { [EX]: 1 } };
const CUST = [
  ["Farmlands Co-operative", "Distributor", "North Island", [1850, 1620, 1540, 2100, 2380]],
  ["PGG Wrightson", "Distributor", "North Island", [1320, 1180, 1050, 1490, 1660]],
  ["Fonterra Farm Source", "Distributor", "North Island", [980, 890, 860, 1140, 1290]],
  ["RD1 Rural Supplies", "Retail chain", "South Island", [720, 650, 610, 830, 940]],
  ["DeLaval", "White label", "Europe", [610, 585, 540, 690, 760]],
  ["Daviesway", "White label", "Australia", [430, 410, 380, 470, 520]],
  ["Blue Pacific", "White label", "Australia", [290, 270, 255, 320, 355]],
  ["Stradbally", "White label", "Europe", [240, 220, 210, 260, 290]],
  ["Patura", "White label", "Europe", [180, 170, 160, 200, 225]],
  ["Farmage", "White label", "Europe", [150, 140, 130, 170, 190]],
  ["Rural Direct", "Retail chain", "North Island", [340, 310, 290, 380, 420]],
  ["AgriCentral", "Retail chain", "South Island", [280, 255, 240, 320, 360]],
  ["Southern Farm Supplies", "Distributor", "South Island", [230, 210, 195, 265, 300]],
  ["Waikato Ag Traders", "Direct", "North Island", [190, 170, 160, 215, 240]],
  ["Canterbury Dairy Co", "Direct", "South Island", [160, 150, 140, 185, 205]],
  ["Export brokers pooled", "Direct", "Europe", [140, 130, 120, 160, 180]],
];
const custAtoms = flat(CUST.map(([n, ch, rg, rev], i) => spread({ name: n, idx: i, attrs: { channel: ch, region: rg } }, CENT[rg], { rev })));

const PUR = [
  ["NZ Polymers", "Raw materials", "Recurring", [1650, 1580, 1520, 1890, 2100]],
  ["Pacific Resins", "Raw materials", "Recurring", [920, 880, 840, 1050, 1180]],
  ["Steel and Tube", "Raw materials", "Recurring", [480, 455, 430, 540, 600]],
  ["Packright", "Packaging", "Recurring", [310, 295, 280, 350, 390]],
  ["Mainfreight", "Freight", "Recurring", [420, 400, 380, 470, 520]],
  ["NZ Post Logistics", "Freight", "Recurring", [180, 170, 160, 200, 225]],
  ["Genesis Energy", "Utilities", "Recurring", [210, 220, 230, 245, 260]],
  ["Spark Business", "IT and comms", "Recurring", [95, 100, 105, 112, 120]],
  ["Systems and licences", "IT and comms", "Ad hoc", [45, 48, 52, 58, 64]],
  ["Legal and audit panel", "Professional services", "Ad hoc", [130, 110, 150, 185, 165]],
];
const purAtoms = flat(PUR.map(([n, cat, rec, spend], i) => spread({ name: n, idx: i, attrs: { category: cat, recurrence: rec } }, { [NZ]: 0.85, [AU]: 0.1, [EX]: 0.05 }, { spend })));

const FTEr = [
  ["Production operators", "Production", "Waged", [980, 1010, 1040, 1120, 1190], [18, 18, 19, 20, 21], { [NZ]: 1 }],
  ["Toolmakers", "Production", "Waged", [260, 270, 280, 295, 310], [3, 3, 3, 3, 3], { [NZ]: 1 }],
  ["Warehouse team", "Warehouse", "Waged", [340, 350, 365, 395, 420], [7, 7, 7, 8, 8], { [NZ]: 1 }],
  ["Drivers", "Warehouse", "Waged", [180, 185, 190, 205, 215], [3, 3, 3, 3, 3], { [NZ]: 1 }],
  ["Sales reps", "Sales", "Salaried", [420, 430, 445, 480, 510], [5, 5, 5, 5, 5], { [NZ]: 0.8, [AU]: 0.2 }],
  ["Customer services", "Sales", "Salaried", [190, 195, 200, 215, 225], [4, 4, 4, 4, 4], { [NZ]: 1 }],
  ["Finance and admin", "Admin", "Salaried", [310, 320, 330, 350, 370], [4, 4, 4, 4, 4], { [NZ]: 1 }],
  ["Leadership", "Admin", "Salaried", [520, 535, 550, 580, 610], [3, 3, 3, 3, 3], { [NZ]: 1 }],
];
const fteAtoms = flat(FTEr.map(([n, dep, emp, cost, fte, ew], i) => spread({ name: n, idx: i, attrs: { department: dep, employment: emp } }, ew, { cost, fte })));

const DS = {
  sales: {
    label: "Product sales", atoms: salesAtoms, orderBy: "value", primary: "rev", base: ["rev", "vol", "gp"],
    dims: { group: { label: "Product group", prov: "stated" }, colour: { label: "Colour", prov: "inferred" }, connection: { label: "Connection", prov: "inferred" }, pack: { label: "Pack size", prov: "inferred" }, capacity: { label: "Capacity", prov: "inferred" } },
    measures: [
      { k: "rev", label: "Revenue, USD 000s", kind: "num" },
      { k: "vol", label: "Volume, units", kind: "num" },
      { k: "price", label: "Price, USD per unit", kind: "num" },
      { k: "gp", label: "Gross profit, USD 000s", kind: "num" },
      { k: "margin", label: "GP margin", kind: "pct" },
    ],
    derived: { price: (a, pi) => (a.vol[pi] > 0 ? (a.rev[pi] * 1000) / a.vol[pi] : null), margin: (a, pi) => (a.rev[pi] !== 0 ? (a.gp[pi] / a.rev[pi]) * 100 : null) },
    sources: [
      { id: "s_ye22", label: "analyse_sales_ye22", periods: [0] },
      { id: "s_ye23", label: "analyse_sales_ye2023", periods: [1] },
      { id: "s_fy24", label: "product_sales_fy24", periods: [2] },
      { id: "s_fy25", label: "product_sales_fy25", periods: [3] },
      { id: "s_ytd26", label: "product_sales_summary_apr25_nov25", periods: [4] },
    ],
  },
  pnl: {
    label: "P&L lines", atoms: pnlAtoms, orderBy: "index", primary: "amt", base: ["amt"],
    dims: { statement: { label: "Statement section", prov: "stated" }, nature: { label: "Cost nature", prov: "inferred" } },
    measures: [{ k: "amt", label: "USD 000s", kind: "num" }], derived: {},
    sources: [
      { id: "p_a22", label: "SL_annual_ye22", periods: [0] },
      { id: "p_a23", label: "SL_annual_ye23", periods: [1] },
      { id: "p_a24", label: "SL_annual_ye24", periods: [2] },
      { id: "p_ma", label: "mgmt_accounts_pl", periods: [3, 4] },
    ],
  },
  bs: {
    label: "Balance sheet", atoms: bsAtoms, orderBy: "index", primary: "bal", base: ["bal"],
    dims: { section: { label: "Statement section", prov: "stated" }, nwc: { label: "Working capital class", prov: "inferred" } },
    measures: [{ k: "bal", label: "Balance, USD 000s", kind: "num" }], derived: {},
    sources: [
      { id: "b_a22", label: "SL_annual_ye22", periods: [0] },
      { id: "b_a23", label: "SL_annual_ye23", periods: [1] },
      { id: "b_a24", label: "SL_annual_ye24", periods: [2] },
      { id: "b_ma", label: "mgmt_accounts_bs", periods: [3, 4] },
    ],
  },
  customers: {
    label: "Customers", atoms: custAtoms, orderBy: "value", primary: "rev", base: ["rev"],
    dims: { channel: { label: "Channel", prov: "stated" }, region: { label: "Region", prov: "stated" } },
    measures: [{ k: "rev", label: "Revenue, USD 000s", kind: "num" }], derived: {},
    sources: [
      { id: "c_ye23", label: "customer_sales_ye23", periods: [0, 1] },
      { id: "c_fy24", label: "customer_sales_fy24", periods: [2] },
      { id: "c_cur", label: "sales_by_customer_fy25_ytd26", periods: [3, 4] },
    ],
  },
  purchases: {
    label: "Purchases", atoms: purAtoms, orderBy: "value", primary: "spend", base: ["spend"],
    dims: { category: { label: "Spend category", prov: "stated" }, recurrence: { label: "Recurrence", prov: "inferred" } },
    measures: [{ k: "spend", label: "Spend, USD 000s", kind: "num" }], derived: {},
    sources: [
      { id: "u_hist", label: "purchases_fy22_fy24", periods: [0, 1, 2] },
      { id: "u_cur", label: "purchases_fy25_ytd26", periods: [3, 4] },
    ],
  },
  fte: {
    label: "FTE and payroll", atoms: fteAtoms, orderBy: "value", primary: "cost", base: ["cost", "fte"],
    dims: { department: { label: "Department", prov: "stated" }, employment: { label: "Employment type", prov: "inferred" } },
    measures: [
      { k: "cost", label: "Cost, USD 000s", kind: "num" },
      { k: "fte", label: "FTE", kind: "num" },
      { k: "avg", label: "Avg cost per FTE, USD 000s", kind: "num" },
    ],
    derived: { avg: (a, pi) => (a.fte[pi] > 0 ? a.cost[pi] / a.fte[pi] : null) },
    sources: [
      { id: "f_hist", label: "wages_history", periods: [0, 1, 2, 3] },
      { id: "f_cur", label: "salaries_ye26", periods: [4] },
    ],
  },
};

// ==================================================================
// Reports and rail
// ==================================================================
const R = (id, name, ds, opts) => ({ id, name, ds, tpl: "sub_below", periods: [0, 1, 2, 3, 4], ...opts });
const REPORTS = [
  R("pnl_lead", "Reported P&L", "pnl"), R("pnl_m", "Reported P&L Monthly", "pnl"),
  R("bs_lead", "Reported Balance Sheet", "bs"), R("bs_m", "Reported BS Monthly", "bs"),
  R("cf", "Reported Cash Flow", "pnl"), R("ebbr", "EBITDA bridge", "pnl"),
  R("qoe", "QoE analysis", "pnl"), R("outturn", "Outturn analysis", "pnl"),
  R("runrate", "Run-rate analysis", "pnl"), R("season", "Seasonality", "sales"),
  R("rev_stream", "Revenue by Revenue Stream", "sales"),
  R("rev_cust_inv", "Revenue by Customer and Invoice", "customers", { flag: "review" }),
  R("cust_trend", "Customer Revenue Trend", "customers"),
  R("pt_cust", "Peach Teats Customer History", "customers", { filter: ["channel", "Distributor"] }),
  R("t1", "PVM Item Sales and Profitability", "sales", { tpl: "topn", measures: ["rev"], plCheck: true }),
  R("t2", "PVM Product", "sales", { tpl: "topn", periods: [4], measures: ["rev", "vol", "price"], plCheck: true }),
  R("pvm_pt", "PVM Profitability by Product Item, Peach Teats", "sales", { filter: ["group", "Peach Teat"], measures: ["rev", "gp", "margin"] }),
  R("pvm_wl", "PV White Label Sales by Product", "customers", { filter: ["channel", "White label"] }),
  R("gp1", "GP Item Sales and Profitability", "sales", { measures: ["gp", "margin"] }),
  R("gp_pt", "GP Profitability by Product Item, Peach Teats", "sales", { filter: ["group", "Peach Teat"], measures: ["gp", "margin"] }),
  R("cogs", "Reported COGS", "pnl", { filter: ["statement", "COGS"] }),
  R("opex", "Reported OPEX", "pnl", { filter: ["statement", "Overheads"] }),
  R("below", "Items Below EBITDA", "pnl", { filter: ["statement", "Below EBITDA"] }),
  R("empc", "Employee Costs", "fte"),
  R("nwc", "Net Working Capital", "bs", { tpl: "nwc" }),
  R("netdebt", "Net Debt", "bs", { filter: ["nwc", "Financing"] }),
  R("debt", "Debt detail", "bs", { filter: ["nwc", "Financing"] }),
  R("days", "Days analysis, DSO DIO DPO", "bs", { filter: ["nwc", "Trade WC"] }),
  R("rec1", "Reconciliation BS Mar 2024", "bs"), R("rec2", "Reconciliation P&L FY25", "pnl"), R("rec3", "Reconciliation BS Mar 2025", "bs"),
  R("purch", "Purchases by Spend Category", "purchases", { flag: "review" }),
  R("prepay", "Prepayments by Account", "bs", { filter: ["section", "Current assets"] }),
  R("accr", "Accrued Expenses by Account", "bs", { filter: ["section", "Current liabilities"] }),
  R("holpay", "Holiday Pay Provision", "bs", { filter: ["section", "Current liabilities"] }),
  R("wages", "Wages and salaries breakdown", "fte"), R("tenure", "Staff tenure", "fte"),
];
const RMAP = Object.fromEntries(REPORTS.map((r) => [r.id, r]));
const LIBRARY = [
  { id: "l1", name: "Revenue by colour", desc: "Product revenue grouped by the parsed colour attribute", section: "Revenue", ds: "sales", dim: "colour" },
  { id: "l2", name: "Revenue by connection type", desc: "Thread vs pull-thru split across the range", section: "Revenue", ds: "sales", dim: "connection" },
  { id: "l3", name: "Revenue by pack size", desc: "Pack of 5, 10 and 50 economics", section: "Revenue", ds: "sales", dim: "pack" },
  { id: "l4", name: "Sales by site", desc: "Hamilton, Auckland and Melbourne split", section: "Revenue", ds: "sales", dim: "site" },
  { id: "l5", name: "Customer concentration, top 10", desc: "Largest customers with the tail in Other", section: "Revenue", ds: "customers", mode: "topn" },
  { id: "l6", name: "Revenue by region", desc: "North Island, South Island, Australia, Europe", section: "Revenue", ds: "customers", dim: "region" },
  { id: "l7", name: "GP by product group, summary", desc: "One margin row per group, no item detail", section: "Gross Profit", ds: "sales", dim: "group", mode: "summary" },
  { id: "l8", name: "Purchases by recurrence", desc: "Recurring vs ad hoc supplier spend", section: "Balance detail", ds: "purchases", dim: "recurrence" },
  { id: "l9", name: "Supplier concentration, top 10", desc: "Largest suppliers with the tail in Other", section: "Balance detail", ds: "purchases", mode: "topn" },
  { id: "l10", name: "FTE by employment type", desc: "Waged vs salaried headcount and cost", section: "FTE", ds: "fte", dim: "employment" },
  { id: "l11", name: "Balance sheet by working capital class", desc: "Trade WC, financing and non-trade balances", section: "NWC and Net Debt", ds: "bs", dim: "nwc" },
  { id: "l12", name: "P&L by cost nature", desc: "Fixed vs variable view of the P&L", section: "Costs", ds: "pnl", dim: "nature" },
];
let libSeq = 0;

// ==================================================================
// Data room, files stage before the databook
// ==================================================================
const DR_FOLDERS = [
  { id: "tb", name: "01 Trial balances", n: 26 },
  { id: "ma", name: "02 Management accounts", n: 214 },
  { id: "ar", name: "03 Sales and debtors", n: 3187 },
  { id: "ap", name: "04 Purchases and creditors", n: 2921 },
  { id: "pay", name: "05 Payroll", n: 388 },
  { id: "bank", name: "06 Bank and treasury", n: 1105 },
  { id: "stock", name: "07 Inventory", n: 342 },
  { id: "legal", name: "08 Contracts and legal", n: 229 },
];
const drFolderName = (id) => { const f = DR_FOLDERS.find((x) => x.id === id); return f ? f.name.replace(/^\d+ /, "") : id; };
const DR_MONTHS = (() => {
  const ms = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];
  const out = [];
  for (let y = 22; y <= 24; y++) ms.forEach((m, i) => out.push(m + "-" + (i < 9 ? y : y + 1)));
  return out;
})();
const DR_YEARS = ["FY23", "FY24", "FY25"];
const DR_KINDS = [
  ["ar", "Sales ledger", "csv", "1 table, 2,140 lines"],
  ["ap", "Purchase ledger", "csv", "1 table, 1,872 lines"],
  ["bank", "Bank statement ANZ 00-4471", "pdf", "1 table, 96 lines"],
  ["ma", "Management accounts", "xlsx", "4 tables, P&L, BS, CF, KPI"],
  ["pay", "Payroll summary", "xlsx", "2 tables"],
  ["stock", "Stock listing", "xlsx", "1 table, 1,204 SKUs"],
  ["tb", "Trial balance", "xlsx", "1 table, 412 accounts"],
  ["ar", "Credit notes", "csv", "1 table, 88 lines"],
];
const DR_FILES = Array.from({ length: 60 }, (_, i) => {
  const k = DR_KINDS[i % DR_KINDS.length], m = DR_MONTHS[(i * 7) % DR_MONTHS.length];
  return { id: "f" + i, folder: k[0], name: k[1] + " " + m + "." + k[2], note: k[3], status: "use", ent: ["nz", "au", "ex"][i % 3], m };
});
const DR_EXCL_CATS = [
  { id: "super", name: "Superseded versions", n: 38, how: "A newer version of the same file is in use" },
  { id: "dup", name: "Duplicates of files in use", n: 121, how: "Same content as a file already extracted" },
  { id: "nodata", name: "No readable data", n: 22, how: "Blank templates, images, or scans below extraction quality" },
  { id: "scope", name: "Out of scope for FDD", n: 13, how: "Not financial content or outside the review window, kept for reference" },
];
const DR_EXCLUDED = [
  { id: "x1", folder: "tb", cat: "super", name: "TB_FY24_v3.xlsx", reason: "Superseded by v4_final", status: "excl" },
  { id: "x2", folder: "ma", cat: "super", name: "Mgmt accounts Mar-24 v2.xlsx", reason: "Superseded by v3", status: "excl" },
  { id: "x3", folder: "stock", cat: "super", name: "Stock listing Dec-24 draft.xlsx", reason: "Superseded by the final listing", status: "excl" },
  { id: "x4", folder: "ma", cat: "dup", name: "Mgmt accounts May-24 (conflict copy).xlsx", reason: "Duplicate of the file in use", status: "excl" },
  { id: "x5", folder: "ar", cat: "dup", name: "Sales ledger Feb-24 (1).csv", reason: "Byte-identical to the file in use", status: "excl" },
  { id: "x6", folder: "bank", cat: "dup", name: "Bank statement ANZ 00-4471 Jul-24 copy.pdf", reason: "Duplicate of the file in use", status: "excl" },
  { id: "x7", folder: "pay", cat: "dup", name: "Payroll summary Jun-24 (email copy).xlsx", reason: "Same content, different filename", status: "excl" },
  { id: "x8", folder: "ar", cat: "nodata", name: "Price list photos.zip", reason: "Images only, nothing to extract", status: "excl" },
  { id: "x9", folder: "stock", cat: "nodata", name: "Stock template (blank).xlsx", reason: "Blank template, no values", status: "excl" },
  { id: "x10", folder: "stock", cat: "nodata", name: "Site photos warehouse.zip", reason: "Images only, nothing to extract", status: "excl" },
  { id: "x11", folder: "legal", cat: "scope", name: "Brand guidelines 2023.pdf", reason: "Not financial content", status: "excl" },
  { id: "x12", folder: "bank", cat: "scope", name: "Statement scans Q1-23.pdf", reason: "Before the review window", status: "excl" },
  { id: "x13", folder: "legal", cat: "scope", name: "Org chart 2022.pptx", reason: "Reference only, no numbers", status: "excl" },
];
const DR_QUESTIONS = [
  { id: "q1", file: "TB_FY24_v4_final.xlsx", folder: "tb", short: "Which FY24 trial balance governs?", q: "Two FY24 trial balances found. v4_final agrees to the signed accounts, v3 differs by 12.4k in Other opex. v4 is in use.", a: ["Keep v4", "Use v3 instead"] },
  { id: "q2", file: "Mgmt accounts May-24.xlsx", folder: "ma", short: "TB or management accounts for May-24?", q: "May-24 management accounts differ from the trial balance by 12.4k in Other opex. The trial balance governs the databook.", a: ["Keep the TB", "Use management accounts"] },
  { id: "q3", file: "Payroll master FY24.xlsx", folder: "pay", short: "Locked file, provide access or skip?", q: "Password protected, could not be read. Payroll cost is complete from the TB, headcount detail is not.", a: ["Not needed", "I will provide access"] },
  { id: "q4", file: "Stock count Jun-24.pdf", folder: "stock", short: "Accept TB-derived closing stock?", q: "Scanned at low quality, values could not be extracted reliably. Closing stock is taken from the TB instead.", a: ["Fine as handled", "Escalate to client"] },
];
const DR_EXCL_MORE = (() => {
  const out = [];
  const push = (cat, folder, name, reason, m) => out.push({ id: "m" + out.length, cat, folder, name, reason, status: "excl", m });
  const kinds = [["ma", "Management accounts"], ["ar", "Sales ledger"], ["ap", "Purchase ledger"], ["bank", "Bank statement"], ["pay", "Payroll summary"], ["stock", "Stock listing"]];
  const count = (c) => out.filter((f) => f.cat === c).length;
  let i = 0;
  while (count("super") < 35) { const k = kinds[i % 6], m = DR_MONTHS[i % DR_MONTHS.length]; push("super", k[0], k[1] + " " + m + " v" + (1 + (i % 3)) + ".xlsx", "Superseded by a later version in use", m); i++; }
  while (count("dup") < 117) { const k = kinds[i % 6], m = DR_MONTHS[i % DR_MONTHS.length]; push("dup", k[0], k[1] + " " + m + " (copy " + (1 + (i % 4)) + ").xlsx", "Duplicate of the file in use", m); i++; }
  while (count("nodata") < 19) { const m = DR_MONTHS[i % DR_MONTHS.length]; if (i % 2) push("nodata", "stock", "Warehouse photos " + m + ".zip", "Images only, nothing to extract", m); else push("nodata", "bank", "Statement scan " + m + ".pdf", "Scan below extraction quality", m); i++; }
  while (count("scope") < 10) { const m = DR_MONTHS[i % DR_MONTHS.length]; if (i % 2) push("scope", "legal", "Insurance certificate " + m + ".pdf", "Reference document, outside scope", m); else push("scope", "legal", "Board pack " + m + ".pdf", "Not financial content", m); i++; }
  return out;
})();
const drCatFiles = (cid) => [...DR_EXCLUDED, ...DR_EXCL_MORE].filter((f) => f.cat === cid);
const DR_ASIDE_BY = {};
[...DR_EXCLUDED, ...DR_EXCL_MORE].forEach((f) => { DR_ASIDE_BY[f.folder] = (DR_ASIDE_BY[f.folder] || 0) + 1; });
DR_EXCLUDED.forEach((f, i) => { f.ent = ["nz", "au", "ex"][i % 3]; const mm = f.name.match(/[A-Z][a-z]{2}-\d{2}|FY\d{2}|Q\d-\d{2}|\d{4}/); f.m = mm ? mm[0] : ""; });
DR_EXCL_MORE.forEach((f, i) => { f.ent = ["nz", "au", "ex", "hold"][i % 4]; });
const LEGAL_ENTS = [
  { id: "hold", name: "Start Demo Limited", jur: "New Zealand", num: "NZBN 9429048112233", cur: "USD", own: null, parent: null, nonOp: true, desc: "Non-trading holding company for the group", status: "Active", nFiles: 396 },
  { id: "nz", grp: "prod", name: "Start Demo NZ Limited", jur: "New Zealand", num: "NZBN 9429031456789", cur: "USD", own: "100%", parent: "hold", desc: "Manufacturing and NZ distribution, Hamilton and Auckland sites", status: "Active", nFiles: 3616 },
  { id: "au", grp: "trade", name: "Start Demo Australia Pty Ltd", jur: "Australia", num: "ACN 611 223 445", cur: "AUD", own: "100%", parent: "hold", desc: "Australian sales and distribution, Melbourne", status: "Active", nFiles: 2431 },
  { id: "ex", grp: "trade", name: "Start Demo Exports Limited", jur: "New Zealand", num: "NZBN 9429040998877", cur: "USD", own: "100%", parent: "hold", desc: "Export sales to Europe and Asia through pooled brokers", status: "Active", nFiles: 1208 },
  { id: "feeds", grp: "prod", name: "Start Demo Feeds Limited", jur: "New Zealand", num: "NZBN 9429052667701", cur: "USD", own: "100%", parent: "hold", desc: "Animal feeds blender bought in the period, consolidated from acquisition", status: "Active", nFiles: 290, ev: "acq", evShort: "Mid-year acquisition", evDetail: "consolidated from Oct-24, 6 months in the numbers", evFrom: 30 },
  { id: "uk", grp: "trade", name: "Start Demo UK Limited", jur: "United Kingdom", num: "CRN 09887123", cur: "GBP", own: "100%", parent: "hold", desc: "UK distributor sold during the period, deconsolidated at completion", status: "Active", nFiles: 214, ev: "disp", evShort: "Disposal", evDetail: "deconsolidated from Jan-25, 12 months in the numbers", evTo: 33 },
  { id: "prop", grp: "other", name: "Start Demo Property Limited", jur: "New Zealand", num: "NZBN 9429030112244", cur: "USD", own: "100%", parent: "hold", desc: "Dormant, previously held the Hamilton site", status: "Dormant", nFiles: 59 },
];
// ==================================================================
// Incoming pass — new files that landed after the databook was built.
// Nothing recomputes on its own: this only declares which existing
// reports WILL move and which new reports WILL appear, so the user can
// see the impact in the rail before choosing to recalculate.
// ==================================================================
const INCOMING_PASS = {
  files: ["TB_FY24_v5.xlsx", "Payroll Sep-24.xlsx", "Sales ledger Sep-24.csv"],
  impacts: {
    pnl_lead: "TB_FY24_v5 moves FY24 revenue and opex",
    gp1: "September sales ledger adds two product rows",
    wages: "September payroll replaces the accrued estimate",
    netdebt: "TB_FY24_v5 shifts closing cash and borrowings",
  },
  adds: [
    { id: "inc_headcount", name: "Headcount by Month", section: "FTE", from: "Payroll Sep-24.xlsx", ds: "fte", tpl: "sub_above" },
    { id: "inc_sep_rev", name: "September Revenue Bridge", section: "Revenue", from: "Sales ledger Sep-24.csv", ds: "sales", tpl: "flat" },
  ],
};

const RAIL = [
  ["Leads", ["pnl_lead", "pnl_m", "bs_lead", "bs_m", "cf", "ebbr"]],
  ["Analysis", ["qoe", "outturn", "runrate", "season"]],
  ["Revenue", ["rev_stream", "rev_cust_inv", "cust_trend", "pt_cust"]],
  ["PVM", ["t1", "t2", "pvm_pt", "pvm_wl"]],
  ["Gross Profit", ["gp1", "gp_pt"]],
  ["Costs", ["cogs", "opex", "below", "empc"]],
  ["NWC and Net Debt", ["nwc", "netdebt", "debt", "days"]],
  ["Stat recs", ["rec1", "rec2", "rec3"]],
  ["Balance detail", ["purch", "prepay", "accr", "holpay"]],
  ["FTE", ["wages", "tenure"]],
];

// ==================================================================
// Engine
// ==================================================================
const getv = (a, key) => (key === "entity" ? a.entity : key === "site" ? a.site : a.attrs[key]);
function sumAgg(ds, ms) {
  const a = {};
  ds.base.forEach((k) => (a[k] = P.map(() => 0)));
  ms.forEach((m) => ds.base.forEach((k) => P.forEach((_, i) => (a[k][i] += m.v[k][i]))));
  return a;
}
const mval = (ds, a, mk, pi) => (ds.derived[mk] ? ds.derived[mk](a, pi) : a[mk][pi]);
const fmtN = (n) => {
  if (n == null) return "-";
  const r = Math.round(n);
  if (r === 0) return "-";
  return r < 0 ? "(" + Math.abs(r).toLocaleString("en-NZ") + ")" : r.toLocaleString("en-NZ");
};
const fmtP = (n) => (n == null ? "-" : n < 0 ? "(" + Math.abs(n).toFixed(1) + "%)" : n.toFixed(1) + "%");
const fmtBy = (kind) => (kind === "pct" ? fmtP : fmtN);
const dimLabel = (ds, key) =>
  key === "entity" ? "Legal entity" : key === "site" ? "Site" : key === "sku" ? "Items" : key === "__custom__" ? "Custom groups" : ds.dims[key].label;
const memberLabel = (ds, key, v) => (key === "pack" ? "Pack of " + v : key === "capacity" ? v + " L" : String(v));
const coverage = (ds, key) => {
  if (key === "sku" || key === "entity" || key === "site") return 100;
  const w = (m) => Math.abs(m.v[ds.primary].reduce((a, b) => a + Math.abs(b), 0));
  const tot = ds.atoms.reduce((s, m) => s + w(m), 0);
  const cov = ds.atoms.filter((m) => getv(m, key) != null).reduce((s, m) => s + w(m), 0);
  return Math.round((100 * cov) / (tot || 1));
};
const membersOf = (ds, key, includeNull) => {
  let vals;
  if (key === "entity") vals = ENTS.filter((e) => ds.atoms.some((a) => a.entity === e));
  else if (key === "site") vals = SITES.filter((s) => ds.atoms.some((a) => a.site === s));
  else {
    const set = new Map();
    ds.atoms.forEach((a) => { const v = getv(a, key); if (v != null) set.set(String(v), true); });
    vals = [...set.keys()].sort();
  }
  if (includeNull && key !== "entity" && key !== "site" && ds.atoms.some((a) => getv(a, key) == null)) vals = [...vals, "__u__"];
  return vals;
};
const axisMembers = (ds, axis, atoms, pi) => {
  if (axis === "entity") return ENTS.filter((m) => atoms.some((a) => a.entity === m));
  if (axis === "site") return SITES.filter((m) => atoms.some((a) => a.site === m));
  const set = new Map();
  atoms.forEach((a) => {
    const v = getv(a, axis) == null ? "__u__" : String(getv(a, axis));
    set.set(v, (set.get(v) || 0) + Math.abs(a.v[ds.primary][pi]));
  });
  return [...set.entries()].sort((x, y) => (x[0] === "__u__") - (y[0] === "__u__") || y[1] - x[1]).map((e) => e[0]);
};

function buildRows(ds, atoms, levels, li, depth, path, opts, out) {
  const lvl = levels[li];
  if (!lvl) return;
  const byVal = (x, y) => Math.abs(y.a[ds.primary][4]) - Math.abs(x.a[ds.primary][4]) || Math.abs(y.a[ds.primary][3]) - Math.abs(x.a[ds.primary][3]);
  const byIdx = (x, y) => x.minIdx - y.minIdx;
  if (lvl.key === "sku") {
    const byName = new Map();
    atoms.forEach((a) => { if (!byName.has(a.name)) byName.set(a.name, []); byName.get(a.name).push(a); });
    let entries = [...byName.entries()].map(([n, ms]) => ({ n, ms, a: sumAgg(ds, ms), minIdx: Math.min(...ms.map((m) => m.idx)) }));
    entries.sort(ds.orderBy === "index" ? byIdx : byVal);
    if (opts.seq) {
      const s = opts.seq;
      if (entries.some((e) => s[e.n] != null)) entries.sort((a, b) => (s[a.n] != null ? s[a.n] : 1e9) - (s[b.n] != null ? s[b.n] : 1e9));
    }
    let head = entries, tail = [];
    if (ds.orderBy === "value" && opts.topN > 0 && entries.length > opts.topN + 1) { head = entries.slice(0, opts.topN); tail = entries.slice(opts.topN); }
    head.forEach((e) => out.push({ type: "leaf", depth, label: e.n, agg: e.a, ms: e.ms }));
    if (tail.length) {
      const op = path + "|other";
      const open = !!opts.open[op];
      const tms = flat(tail.map((e) => e.ms));
      out.push({ type: "other", depth, label: "Other, " + tail.length + " items", agg: sumAgg(ds, tms), ms: tms, path: op, chev: true, open });
      if (open) tail.forEach((e) => out.push({ type: "leaf", depth: depth + 1, label: e.n, agg: e.a, ms: e.ms }));
    }
    return;
  }
  const map = new Map();
  atoms.forEach((a) => {
    const raw = lvl.key === "__custom__" ? (opts.groupsMap || {})[a.name] : getv(a, lvl.key);
    const v = raw == null ? "__u__" : String(raw);
    if (!map.has(v)) map.set(v, []);
    map.get(v).push(a);
  });
  let entries = [...map.entries()].map(([v, ms]) => ({ v, ms, a: sumAgg(ds, ms), minIdx: Math.min(...ms.map((m) => m.idx)) }));
  if (lvl.key === "entity") entries.sort((x, y) => ENTS.indexOf(x.v) - ENTS.indexOf(y.v));
  else if (lvl.key === "site") entries.sort((x, y) => SITES.indexOf(x.v) - SITES.indexOf(y.v));
  else if (ds.orderBy === "index") entries.sort(byIdx);
  else entries.sort((x, y) => (x.v === "__u__") - (y.v === "__u__") || byVal(x, y));
  const hasChild = li + 1 < levels.length;
  entries.forEach((e) => {
    if (lvl.key === "__custom__" && e.v === "__u__") { buildRows(ds, e.ms, levels, li + 1, depth, path + "|ungrouped", opts, out); return; }
    const base = e.v === "__u__" ? "No " + dimLabel(ds, lvl.key).toLowerCase() : memberLabel(ds, lvl.key, e.v);
    if (!hasChild) { out.push({ type: "leaf", depth, label: base, agg: e.a, ms: e.ms }); return; }
    const gk = lvl.key + "::" + e.v;
    const pth = path + "|" + gk;
    const vis = opts.overrides[gk] || lvl.vis || "sub";
    if (vis === "hidden") { buildRows(ds, e.ms, levels, li + 1, depth, pth, opts, out); return; }
    if (vis === "sub") {
      const open = !opts.open[pth];
      if (open) buildRows(ds, e.ms, levels, li + 1, depth, pth, opts, out);
      out.push({ type: "sub", depth, label: "Total " + base, agg: e.a, ms: e.ms, gk, vis, path: pth, chev: true, open });
      return;
    }
    const open = !!opts.open[pth];
    const collapsedLabel = vis === "label" && !open;
    out.push({ type: vis === "hier" || collapsedLabel ? "hier" : "band", depth, label: base, agg: e.a, ms: e.ms, gk, vis, path: pth, chev: true, open });
    if (open) buildRows(ds, e.ms, levels, li + 1, depth + 1, pth, opts, out);
  });
}

// ==================================================================
// Thirteen templates
// ==================================================================
const d1 = (ds) => Object.keys(ds.dims)[0];
const d2 = (ds) => Object.keys(ds.dims)[1] || Object.keys(ds.dims)[0];
const TPLS = [
  { id: "flat", name: "As extracted", desc: "Flat item list, source order, no grouping",
    mk: (ds) => ({ rows: [{ key: "sku" }], topN: 0, colField: null, colMode: "single", split: null }) },
  { id: "topn", name: "Top 15 + Other", desc: "Ranked items, tail folded into Other",
    mk: (ds) => ({ rows: [{ key: "sku" }], topN: 15, colField: null, colMode: "single", split: null }) },
  { id: "sub_below", name: "Subtotals", desc: "Accounts flat, a single-ruled subtotal line under each block",
    mk: (ds) => ({ rows: [{ key: d1(ds), vis: "sub" }, { key: "sku" }], topN: 15, colField: null, colMode: "single", split: null }) },
  { id: "sub_above", name: "Grouped hierarchy", desc: "Category header rows, items nested inside, second hierarchy level",
    mk: (ds) => ({ rows: [{ key: d1(ds), vis: "hier" }, { key: "sku" }], topN: 15, colField: null, colMode: "single", split: null }) },
  { id: "summary", name: "Group summary", desc: "One row per category, no item detail",
    mk: (ds) => ({ rows: [{ key: d1(ds) }], topN: 0, colField: null, colMode: "single", split: null }) },
  { id: "two_level", name: "Two-level tree", desc: "Category, then sub-category, then items",
    mk: (ds) => ({ rows: [{ key: d1(ds), vis: "hier" }, { key: d2(ds), vis: "hier" }, { key: "sku" }], topN: 10, colField: null, colMode: "single", split: null }) },
  { id: "labels", name: "Section labels", desc: "Categories as headings without numbers",
    mk: (ds) => ({ rows: [{ key: d1(ds), vis: "label" }, { key: "sku" }], topN: 15, colField: null, colMode: "single", split: null }) },
  { id: "matrix", name: "Matrix", desc: "Category rows crossed with a second attribute",
    mk: (ds) => ({ rows: [{ key: d1(ds) }], topN: 0, colField: d2(ds), colMode: "single", split: null }) },
  { id: "ent_rows", name: "By legal entity", desc: "Everything nested under each entity",
    mk: (ds) => ({ rows: [{ key: "entity", vis: "hier" }, { key: d1(ds), vis: "hier" }, { key: "sku" }], topN: 10, colField: null, colMode: "single", split: null }) },
  { id: "ent_cols", name: "Entity columns", desc: "One period, a column per entity with eliminations",
    mk: (ds) => ({ rows: [{ key: d1(ds), vis: "sub" }, { key: "sku" }], topN: 15, colField: "entity", colMode: "single", split: null }) },
  { id: "ent_bands", name: "Entities side by side", desc: "One wide table, a full period band per entity",
    mk: (ds) => ({ rows: [{ key: d1(ds), vis: "sub" }, { key: "sku" }], topN: 10, colField: "entity", colMode: "bands", split: null }) },
  { id: "split_group", name: "One table per group", desc: "Separate stacked table for every category",
    mk: (ds) => ({ rows: [{ key: "sku" }], topN: 10, colField: null, colMode: "single", split: d1(ds) }) },
  { id: "split_ent", name: "One table per entity", desc: "Separate stacked table for every entity",
    mk: (ds) => ({ rows: [{ key: "sku" }], topN: 10, colField: null, colMode: "single", split: "entity" }) },
  { id: "multi", name: "Multi-measure", desc: "Every measure side by side, grouped with subtotals",
    mk: (ds) => ({ rows: [{ key: d1(ds), vis: "sub" }, { key: "sku" }], topN: 15, colField: null, colMode: "single", split: null, values: ds.measures.map((m) => m.k) }) },
  { id: "rank", name: "League table", desc: "Every item ranked largest first, nothing folded into Other",
    mk: (ds) => ({ rows: [{ key: "sku" }], topN: 0, colField: null, colMode: "single", split: null }) },
  { id: "matrix_bands", name: "Cross-tab, side by side", desc: "Category rows crossed with a second attribute as full bands",
    mk: (ds) => ({ rows: [{ key: d1(ds), vis: "sub" }, { key: "sku" }], topN: 10, colField: d2(ds), colMode: "bands", split: null }) },
  { id: "site_cols", name: "Site columns", desc: "One period, a column per site",
    mk: (ds) => ({ rows: [{ key: d1(ds), vis: "sub" }, { key: "sku" }], topN: 15, colField: "site", colMode: "single", split: null }) },
  { id: "site_bands", name: "Sites side by side", desc: "One wide table, a full period band per site",
    mk: (ds) => ({ rows: [{ key: d1(ds), vis: "sub" }, { key: "sku" }], topN: 10, colField: "site", colMode: "bands", split: null }) },
  { id: "split_site", name: "One table per site", desc: "Separate stacked table for every site",
    mk: (ds) => ({ rows: [{ key: "sku" }], topN: 10, colField: null, colMode: "single", split: "site" }) },
];
const TPLMAP = Object.fromEntries(TPLS.map((t) => [t.id, t]));
const CORE_TPL = ["flat", "sub_below", "summary", "ent_bands", "split_ent", "matrix"];

const mkSpec = (rep) => {
  const ds = DS[rep.ds];
  const base = rep.tpl === "nwc"
    ? { rows: [{ key: "nwc", vis: "sub" }, { key: "sku" }], topN: 0, colField: null, colMode: "single", split: null }
    : (TPLMAP[rep.tpl] || TPLMAP.sub_below).mk(ds);
  return {
    tpl: rep.tpl, ...base,
    values: rep.measures ? [...rep.measures] : [ds.measures[0].k],
    overrides: {}, open: {}, colPeriod: 4,
    periodsSel: [...rep.periods],
    renames: {}, catRenames: {}, srcSel: {}, seq: {},
    sources: ds.sources.map((s) => s.id),
    topN: ds.orderBy === "index" ? 0 : base.topN,
  };
};
const PL_OFFSET = [517, 433, 249, 495, 892];
const MONO = { fontFamily: "'IBM Plex Mono', ui-monospace, SFMono-Regular, monospace" };
const UIF = { fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" };
const dragRef = { key: null };
const rowDragRef = { ri: null };
const perRange = (ps) => (ps.length > 1 ? P[ps[0]] + " to " + P[ps[ps.length - 1]] : P[ps[0]]);

// ==================================================================
// Agentic build helpers
// ==================================================================
const ALLFILES = flat(Object.entries(DS).map(([k, d]) => d.sources.map((s) => ({ ...s, dsKey: k, store: d.label }))));
const fileExt = (label) => (/\.pdf$|summary/i.test(label) ? "pdf" : /\.csv$/i.test(label) ? "csv" : "xlsx");
const FIcon = ({ label, size = 10 }) => (fileExt(label) === "pdf"
  ? <FileText size={size} className="text-muted-foreground shrink-0" />
  : <FileSpreadsheet size={size} className="text-muted-foreground shrink-0" />);
let agentSeq = 0;
function inferAgent(prompt, fileDs) {
  const p = prompt.toLowerCase();
  if (p.includes("margin") || p.includes("gross")) return { ds: "sales", tpl: "sub_above", measures: ["gp", "margin"] };
  if (p.includes("side by side") || p.includes("consol")) return { ds: fileDs, tpl: "ent_bands" };
  if (p.includes("entity")) return { ds: fileDs, tpl: "ent_rows" };
  if (p.includes("customer") || p.includes("channel")) return { ds: "customers", tpl: "sub_above" };
  if (p.includes("supplier") || p.includes("purchase") || p.includes("spend")) return { ds: "purchases", tpl: "sub_above" };
  if (p.includes("wage") || p.includes("fte") || p.includes("staff") || p.includes("headcount")) return { ds: "fte", tpl: "sub_above" };
  if (p.includes("working capital") || p.includes("nwc") || p.includes("balance")) return { ds: "bs", tpl: "sub_above" };
  if (p.includes("matrix")) return { ds: fileDs, tpl: "matrix" };
  if (p.includes("per ") && p.includes("table")) return { ds: fileDs, tpl: "split_group" };
  return { ds: fileDs, tpl: "sub_above" };
}

// ==================================================================
// ==================================================================
// Stage 1: Upload / data-room intake
// ==================================================================
function UploadStage({
  projectId,
  canEdit,
  permissionsLoading,
  documents,
  setDocuments,
  isLoading,
  loadError,
  goNext,
  reviewEnrolled,
  reviewStarting,
  reviewFailed,
  reviewReady,
  retryAnalysis,
  financialYearEnd,
  onFinancialYearEndChange,
  scopeFrom,
  onScopeFromChange,
  scopeTo,
  onScopeToChange,
  includeYtd,
  onIncludeYtdChange,
  includeForecast,
  onIncludeForecastChange,
  forecastYears,
  onForecastYearsChange,
  analysisSettingsLoading,
  dealContext,
  setDealContext,
  dealContextLoading,
  onDealContextBlur,
}) {
  const successfulDocuments = documents.filter((f) => f.status === "done" && f.isSupported);
  const uploadsInProgress = documents.some((f) => f.status === "uploading" || f.status === "deleting");

  const updateDocument = (localId, patch) => {
    setDocuments((prev) => prev.map((f) => (f.localId === localId ? { ...f, ...patch } : f)));
  };

  const uploadEntries = async (entries) => {
    if (!projectId) return;
    const uploadable = entries.filter((entry) => entry.file);
    if (uploadable.length === 0) return;
    uploadable.forEach((entry) => {
      updateDocument(entry.localId, { status: "uploading" });
    });
    const pendingIds = new Set(uploadable.map((entry) => entry.localId));
    // Let React paint the optimistic upload state before the request starts.
    await new Promise((resolve) => window.requestAnimationFrame(resolve));
    try {
      const result = await uploadProjectDocuments(
        projectId,
        uploadable.map((entry) => entry.file),
      );
      setDocuments((prev) => {
        const retained = prev.filter((document) => !pendingIds.has(document.localId));
        const existingIds = new Set(retained.map((document) => document.id));
        return [
          ...retained,
          ...result.documents
            .filter((document) => !existingIds.has(document.id))
            .map((document) => ({
              localId: `document-${document.id}`,
              id: document.id,
              name: document.original_filename,
              isSupported: document.is_supported,
              size: document.size_bytes,
              status: "done",
              file: null,
            })),
        ];
      });
      if (result.skipped.length > 0 || result.failed.length > 0) {
        toast({
          title: "Files processed with issues",
          description: documentBatchUploadFeedback(result),
          variant: result.documents.length === 0 && result.failed.length > 0
            ? "destructive"
            : undefined,
        });
      }
    } catch {
      uploadable.forEach((entry) => {
        updateDocument(entry.localId, { status: "error" });
      });
      toast({
        title: "Upload failed",
        description: "The selected files could not be uploaded. Try again or remove them.",
        variant: "destructive",
      });
    }
  };

  const handleFiles = (fs) => {
    const list = Array.from(fs || []);
    if (list.length === 0 || !projectId || !canEdit) return;

    const seen = new Set(documents.map((f) => f.name));
    const added = [];
    list.forEach((file, index) => {
      if (seen.has(file.name)) return;
      seen.add(file.name);
      added.push({
        localId: `upload-${Date.now()}-${index}-${file.name}`,
        id: null,
        name: file.name,
        size: file.size,
        status: "uploading",
        file,
      });
    });

    if (added.length === 0) {
      toast({
        title: "No new files",
        description: "Files with the same names are already in this project.",
      });
      return;
    }

    setDocuments((prev) => [...prev, ...added]);
    void uploadEntries(added);
  };

  const removeDocument = async (document) => {
    if (!canEdit || document.status === "uploading" || document.status === "deleting") return;
    if (reviewStarting) {
      toast({
        title: "Analysis in progress",
        description: "Files cannot be deleted while source analysis is running.",
      });
      return;
    }
    if (!document.id) {
      setDocuments((prev) => prev.filter((f) => f.localId !== document.localId));
      return;
    }

    updateDocument(document.localId, { status: "deleting" });
    try {
      await deleteProjectDocument(projectId, document.id);
      setDocuments((prev) => prev.filter((f) => f.localId !== document.localId));
    } catch {
      updateDocument(document.localId, { status: "done" });
      toast({
        title: "Delete failed",
        description: `${document.name} could not be removed.`,
        variant: "destructive",
      });
    }
  };

  const clearAll = async () => {
    if (!canEdit || uploadsInProgress) return;
    if (reviewStarting) {
      toast({
        title: "Analysis in progress",
        description: "Files cannot be deleted while source analysis is running.",
      });
      return;
    }
    const stored = documents.filter((f) => f.id);
    setDocuments((prev) => prev.map((f) => (f.id ? { ...f, status: "deleting" } : f)));
    const results = await Promise.allSettled(
      stored.map((f) => deleteProjectDocument(projectId, f.id)),
    );
    const failedIds = new Set(
      results.flatMap((result, index) => (result.status === "rejected" ? [stored[index].id] : [])),
    );
    setDocuments((prev) =>
      prev
        .filter((f) => f.id && failedIds.has(f.id))
        .map((f) => ({ ...f, status: "done" })),
    );
    if (failedIds.size > 0) {
      toast({
        title: "Some files were not deleted",
        description: `${failedIds.size} file${failedIds.size === 1 ? "" : "s"} remain in the project.`,
        variant: "destructive",
      });
    }
  };

  const downloadDocument = async (document) => {
    if (!projectId || !document.id || document.status !== "done") return;
    try {
      const { url } = await getDocumentDownloadUrl(projectId, document.id);
      const link = window.document.createElement("a");
      link.href = url;
      link.download = document.name;
      link.rel = "noopener";
      window.document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      toast({
        title: "Download failed",
        description: `${document.name} could not be downloaded.`,
        variant: "destructive",
      });
    }
  };

  const uploadDocuments: DatabookUploadDocument[] = documents.map((document) => ({
    key: document.localId,
    name: document.name,
    size: document.size,
    isSupported: document.isSupported,
    status: document.status === "done" ? "ready" : document.status,
    canDownload: document.status === "done" && !!document.id,
    canRetry: document.status === "error" && !!document.file,
  }));
  const sourceDocument = (document: DatabookUploadDocument) =>
    documents.find((candidate) => candidate.localId === document.key);
  const canUploadDocuments = !!projectId
    && canEdit
    && !permissionsLoading;
  const canDeleteDocuments = canUploadDocuments;

  return (
    <DatabookUploadStage
      documents={uploadDocuments}
      documentsLoading={isLoading}
      documentsLoadError={loadError}
      canUpload={canUploadDocuments}
      uploadPrompt={
        !projectId
          ? "Open Databook Builder from a project"
          : permissionsLoading
            ? "Checking project access…"
            : canEdit
              ? "Drag files or folders here, or browse"
              : "Only editors and managers can upload files"
      }
      dealContext={dealContext || ""}
      dealContextDisabled={!canEdit || dealContextLoading}
      onDealContextChange={setDealContext}
      onSaveDealContext={onDealContextBlur}
      financialYearEnd={financialYearEnd}
      financialYearEndDisabled={!canEdit || analysisSettingsLoading}
      onFinancialYearEndChange={onFinancialYearEndChange}
      periodOptions={BUILDER_PERIOD_OPTIONS}
      scopeFrom={scopeFrom}
      scopeTo={scopeTo}
      includeYtd={includeYtd}
      scopeDisabled={!canEdit || analysisSettingsLoading}
      onScopeFromChange={onScopeFromChange}
      onScopeToChange={onScopeToChange}
      onIncludeYtdChange={onIncludeYtdChange}
      includeForecast={includeForecast}
      forecastYears={forecastYears}
      forecastDisabled={!canEdit || analysisSettingsLoading}
      onIncludeForecastChange={onIncludeForecastChange}
      onForecastYearsChange={onForecastYearsChange}
      canClearDocuments={canDeleteDocuments && !uploadsInProgress}
      canRemoveDocuments={canDeleteDocuments}
      onFilesSelected={handleFiles}
      onClearDocuments={clearAll}
      onRemoveDocument={(document) => {
        const source = sourceDocument(document);
        if (source) removeDocument(source);
      }}
      onDownloadDocument={(document) => {
        const source = sourceDocument(document);
        if (source) downloadDocument(source);
      }}
      onRetryDocument={(document) => {
        const source = sourceDocument(document);
        if (source) void uploadEntries([source]);
      }}
      continueLabel={
        reviewStarting
          ? "Analysing sources…"
          : reviewFailed
            ? "Retry analysis →"
            : reviewReady
              ? "Continue →"
            : reviewEnrolled
              ? "Analysis already started"
              : "Start analysis →"
      }
      continueDisabled={
        successfulDocuments.length === 0
        || uploadsInProgress
        || analysisSettingsLoading
        || reviewStarting
        || (reviewEnrolled && !reviewFailed && !reviewReady)
      }
      analysisRunning={reviewStarting}
      onRetryAnalysis={reviewReady && canEdit ? retryAnalysis : undefined}
      onContinue={goNext}
    />
  );
}

export default function DatabookBuilderEngine({ projectId, canEdit, permissionsLoading }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const restoredStageRef = useRef(loadLastBuilderStage(projectId));
  const autoOpenDetectedDatabookRef = useRef(restoredStageRef.current === null);
  const previousReviewStateRef = useRef(null);
  const pendingPostAnalysisNavigationRef = useRef(false);
  const [sel, setSel] = useState("t1");
  const [specs, setSpecs] = useState<any>({});
  const [q, setQ] = useState("");
  const [fq, setFq] = useState("");
  const [railOpen, setRailOpen] = useState(true);
  const [fieldsOpen, setFieldsOpen] = useState(false);
  const [customize, setCustomize] = useState(false);
  const [ws, setWs] = useState(restoredStageRef.current ?? "upload");
  const construction = useAgentDatabookChat(projectId, ws === "book");
  const [dealContext, setDealContext] = useState("");
  const [projectName, setProjectName] = useState("");
  const [dealContextLoading, setDealContextLoading] = useState(!!projectId);
  const [builderSettings, setBuilderSettings] = useState(DEFAULT_BUILDER_SETTINGS);
  const [builderSettingsLoading, setBuilderSettingsLoading] = useState(!!projectId);
  const [builderSettingsSaving, setBuilderSettingsSaving] = useState(false);
  const savedDealContextRef = useRef("");
  const [uploadDocuments, setUploadDocuments] = useState([]);
  const [uploadDocumentsLoading, setUploadDocumentsLoading] = useState(!!projectId);
  const [uploadDocumentsLoadError, setUploadDocumentsLoadError] = useState(false);
  const [prebuildReview, setPrebuildReview] = useState(null);
  const [prebuildReviewLoadFailed, setPrebuildReviewLoadFailed] = useState(false);
  const [coverageState, setCoverageState] = useState({ status: "loading" });
  const [coverageReload, setCoverageReload] = useState(0);
  const [legalEntitiesState, setLegalEntitiesState] = useState<LegalEntitiesLoadState>({
    status: "loading",
  });
  const [legalStructureDirty, setLegalStructureDirty] = useState(false);
  const [entityCatalogReload, setEntityCatalogReload] = useState(0);
  const [basisOptionsState, setBasisOptionsState] =
    useState<BasisOptionsLoadState>({ status: "loading" });
  const [basisSelection, setBasisSelection] = useState<BasisSelectionState>({
    builderContextId: null,
    sourceRevisionId: null,
    legalStructureRevisionId: null,
    legalStructureFingerprint: null,
    optionId: null,
  });
  const [basisOptionsReload, setBasisOptionsReload] = useState(0);
  const requestedBasisRefreshRef = useRef<string | null>(null);
  const askDillyAvailable = ["questions_required", "ready"].includes(
    prebuildReview?.state,
  );
  const reviewIdentity = prebuildReview?.job?.id ?? null;
  const askDillyHistoryKey = useMemo(() => [
    "builder-chat-history",
    "ask-dilly",
    projectId,
    reviewIdentity,
  ], [projectId, reviewIdentity]);
  const askDillyHistoryQuery = useQuery({
    queryKey: askDillyHistoryKey,
    queryFn: () => getPrebuildReviewAskDillyHistory(projectId),
    enabled: Boolean(projectId) && askDillyAvailable,
    retry: false,
  });
  const {
    state: databookGenerationState,
    flowDetected: databookFlowDetected,
    start: startBuilderDatabook,
    retry: retryBuilderDatabook,
  } = useBuilderDatabook({
    projectId,
    canEdit,
    basisSelectionLoading: ["loading", "pending_refresh"].includes(
      basisOptionsState.status,
    ),
    builderContextId: basisSelection.builderContextId,
    sourceRevisionId: basisSelection.sourceRevisionId,
    legalStructureRevisionId: basisSelection.legalStructureRevisionId,
    legalStructureFingerprint: basisSelection.legalStructureFingerprint,
    basisId: basisSelection.optionId,
    financialYearEndMonth: financialYearEndMonth(
      builderSettings.financialYearEnd,
    ),
  });
  const showConstruction = construction.query.data
    ? Boolean(construction.query.data.eligible || construction.query.data.sessions.length)
    : databookGenerationState.outcome?.error_type === "other";
  const [selectedDatabookVersionId, setSelectedDatabookVersionId] = useState<string | null>(null);
  useEffect(() => {
    if (construction.latestOutput) setSelectedDatabookVersionId(null);
  }, [construction.latestOutput]);
  const [databookDownloading, setDatabookDownloading] = useState(false);
  const databookVersionsQuery = useQuery({
    queryKey: ["databook-versions", projectId],
    queryFn: () => listDatabookVersions(projectId),
    enabled: Boolean(projectId),
    refetchInterval: ["queued", "running", "finalizing", "reconnecting"].includes(databookGenerationState.status)
      ? 5000 : false,
  });
  const constructionBaseVersion = databookVersionsQuery.data?.find((version) => version.id === construction.query.data?.base_version_id);
  const latestDatabookVersionId = databookVersionsQuery.data?.[0]?.id;
  const activeDatabookVersionId = selectedDatabookVersionId ?? latestDatabookVersionId;
  const versionOutcomeQuery = useQuery({
    queryKey: ["databook-version-outcome", projectId, activeDatabookVersionId, databookGenerationState.status],
    queryFn: () => getDatabookVersionOutcome(projectId, activeDatabookVersionId!),
    enabled: Boolean(projectId && activeDatabookVersionId),
  });
  const historicalDatabookVersion = activeDatabookVersionId !== latestDatabookVersionId
    ? databookVersionsQuery.data?.find((version) => version.id === activeDatabookVersionId)
    : undefined;
  const canEditDisplayedDatabook = canEdit
    && databookGenerationState.status === "ready"
    && Boolean(activeDatabookVersionId)
    && activeDatabookVersionId === latestDatabookVersionId;
  const databookSheetsQuery = useQuery({
    queryKey: ["databook-sheets", projectId, activeDatabookVersionId],
    queryFn: () => getDatabookSheets(projectId, activeDatabookVersionId),
    enabled: Boolean(projectId) && Boolean(activeDatabookVersionId),
  });
  useEffect(() => {
    setSelectedDatabookVersionId(null);
  }, [projectId]);
  useEffect(() => {
    if (databookGenerationState.status === "ready") {
      void queryClient.invalidateQueries({ queryKey: ["databook-versions", projectId] });
    }
  }, [databookGenerationState.status, projectId, queryClient]);
  const [databookPreviewRevision, setDatabookPreviewRevision] = useState(0);
  const [databookPreviewRefreshing, setDatabookPreviewRefreshing] = useState(false);
  const [selectedDatabookSheetChoice, setSelectedDatabookSheetChoice] = useState(
    () => loadLastDatabookSheet(projectId),
  );
  const [selectedDatabookSheetRowOffset, setSelectedDatabookSheetRowOffset] = useState(0);
  const [expandedDatabookOutlineGroups, setExpandedDatabookOutlineGroups] = useState<string[]>([]);
  const [expandedDatabookColumnOutlineGroups, setExpandedDatabookColumnOutlineGroups] = useState<string[]>([]);
  const [databookCellSelection, setDatabookCellSelection] = useState(null);
  useEffect(() => {
    setSelectedDatabookSheetChoice(loadLastDatabookSheet(projectId));
    setSelectedDatabookSheetRowOffset(0);
    setExpandedDatabookOutlineGroups([]);
    setExpandedDatabookColumnOutlineGroups([]);
    setDatabookCellSelection(null);
  }, [projectId]);
  useEffect(() => {
    setSelectedDatabookSheetRowOffset(0);
    setExpandedDatabookOutlineGroups([]);
    setExpandedDatabookColumnOutlineGroups([]);
    setDatabookCellSelection(null);
  }, [activeDatabookVersionId]);
  const selectedDatabookSheetName = selectedDatabookSheet(
    databookSheetsQuery.data?.sheets,
    selectedDatabookSheetChoice,
  );
  useEffect(() => {
    saveLastDatabookSheet(projectId, selectedDatabookSheetName);
  }, [projectId, selectedDatabookSheetName]);
  const databookSheetRowOffset = activeDatabookSheetRowOffset(
    selectedDatabookSheetChoice,
    selectedDatabookSheetName,
    selectedDatabookSheetRowOffset,
  );
  const databookSheetQuery = useQuery({
    queryKey: [...databookSheetQueryKey(
      projectId,
      selectedDatabookSheetName,
      databookSheetRowOffset,
      expandedDatabookOutlineGroups,
      expandedDatabookColumnOutlineGroups,
      databookPreviewRevision,
    ), databookSheetsQuery.data?.version_id],
    queryFn: () => getDatabookSheet(
      projectId,
      selectedDatabookSheetName,
      databookSheetRowOffset,
      expandedDatabookOutlineGroups,
      expandedDatabookColumnOutlineGroups,
      databookSheetsQuery.data?.version_id,
    ),
    enabled:
      Boolean(projectId)
      && Boolean(selectedDatabookSheetName)
      && Boolean(databookSheetsQuery.data?.version_id)
      && !databookPreviewRefreshing,
    staleTime: DATABOOK_SHEET_STALE_TIME,
    placeholderData: (previousSheet, previousQuery) => (
      previousSheet?.name === selectedDatabookSheetName
      && previousQuery?.queryKey[1] === projectId
      && previousQuery?.queryKey[6] === databookPreviewRevision
      && previousQuery?.queryKey[7] === databookSheetsQuery.data?.version_id
        ? previousSheet
        : undefined
    ),
  });
  const selectedDatabookRange = databookCellSelectionA1(
    databookCellSelection,
    databookSheetQuery.data ?? null,
  );
  const selectedDatabookContextLabel = (
    selectedDatabookSheetName && selectedDatabookRange
      ? `${selectedDatabookSheetName} · ${selectedDatabookRange.replaceAll("$", "")}`
      : undefined
  );
  const retainedAgentRequest = activeBuilderAgentRequest(
    askDillyHistoryQuery.data,
  );
  const agentRequestQuery = useQuery({
    queryKey: [
      "builder-agent-request",
      projectId,
      retainedAgentRequest?.request_id ?? null,
    ],
    queryFn: () => getBuilderAgentRequest(
      projectId,
      retainedAgentRequest.request_id,
    ),
    enabled: Boolean(projectId) && Boolean(retainedAgentRequest),
    retry: true,
    retryDelay: 2_000,
    refetchInterval: (query) => builderAgentPollingInterval(query.state.data),
  });
  const currentAgentRequest = (
    agentRequestQuery.data?.request_id === retainedAgentRequest?.request_id
      ? agentRequestQuery.data
      : retainedAgentRequest
  );
  const agentRequestActive = retainedAgentRequest !== null;
  const askDillyRequestError = (
    askDillyHistoryQuery.data?.request?.status === "failed"
      ? askDillyHistoryQuery.data.request.error
      : null
  );
  const [reviewBusy, setReviewBusy] = useState(false);
  const selectStage = (stage) => {
    autoOpenDetectedDatabookRef.current = false;
    setWs(stage === "questions" ? "basis" : stage === "files" ? "book" : stage);
  };
  const storeAcceptedAgentRequest = (request) => {
    queryClient.setQueryData(
      askDillyHistoryKey,
      (history) => updateBuilderChatRequest(history, request),
    );
  };
  const askDilly = async (question) => {
    if (ws === "upload") {
      throw new Error("Ask Dilli is not available during uploads.");
    }
    if (!projectId || !askDillyAvailable) {
      throw new Error("Source analysis must complete before using Ask Dilli.");
    }
    if (
      ws === "book"
      && (
        !canEditDisplayedDatabook
        || !selectedDatabookSheetName
      )
    ) {
      throw new Error("The databook must be ready before using Ask Dilli.");
    }
    const request = await askBuilderSourceAnalysis(
      projectId,
      question,
      ws,
      ws === "book"
        ? {
            currentSheet: selectedDatabookSheetName,
            selectedRange: selectedDatabookRange,
          }
        : undefined,
    );
    storeAcceptedAgentRequest(request);
    return "";
  };
  const refreshDatabookAfterVersionChange = useCallback(async () => {
    const previousSheetName = selectedDatabookSheetName;
    setSelectedDatabookVersionId(null);
    setDatabookPreviewRevision((revision) => revision + 1);
    setDatabookPreviewRefreshing(true);
    try {
      await queryClient.cancelQueries({ queryKey: ["databook-sheet", projectId] });
      await queryClient.cancelQueries({ queryKey: ["databook-insights", projectId] });
      queryClient.removeQueries({ queryKey: ["databook-sheet", projectId] });
      queryClient.removeQueries({ queryKey: ["databook-insights", projectId] });
      setDatabookCellSelection(null);
      setSelectedDatabookSheetRowOffset(0);
      setExpandedDatabookOutlineGroups([]);
      setExpandedDatabookColumnOutlineGroups([]);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["databook-sheets", projectId],
        }),
        queryClient.invalidateQueries({
          queryKey: ["databook-sheet", projectId],
          refetchType: "none",
        }),
        queryClient.invalidateQueries({
          queryKey: ["databook-versions", projectId],
        }),
      ]);
      const refreshedSheets = await getDatabookSheets(projectId);
      queryClient.setQueryData(
        ["databook-sheets", projectId, refreshedSheets.version_id],
        refreshedSheets,
      );
      const nextSheetName = selectedDatabookSheet(
        refreshedSheets.sheets,
        previousSheetName,
      );
      if (nextSheetName) {
        await queryClient.fetchQuery({
          queryKey: [...databookSheetQueryKey(projectId, nextSheetName, 0, [], [], databookPreviewRevision + 1), refreshedSheets.version_id],
          queryFn: () => getDatabookSheet(projectId, nextSheetName, 0, [], [], refreshedSheets.version_id),
        });
      }
      setSelectedDatabookSheetChoice(nextSheetName);
    } finally {
      setDatabookPreviewRefreshing(false);
    }
  }, [projectId, queryClient, selectedDatabookSheetName, databookPreviewRevision]);
  const retryDatabookVersion = () => {
    void Promise.all([
      queryClient.invalidateQueries({ queryKey: ["databook-sheets", projectId, activeDatabookVersionId] }),
      queryClient.invalidateQueries({ queryKey: ["databook-insights", projectId, activeDatabookVersionId] }),
      databookSheetQuery.refetch(),
    ]).catch(() => {
      toast({ title: "Databook refresh failed", description: "Try refreshing the page.", variant: "destructive" });
    });
  };
  const handledAgentRequestRef = useRef(null);
  useEffect(() => {
    const request = agentRequestQuery.data;
    if (!request || request.status === "active") return;
    if (handledAgentRequestRef.current === request.request_id) return;
    handledAgentRequestRef.current = request.request_id;

    if (request.status === "failed") {
      queryClient.setQueryData(
        askDillyHistoryKey,
        (history) => updateBuilderChatRequest(history, request),
      );
      return;
    }

    const askResult = request.ask_dilly_result;
    if (askResult) {
      queryClient.setQueryData(
        askDillyHistoryKey,
        (history) => appendBuilderChatTurn(history, askResult.turn),
      );
      if (request.operation === "ask_dilly_databook" && askResult.databook_changed) {
        void refreshDatabookAfterVersionChange().catch(() => {
          toast({
            title: "Databook refresh failed",
            description: "The Databook was updated. Refresh the page to load it.",
            variant: "destructive",
          });
        });
      } else if (request.operation === "ask_dilly_builder") {
        setCoverageReload((value) => value + 1);
        setEntityCatalogReload((value) => value + 1);
        setBasisOptionsReload((value) => value + 1);
      }
    }
  }, [
    agentRequestQuery.data,
    askDillyHistoryKey,
    queryClient,
    refreshDatabookAfterVersionChange,
  ]);
  useEffect(() => {
    if (
      databookFlowDetected
      && autoOpenDetectedDatabookRef.current
    ) {
      autoOpenDetectedDatabookRef.current = false;
      setWs("book");
    }
  }, [databookFlowDetected]);
  useEffect(() => {
    saveLastBuilderStage(projectId, ws);
  }, [projectId, ws]);
  const handleBuilderDatabookDownload = async () => {
    if (!projectId || !activeDatabookVersionId || databookDownloading) return;
    setDatabookDownloading(true);
    try {
      await downloadDatabookVersion(projectId, activeDatabookVersionId);
    } catch {
      toast({
        title: "Download failed",
        description: "The selected databook could not be downloaded.",
        variant: "destructive",
      });
    } finally {
      setDatabookDownloading(false);
    }
  };
  useEffect(() => {
    let cancelled = false;
    setBuilderSettings(DEFAULT_BUILDER_SETTINGS);
    if (!projectId) {
      setBuilderSettingsLoading(false);
      return () => { cancelled = true; };
    }

    setBuilderSettingsLoading(true);
    getBuilderAnalysisSettings(projectId)
      .then((settings) => {
        if (cancelled) return;
        setBuilderSettings((current) => ({
          ...current,
          financialYearEnd: settings.financial_year_end_month
            ? financialYearEndLabel(settings.financial_year_end_month)
            : DEFAULT_BUILDER_SETTINGS.financialYearEnd,
          scopeFrom: settings.analysis_period_start_fy
            ? `FY${String(settings.analysis_period_start_fy).slice(-2)}`
            : DEFAULT_BUILDER_SETTINGS.scopeFrom,
          scopeTo: settings.analysis_period_end_fy
            ? `FY${String(analysisPeriodEndSelection(
              settings.analysis_period_end_fy,
              settings.analysis_period_end_is_ytd,
            ).fullFiscalYear).slice(-2)}`
            : DEFAULT_BUILDER_SETTINGS.scopeTo,
          includeYtd: settings.analysis_period_end_fy
            ? settings.analysis_period_end_is_ytd
            : DEFAULT_BUILDER_SETTINGS.includeYtd,
          includeForecast: settings.forecast_years !== null,
          forecastYears: settings.forecast_years
            ? `${settings.forecast_years} ${settings.forecast_years === 1 ? "year" : "years"}`
            : DEFAULT_BUILDER_SETTINGS.forecastYears,
        }));
      })
      .catch(() => {
        if (!cancelled) {
          toast({
            title: "Analysis settings could not be loaded",
            description: "The default reporting period is shown. Refresh and try again before starting analysis.",
            variant: "destructive",
          });
        }
      })
      .finally(() => {
        if (!cancelled) setBuilderSettingsLoading(false);
      });
    return () => { cancelled = true; };
  }, [projectId]);
  useEffect(() => {
    let cancelled = false;
    savedDealContextRef.current = "";
    setDealContext("");
    setProjectName("");
    if (!projectId) {
      setDealContextLoading(false);
      return () => { cancelled = true; };
    }

    setDealContextLoading(true);
    getProjectWithRfi(projectId)
      .then((project) => {
        if (cancelled) return;
        const context = project.deal_context || "";
        setProjectName(project.name || "");
        savedDealContextRef.current = context;
        setDealContext(context);
      })
      .catch(() => {
        if (!cancelled) {
          toast({
            title: "Deal context could not be loaded",
            description: "You can still enter it before starting the review.",
            variant: "destructive",
          });
        }
      })
      .finally(() => {
        if (!cancelled) setDealContextLoading(false);
      });
    return () => { cancelled = true; };
  }, [projectId]);
  useEffect(() => {
    let cancelled = false;
    if (!projectId) {
      setUploadDocuments([]);
      setUploadDocumentsLoading(false);
      setUploadDocumentsLoadError(false);
      return () => { cancelled = true; };
    }

    setUploadDocumentsLoading(true);
    setUploadDocumentsLoadError(false);
    listProjectDocuments(projectId)
      .then((docs) => {
        if (cancelled) return;
        const storedDocuments = docs.map((doc) => ({
          localId: doc.id,
          id: doc.id,
          name: doc.original_filename,
          isSupported: doc.is_supported,
          size: doc.size_bytes,
          status: "done",
          file: null,
        }));
        setUploadDocuments((current) => {
          const storedNames = new Set(storedDocuments.map((document) => document.name));
          const optimisticDocuments = current.filter(
            (document) => !document.id && !storedNames.has(document.name),
          );
          return [...storedDocuments, ...optimisticDocuments];
        });
      })
      .catch(() => {
        if (!cancelled) setUploadDocumentsLoadError(true);
      })
      .finally(() => {
        if (!cancelled) setUploadDocumentsLoading(false);
      });

    return () => { cancelled = true; };
  }, [projectId]);
  const downloadDocById = async (documentId, name) => {
    if (!projectId || !documentId) {
      toast({ title: "File not available", description: `${name} is referenced here but isn't in the uploaded data room.` });
      return;
    }
    try {
      const { url } = await getDocumentDownloadUrl(projectId, documentId);
      const link = window.document.createElement("a");
      link.href = url; link.download = name; link.rel = "noopener";
      window.document.body.appendChild(link); link.click(); link.remove();
    } catch {
      toast({ title: "Download failed", description: `${name} could not be downloaded.`, variant: "destructive" });
    }
  };
  const downloadBasisComponent = async (component) => {
    if (!projectId) {
      toast({
        title: "Sources not available",
        description: "The project is unavailable.",
        variant: "destructive",
      });
      return;
    }
    try {
      await downloadBasisComponentSources(projectId, component);
    } catch {
      toast({
        title: "Download failed",
        description: `${component.label} sources could not be downloaded.`,
        variant: "destructive",
      });
    }
  };
  const [fileOv, setFileOv] = useState<any>({});
  const [qAns, setQAns] = useState<any>({});
  const [qAttachmentIds, setQAttachmentIds] = useState<Record<string, string[]>>({});
  const [questionUploads, setQuestionUploads] = useState<Record<string, boolean>>({});
  const [reviewQuestions, setReviewQuestions] = useState<any[]>([]);
  const reviewBatchRef = useRef(null);
  const applyReviewState = (next) => {
    setPrebuildReviewLoadFailed(false);
    const acceptedAnswers = Object.fromEntries(
      (next.answers || []).map((answer) => [answer.question_id, answer.answer]),
    );
    const acceptedAttachments = Object.fromEntries(
      (next.answers || []).map((answer) => [
        answer.question_id,
        answer.attachment_document_ids || [],
      ]),
    );
    if (reviewBatchRef.current !== next.batch_id) {
      reviewBatchRef.current = next.batch_id;
      setQAns(acceptedAnswers);
      setQAttachmentIds(acceptedAttachments);
      setReviewQuestions(next.questions || []);
    } else {
      if (next.questions?.length) {
        setReviewQuestions(next.questions);
      }
      if (next.answers?.length) {
        setQAns(acceptedAnswers);
        setQAttachmentIds(acceptedAttachments);
      }
    }
    setPrebuildReview(next);
  };
  useEffect(() => {
    if (!projectId) {
      setPrebuildReview(null);
      return;
    }
    let cancelled = false;
    getPrebuildReview(projectId)
      .then((next) => { if (!cancelled) applyReviewState(next); })
      .catch(() => {
        if (!cancelled) {
          setPrebuildReviewLoadFailed(true);
          toast({
            title: "Review status unavailable",
            description: "Refresh the page to load the current source review.",
            variant: "destructive",
          });
        }
      });
    return () => { cancelled = true; };
  }, [projectId]);
  useEffect(() => {
    if (!projectId || prebuildReview?.state !== "running") return;
    let cancelled = false;
    const timer = window.setInterval(() => {
      getPrebuildReview(projectId)
        .then((next) => { if (!cancelled) applyReviewState(next); })
        .catch(() => {});
    }, 2000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [projectId, prebuildReview?.state]);
  const showQuestionsStage = shouldShowQuestionsStage(
    prebuildReview?.state,
    prebuildReview?.batch_id,
    reviewQuestions.length,
  );
  const questionAnswersSubmitted = Boolean(prebuildReview?.answers?.length);
  const acceptedQuestionAnswers = useMemo(
    () => Object.fromEntries(
      (prebuildReview?.answers || []).map((answer) => [
        answer.question_id,
        answer.answer,
      ]),
    ),
    [prebuildReview?.answers],
  );
  const acceptedQuestionAttachments = useMemo(
    () => Object.fromEntries(
      (prebuildReview?.answers || []).map((answer) => [
        answer.question_id,
        answer.attachment_document_ids || [],
      ]),
    ),
    [prebuildReview?.answers],
  );
  const questionAnswersChanged = reviewQuestions.some((question) => {
    if ((qAns[question.id] || "") !== (acceptedQuestionAnswers[question.id] || "")) {
      return true;
    }
    const draftAttachments = [...(qAttachmentIds[question.id] || [])].sort();
    const acceptedAttachments = [
      ...(acceptedQuestionAttachments[question.id] || []),
    ].sort();
    return draftAttachments.join("\n") !== acceptedAttachments.join("\n");
  });
  const showCoverageStage = (
    coverageState.status === "loaded"
    && coverageState.coverage.recommend_not_build
  );
  const connectedStages = connectedDatabookBuilderStages(showCoverageStage);
  const navigationNeedsCoverage = ["questions_required", "ready"].includes(prebuildReview?.state);
  const stageNavigationLoading = Boolean(projectId) && (
    !prebuildReview
    || (navigationNeedsCoverage && coverageState.status === "loading")
  );
  const stageNavigationLoadFailed = prebuildReviewLoadFailed
    || (navigationNeedsCoverage && coverageState.status === "failed");
  const unansweredQuestionCount = reviewQuestions.filter(
    (question) => (
      !qAns[question.id]?.trim()
      && !(qAttachmentIds[question.id]?.length > 0)
    ),
  ).length;
  const questionAttachments = useMemo(() => {
    const documentsById = new Map(
      uploadDocuments
        .filter((document) => document.id)
        .map((document) => [document.id, document]),
    );
    return Object.fromEntries(
      reviewQuestions.map((question) => [
        question.id,
        (qAttachmentIds[question.id] || []).map((documentId) => ({
          documentId,
          filename: documentsById.get(documentId)?.name || "Attached file",
        })),
      ]),
    );
  }, [qAttachmentIds, reviewQuestions, uploadDocuments]);
  useEffect(() => {
    if (!prebuildReview) return;
    const previousReviewState = previousReviewStateRef.current;
    const reviewState = prebuildReview?.state ?? null;
    previousReviewStateRef.current = reviewState;
    if (
      !["questions_required", "ready"].includes(previousReviewState ?? "")
      && ["questions_required", "ready"].includes(reviewState ?? "")
      && ["upload", "analysis"].includes(ws)
    ) {
      pendingPostAnalysisNavigationRef.current = true;
    }
    if (ws === "questions" && !showQuestionsStage) {
      selectStage(prebuildReview?.state === "ready" ? "basis" : "ents");
    } else if (
      ws === "questions"
      && previousReviewState === "running"
      && reviewState === "ready"
    ) {
      selectStage("basis");
    }
  }, [
    prebuildReview?.state,
    prebuildReview?.batch_id,
    reviewQuestions.length,
    showQuestionsStage,
    ws,
  ]);
  useEffect(() => {
    if (coverageState.status !== "loaded") return;
    const nextStage = postAnalysisBuilderStage(
      coverageState.coverage.recommend_not_build,
    );
    if (
      pendingPostAnalysisNavigationRef.current
      && ["upload", "analysis"].includes(ws)
    ) {
      pendingPostAnalysisNavigationRef.current = false;
      selectStage(nextStage);
      return;
    }
    if (ws === "coverage" && !coverageState.coverage.recommend_not_build) {
      selectStage(nextStage);
    }
  }, [coverageState, ws]);
  useEffect(() => {
    if (
      !projectId
      || !["questions_required", "ready"].includes(prebuildReview?.state)
    ) {
      setCoverageState({ status: "loading" });
      return;
    }
    let cancelled = false;
    setCoverageState({ status: "loading" });
    getPrebuildReviewCoverage(projectId)
      .then((coverage) => {
        if (!cancelled) setCoverageState({ status: "loaded", coverage });
      })
      .catch(() => {
        if (!cancelled) setCoverageState({ status: "failed" });
      });
    return () => { cancelled = true; };
  }, [
    projectId,
    prebuildReview?.state,
    prebuildReview?.batch_id,
    coverageReload,
  ]);
  useEffect(() => {
    if (
      !projectId
      || !["questions_required", "ready"].includes(prebuildReview?.state)
    ) {
      setLegalEntitiesState({ status: "loading" });
      return;
    }
    let cancelled = false;
    setLegalEntitiesState({ status: "loading" });
    getPrebuildReviewLegalStructure(projectId)
      .then((result) => {
        if (!cancelled) {
          setLegalEntitiesState({
            status: "loaded",
            resource: result,
          });
        }
      })
      .catch(() => {
        if (!cancelled) setLegalEntitiesState({ status: "failed" });
      });
    return () => { cancelled = true; };
  }, [
    projectId,
    prebuildReview?.state,
    prebuildReview?.batch_id,
    entityCatalogReload,
  ]);
  useEffect(() => {
    if (!projectId || !canLoadBasisOptions(prebuildReview?.state)) {
      setBasisOptionsState({ status: "loading" });
      setBasisSelection({
        builderContextId: null,
        sourceRevisionId: null,
        legalStructureRevisionId: null,
        legalStructureFingerprint: null,
        optionId: null,
      });
      return;
    }
    let cancelled = false;
    setBasisOptionsState((current) =>
      current.status === "pending_refresh"
        ? current
        : { status: "loading" }
    );
    Promise.all([
      getPrebuildReviewBasisOptions(projectId),
      getDatabookJobStatus(projectId).catch(() => null),
    ])
      .then(async ([response, currentJob]) => {
        if (cancelled) return;
        setBasisOptionsState(basisLoadState(response));
        setBasisSelection((previous) =>
          reconcileBasisSelection(previous, response, currentJob)
        );
        if (!canEdit || !shouldRefreshBasis(response)) return;

        const refreshKey = [
          projectId,
          response.source_revision_id,
          response.legal_structure_revision_id,
          response.legal_structure_fingerprint,
        ].join(":");
        if (requestedBasisRefreshRef.current === refreshKey) return;
        requestedBasisRefreshRef.current = refreshKey;
        try {
          await refreshPrebuildReviewBasis(projectId);
          if (!cancelled) {
            setBasisOptionsReload((current) => current + 1);
          }
        } catch (error) {
          if (requestedBasisRefreshRef.current === refreshKey) {
            requestedBasisRefreshRef.current = null;
          }
          if (!cancelled) {
            setBasisOptionsState({
              status: "refresh_failed",
              sourceRevisionId: response.source_revision_id,
              error: error instanceof Error
                ? error.message
                : "Could not refresh Basis options.",
            });
          }
        }
      })
      .catch(() => {
        if (!cancelled) {
          setBasisOptionsState({
            status: "failed",
            sourceRevisionId: null,
            invalidArtifact: false,
          });
        }
      });
    return () => { cancelled = true; };
  }, [
    projectId,
    prebuildReview?.state,
    prebuildReview?.batch_id,
    basisOptionsReload,
    canEdit,
  ]);
  useEffect(() => {
    if (basisOptionsState.status !== "pending_refresh") return;
    const timer = window.setInterval(() => {
      setBasisOptionsReload((current) => current + 1);
    }, BASIS_REFRESH_POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [basisOptionsState.status]);
  const builderSettingsPayload = (values = builderSettings) => ({
    financial_year_end_month: financialYearEndMonth(values.financialYearEnd),
    analysis_period_start_fy: periodFiscalYear(values.scopeFrom),
    analysis_period_end_fy: analysisPeriodEndFiscalYear(
      periodFiscalYear(values.scopeTo),
      values.includeYtd,
    ),
    analysis_period_end_is_ytd: values.includeYtd,
    forecast_years: values.includeForecast
      ? forecastYearCount(values.forecastYears)
      : null,
  });
  const changeBuilderSettings = async (patch) => {
    const previous = builderSettings;
    const next = { ...builderSettings, ...patch };
    const startFy = periodFiscalYear(next.scopeFrom);
    const endFy = periodFiscalYear(next.scopeTo);
    if (
      (patch.scopeFrom !== undefined || patch.includeYtd === false)
      && startFy && endFy && startFy > endFy
    ) {
      next.scopeTo = next.scopeFrom;
    }
    setBuilderSettings(next);
    if (
      !projectId
      || !canEdit
    ) {
      return;
    }
    setBuilderSettingsSaving(true);
    try {
      await updateBuilderAnalysisSettings(
        projectId,
        builderSettingsPayload(next),
      );
    } catch {
      setBuilderSettings(previous);
      toast({
        title: "Analysis settings were not saved",
        description: "Refresh the page and try again.",
        variant: "destructive",
      });
    } finally {
      setBuilderSettingsSaving(false);
    }
  };
  const beginPrebuildReview = async () => {
    if (
      !projectId
      || reviewBusy
      || builderSettingsLoading
      || builderSettingsSaving
    ) {
      return;
    }
    setReviewBusy(true);
    selectStage("upload");
    try {
      const settings = builderSettingsPayload();
      await updateBuilderAnalysisSettings(projectId, settings);
      await startPrebuildReview(projectId, dealContext, settings);
      savedDealContextRef.current = dealContext.trim();
      applyReviewState(await getPrebuildReview(projectId));
    } catch (error) {
      toast({
        title: "Review could not start",
        description: backendErrorMessage(
          error,
          "The uploaded sources are still available. Try again.",
        ),
        variant: "destructive",
      });
      selectStage("upload");
    } finally {
      setReviewBusy(false);
    }
  };
  const saveDealContext = async () => {
    if (!projectId || !canEdit || dealContextLoading) return false;
    const normalized = dealContext.trim();
    if (normalized === savedDealContextRef.current) return true;
    try {
      await updateProject(projectId, { deal_context: normalized });
      savedDealContextRef.current = normalized;
      setDealContext(normalized);
      return true;
    } catch {
      toast({
        title: "Deal context was not saved",
        description: "Try again or continue to save it when the review starts.",
        variant: "destructive",
      });
      return false;
    }
  };
  const retryReview = async () => {
    if (!projectId || reviewBusy) return;
    setReviewBusy(true);
    try {
      await retryPrebuildReview(projectId);
      applyReviewState(await getPrebuildReview(projectId));
    } catch (error) {
      toast({
        title: "Retry could not start",
        description: backendErrorMessage(
          error,
          "Refresh the page and try again.",
        ),
        variant: "destructive",
      });
    } finally {
      setReviewBusy(false);
    }
  };
  const retryBasisOptions = async () => {
    if (basisOptionsState.status !== "refresh_failed") {
      setBasisOptionsReload((current) => current + 1);
      return;
    }
    if (!projectId || !canEdit) return;

    const refreshKey = [
      projectId,
      basisSelection.sourceRevisionId,
      basisSelection.legalStructureRevisionId,
      basisSelection.legalStructureFingerprint,
    ].join(":");
    requestedBasisRefreshRef.current = refreshKey;
    setBasisOptionsState({
      status: "pending_refresh",
      sourceRevisionId: basisOptionsState.sourceRevisionId,
    });
    try {
      await refreshPrebuildReviewBasis(projectId);
      setBasisOptionsReload((current) => current + 1);
    } catch (error) {
      if (requestedBasisRefreshRef.current === refreshKey) {
        requestedBasisRefreshRef.current = null;
      }
      setBasisOptionsState({
        status: "refresh_failed",
        sourceRevisionId: basisOptionsState.sourceRevisionId,
        error: error instanceof Error
          ? error.message
          : "Could not refresh Basis options.",
      });
    }
  };
  const attachQuestionFiles = async (questionId, files) => {
    if (!projectId || !canEdit || files.length === 0) return;
    setQuestionUploads((current) => ({ ...current, [questionId]: true }));
    try {
      const result = await uploadProjectDocuments(projectId, files);
      const uploadedDocuments = result.documents.map((document) => ({
        localId: `document-${document.id}`,
        id: document.id,
        name: document.original_filename,
        isSupported: document.is_supported,
        size: document.size_bytes,
        status: "done",
        file: null,
      }));
      setUploadDocuments((current) => {
        const existingIds = new Set(current.map((document) => document.id));
        return [
          ...current,
          ...uploadedDocuments.filter((document) => !existingIds.has(document.id)),
        ];
      });
      setQAttachmentIds((current) => ({
        ...current,
        [questionId]: [
          ...new Set([
            ...(current[questionId] || []),
            ...result.documents.map((document) => document.id),
          ]),
        ],
      }));
      if (result.skipped.length > 0 || result.failed.length > 0) {
        toast({
          title: "Files processed with issues",
          description: documentBatchUploadFeedback(result),
          variant: result.documents.length === 0 && result.failed.length > 0
            ? "destructive"
            : undefined,
        });
      }
    } catch {
      toast({
        title: "Upload failed",
        description: "The files were not attached. Try again.",
        variant: "destructive",
      });
    } finally {
      setQuestionUploads((current) => {
        const next = { ...current };
        delete next[questionId];
        return next;
      });
    }
  };
  const submitQuestionAnswers = async () => {
    if (
      !projectId
      || !canEdit
      || !prebuildReview?.batch_id
      || reviewBusy
      || Object.keys(questionUploads).length > 0
      || unansweredQuestionCount > 0
      || reviewQuestions.length === 0
    ) {
      return;
    }
    const revisingAnswers = questionAnswersSubmitted;
    setReviewBusy(true);
    try {
      const mutation = await submitPrebuildReviewAnswers(
        projectId,
        prebuildReview.batch_id,
        reviewQuestions.map((question) => ({
          question_id: question.id,
          answer: qAns[question.id] || "",
          attachment_document_ids: qAttachmentIds[question.id] || [],
        })),
      );
      setPrebuildReview((current) => current ? {
        ...current,
        state: mutation.state,
        answers: reviewQuestions.map((question) => ({
          question_id: question.id,
          answer: qAns[question.id] || "",
          attachment_document_ids: qAttachmentIds[question.id] || [],
        })),
      } : current);
      try {
        applyReviewState(await getPrebuildReview(projectId));
      } catch {
        // The accepted answer job will continue to be refreshed by status polling.
      }
      if (revisingAnswers) {
        setCoverageReload((current) => current + 1);
        setEntityCatalogReload((current) => current + 1);
        setBasisOptionsReload((current) => current + 1);
      }
    } catch {
      toast({
        title: "Answers could not be submitted",
        description: "Your answers are still here. Try again.",
        variant: "destructive",
      });
    } finally {
      setReviewBusy(false);
    }
  };
  const [filesCtx, setFilesCtx] = useState<any>(null);
  const [recompNote, setRecompNote] = useState(false);
  useEffect(() => { if (!recompNote) return; const t = setTimeout(() => setRecompNote(false), 3600); return () => clearTimeout(t); }, [recompNote]);
  const answerQ = (id, label) => { setQAns((p) => ({ ...p, [id]: label })); setRecompNote(true); };
  const [tblOv, setTblOv] = useState<any>({});
  const onTblOv = (key, patch) => {
    setTblOv((p) => {
      const n = { ...p };
      if (patch == null) delete n[key];
      else n[key] = { ...(n[key] || {}), ...patch };
      return n;
    });
    setRecompNote(true);
  };
  const toggleFile = (id, ov) => {
    setFileOv((p) => {
      const n = { ...p };
      const apply = (k, o) => { if (o == null) delete n[k]; else n[k] = o; };
      if (typeof id === "object") Object.entries(id).forEach(([k, o]) => apply(k, o));
      else apply(id, ov);
      return n;
    });
    setRecompNote(true);
  };
  // Optional coverage-readiness gate — only in the flow when coverage is
  // thin enough to warrant requesting the gaps before building.
  const [libOpen, setLibOpen] = useState(false);
  const [libAdded, setLibAdded] = useState<any[]>([]);
  const [galleryOpen, setGalleryOpen] = useState(false);
  // Fields panel starts in plain-question mode; the pivot field well is
  // behind an "Advanced" toggle for people who know what a pivot is.
  const [fieldsSimple, setFieldsSimple] = useState(true);
  const [hoverArea, setHoverArea] = useState<any>(null);
  const [mode, setMode] = useState("det");
  const [reports, setReports] = useState(RMAP);
  const [rail, setRail] = useState(RAIL);
  // New files that arrived since the book was last computed. Cleared by
  // recalculating, which turns the promised additions into real drafts.
  const [pendingPass, setPendingPass] = useState(INCOMING_PASS);
  const applyPass = () => {
    if (!pendingPass) return;
    setReports((r) => {
      const next = { ...r };
      pendingPass.adds.forEach((a) => {
        next[a.id] = { id: a.id, name: a.name, ds: a.ds, tpl: a.tpl, periods: [0, 1, 2, 3, 4], agent: true };
      });
      return next;
    });
    setRail((rl) => rl.map(([s, ids]) => {
      const extra = pendingPass.adds.filter((a) => a.section === s).map((a) => a.id);
      return extra.length ? [s, [...ids, ...extra]] : [s, ids];
    }));
    setPendingPass(null);
  };
  const [selRows, setSelRows] = useState<any[]>([]);
  const [groupName, setGroupName] = useState("New group");
  const [ctx, setCtx] = useState<any>(null);
  const [reconOpen, setReconOpen] = useState<any>(null);
  const rep = reports[sel];
  const ds = DS[rep.ds];
  const spec = specs[sel] || mkSpec(rep);
  const patch = (p) => setSpecs((s) => ({ ...s, [sel]: { ...(s[sel] || mkSpec(rep)), ...p } }));

  const dimFields = ["entity", "site", ...Object.keys(ds.dims), "sku"];
  const inArea = (key) =>
    spec.rows.some((r) => r.key === key) ? "rows"
    : spec.colField === key ? "columns"
    : spec.values.includes(key) ? "values" : null;

  const enforceSkuLast = (rows) => {
    const i = rows.findIndex((r) => r.key === "sku");
    if (i === -1 || i === rows.length - 1) return rows;
    const c = [...rows]; const [s] = c.splice(i, 1); c.push(s); return c;
  };
  const removeEverywhere = (key, s) => ({
    ...s,
    rows: s.rows.filter((r) => r.key !== key),
    colField: s.colField === key ? null : s.colField,
    values: s.values.filter((v) => v !== key),
  });
  const moveField = (key, to, beforeKey) => {
    const isMeasure = ds.measures.some((m) => m.k === key);
    let s = removeEverywhere(key, { ...spec });
    if (key === "__custom__" && to === "off") s.customGroups = [];
    if (to === "values" && isMeasure) {
      const arr = [...s.values];
      const bi = beforeKey ? arr.indexOf(beforeKey) : -1;
      bi === -1 ? arr.push(key) : arr.splice(bi, 0, key);
      s.values = arr;
    } else if (to === "rows" && !isMeasure) {
      const chip = { key, vis: key === "sku" ? undefined : "sub" };
      const arr = [...s.rows];
      const bi = beforeKey ? arr.findIndex((r) => r.key === beforeKey) : -1;
      bi === -1 ? arr.push(chip) : arr.splice(bi, 0, chip);
      s.rows = enforceSkuLast(arr);
    } else if (to === "columns" && !isMeasure && key !== "sku" && key !== "__custom__") {
      s.colField = key; s.split = null;
    } else if (to !== "off") return;
    setSpecs((st) => ({ ...st, [sel]: { ...s, tpl: "custom" } }));
  };
  const toggleField = (key) => {
    const isMeasure = ds.measures.some((m) => m.k === key);
    if (inArea(key)) setSpecs((st) => ({ ...st, [sel]: { ...removeEverywhere(key, { ...spec }), tpl: "custom" } }));
    else moveField(key, isMeasure ? "values" : "rows");
  };
  const setVis = (key, vis) => {
    const pref = key + "::";
    const overrides = Object.fromEntries(Object.entries(spec.overrides).filter(([k]) => !k.startsWith(pref)));
    patch({ rows: spec.rows.map((r) => (r.key === key ? { ...r, vis } : r)), overrides, tpl: "custom" });
  };
  const renameRow = (r, v) => {
    if (!v) return;
    if (r.type === "leaf") patch({ renames: { ...(spec.renames || {}), [r.label]: v } });
    else if (r.gk) patch({ catRenames: { ...(spec.catRenames || {}), [r.gk]: v } });
  };
  const cycleRow = (gk, vis) => patch({ overrides: { ...spec.overrides, [gk]: vis === "sub" ? "hidden" : vis === "hier" ? "label" : "hier" } });
  const toggleOpen = (pth) => patch({ open: { ...spec.open, [pth]: !spec.open[pth] } });
  const applyTemplate = (t) => {
    const conf = t.mk(ds);
    patch({ tpl: t.id, ...conf, overrides: {}, open: {}, topN: ds.orderBy === "index" ? 0 : conf.topN });
    setSelRows([]);
    setGalleryOpen(false);
  };
  const setSplit = (v) => patch({ split: v || null, colField: v ? null : spec.colField, tpl: "custom" });

  // sources and period mask
  const mask = useMemo(() => new Set(flat(ds.sources.filter((s) => spec.sources.includes(s.id)).map((s) => s.periods))), [ds, spec.sources]);
  const perExcl = useMemo(() => {
    return P.map((_, pi) => {
      const src = ds.sources.find((s) => spec.sources.includes(s.id) && s.periods.includes(pi));
      const sel = src && spec.srcSel && spec.srcSel[src.id];
      return new Set(sel ? sel.excluded : []);
    });
  }, [ds, spec.sources, spec.srcSel]);
  const scoped = useMemo(() => {
    let a = ds.atoms;
    if (rep.filter) a = a.filter((x) => String(getv(x, rep.filter[0])) === rep.filter[1]);
    return a.map((x) => {
      const v = {};
      Object.entries(x.v).forEach(([k, arr]) => {
        v[k] = arr.map((val, i) => (mask.has(i) && !perExcl[i].has(x.name) ? val : 0));
      });
      return { ...x, v };
    });
  }, [ds, rep, mask, perExcl]);
  const SOURCE = useMemo(() => sumAgg(ds, scoped), [ds, scoped]);
  const innerRows = spec.split ? (spec.rows.filter((r) => r.key !== spec.split).length ? spec.rows.filter((r) => r.key !== spec.split) : [{ key: "sku" }]) : spec.rows;
  const gm = useMemo(() => {
    const m = {};
    (spec.customGroups || []).forEach((g) => g.members.forEach((n) => (m[n] = g.name)));
    return m;
  }, [spec.customGroups]);
  const rows = useMemo(() => {
    if (spec.split) return null;
    const out = [];
    buildRows(ds, scoped, spec.rows, 0, 0, "r", { topN: spec.topN, pos: spec.pos, overrides: spec.overrides, open: spec.open, groupsMap: gm, seq: spec.seq }, out);
    return out;
  }, [ds, scoped, spec, gm]);
  const shownMeasures = spec.values.length ? spec.values : [ds.measures[0].k];
  const fullScope = !rep.filter;
  const effP = spec.periodsSel && spec.periodsSel.length ? spec.periodsSel : rep.periods;
  const repEff = { ...rep, periods: effP };
  const [srcModal, setSrcModal] = useState<any>(null);
  const [perMenu, setPerMenu] = useState<any>(null);
  const statusOf = (id) => {
    const r = reports[id]; const sp = specs[id];
    if (sp && sp.tpl !== r.tpl) return "custom";
    if (r.agent) return "draft";
    if (r.flag === "review") return "review";
    return "auto";
  };
  const handleAgentBuild = (def, sectionName) => {
    setReports((r) => ({ ...r, [def.id]: def }));
    setRail((rl) => rl.map(([s, ids]) => (s === sectionName ? [s, [...ids, def.id]] : [s, ids])));
    setSel(def.id);
    setMode("det");
  };
  // Duplicate a report so you can spin a variant off an existing table
  // without rebuilding it. Copies the report definition and its live
  // layout spec, and drops the copy right after the original.
  const duplicateReport = (id) => {
    agentSeq += 1;
    const newId = "copy_" + agentSeq;
    const src = reports[id];
    const srcSpec = specs[id] || mkSpec(src);
    setReports((r) => ({ ...r, [newId]: { ...src, id: newId, name: src.name + " copy", agent: true, flag: undefined } }));
    setSpecs((s) => ({ ...s, [newId]: JSON.parse(JSON.stringify(srcSpec)) }));
    setRail((rl) => rl.map(([s, ids]) => {
      const i = ids.indexOf(id);
      if (i === -1) return [s, ids];
      const next = [...ids]; next.splice(i + 1, 0, newId); return [s, next];
    }));
    setSel(newId);
    setMode("det");
  };
  const selectable = mode === "det" && customize && !spec.split && !spec.colField && spec.rows.length > 0 && spec.rows[spec.rows.length - 1].key === "sku";
  const toggleSelRow = (label) => setSelRows((s) => (s.includes(label) ? s.filter((x) => x !== label) : [...s, label]));
  const createGroup = () => {
    if (!selRows.length) return;
    const name = groupName.trim() || "Group " + ((spec.customGroups || []).length + 1);
    patch({
      customGroups: [...(spec.customGroups || []), { name, members: selRows }],
      rows: spec.rows.some((r) => r.key === "__custom__") ? spec.rows : [{ key: "__custom__", vis: "sub" }, ...spec.rows],
      tpl: "custom",
    });
    setSelRows([]);
  };
  const groupByDim = (key) => {
    patch({ rows: [{ key, vis: "sub" }, { key: "sku" }], tpl: "custom", overrides: {}, open: {}, colField: null, split: null });
    setCtx(null);
  };
  const moveRow = (fromRi, toRi) => {
    if (!rows || !rows[fromRi] || !rows[toRi] || rows[fromRi].type !== "leaf" || rows[toRi].type !== "leaf" || fromRi === toRi) return;
    const depth = rows[fromRi].depth;
    let s = fromRi; while (s - 1 >= 0 && rows[s - 1].type === "leaf" && rows[s - 1].depth === depth) s--;
    let e = fromRi; while (e + 1 < rows.length && rows[e + 1].type === "leaf" && rows[e + 1].depth === depth) e++;
    if (toRi < s || toRi > e) return;
    const names = rows.slice(s, e + 1).map((r) => r.label);
    const dragged = rows[fromRi].label;
    const targetName = rows[toRi].label;
    const moving = selRows.includes(dragged) && selRows.length > 1 ? names.filter((n) => selRows.includes(n)) : [dragged];
    if (moving.includes(targetName)) return;
    const rest = names.filter((n) => !moving.includes(n));
    let at = rest.indexOf(targetName);
    if (fromRi < toRi) at += 1;
    const arr = [...rest.slice(0, at), ...moving, ...rest.slice(at)];
    const seq = { ...(spec.seq || {}) };
    arr.forEach((n, ix) => (seq[n] = ix));
    patch({ seq, tpl: "custom" });
  };
  const dropPeriod = (pi) => { if (effP.length > 1) patch({ periodsSel: effP.filter((x) => x !== pi) }); };
  const applySimilar = () => {
    const structural = { tpl: spec.tpl, rows: spec.rows, topN: spec.topN, colField: spec.colField, colMode: spec.colMode, split: spec.split, overrides: spec.overrides, periodsSel: spec.periodsSel };
    setSpecs((st) => {
      const next = { ...st };
      Object.values(reports).forEach((r) => {
        if (r.ds === rep.ds && r.id !== rep.id) next[r.id] = { ...(st[r.id] || mkSpec(r)), ...structural };
      });
      return next;
    });
  };
  const [tplOpen, setTplOpen] = useState<any>(null);
  const [repMenu, setRepMenu] = useState<any>(null);
  const [selCols, setSelCols] = useState<any[]>([]);
  // In a multi-block table (e.g. Gross profit + GP margin side by side) the same
  // period index appears once per block, so a raw `selCols.includes(pi)` check would
  // highlight every twin. Track the block key of the last click so the highlight is
  // scoped to that block; period-level operations (Delete / Keep only / Fill) still
  // apply to the pi and therefore affect all blocks as expected.
  const [colBlock, setColBlock] = useState<any>(null);
  const colAnchor = useRef(null);
  const colClick = (pi, e, blockKey = null) => {
    if (e.shiftKey && colAnchor.current != null) {
      const a = effP.indexOf(colAnchor.current), b = effP.indexOf(pi);
      if (a >= 0 && b >= 0) { const [lo, hi] = a < b ? [a, b] : [b, a]; setSelCols(effP.slice(lo, hi + 1)); setColBlock(blockKey); return; }
    }
    if (e.metaKey || e.ctrlKey) { colAnchor.current = pi; setColBlock(blockKey); setSelCols((c) => (c.includes(pi) ? c.filter((x) => x !== pi) : [...c, pi])); return; }
    colAnchor.current = pi;
    setColBlock(blockKey);
    setSelCols((c) => (c.length === 1 && c[0] === pi ? [] : [pi]));
  };
  const [attachFor, setAttachFor] = useState<any>(null);
  const attachFile = (id, label, opts = {}) => {
    setSpecs((st) => {
      const sp = st[id];
      let next = { ...sp };
      const pis = opts.colPis || (opts.colPi != null ? [opts.colPi] : null);
      if (pis) {
        const cs = { ...(next.colSrc || {}) };
        pis.forEach((p) => (cs[p] = label));
        next.colSrc = cs;
        if (next.srcSel) {
          const heal = {};
          Object.entries(next.srcSel).forEach(([sid, v]) => {
            heal[sid] = { ...v, excluded: (v.excluded || []).filter((x) => !pis.includes(x)) };
          });
          next.srcSel = heal;
        }
        if (next.periodsSel) next.periodsSel = [...new Set([...next.periodsSel, ...pis])].sort((a, b) => a - b);
      } else if (opts.rows) {
        next.rowSrc = [...(next.rowSrc || []), { rows: opts.rows, label }];
      } else {
        next.extraSrc = opts.replace ? [label] : [...(next.extraSrc || []), label];
      }
      if (next.tpl === "pvm" && !pis && !opts.rows) next.pvmSrc = label;
      return { ...st, [id]: next };
    });
    if (opts.rows) setSelRows([]);
    if (opts.colPis) (setSelCols([]), setColBlock(null));
    setSel(id);
    setAttachFor(null);
  };
  const addTemplate = (cfg) => {
    libSeq += 1;
    const id = "tpl_" + libSeq;
    const def = { id, name: "Price volume mix, " + P[cfg.pvmFrom] + " to " + P[cfg.pvmTo], ds: "sales", tpl: "sub_below", periods: [0, 1, 2, 3, 4], lib: true };
    const spec2 = { ...mkSpec(def), tpl: "pvm", ...cfg };
    setReports((r) => ({ ...r, [id]: def }));
    setRail((rl) => rl.map(([s, ids]) => (s === "Revenue" ? [s, [...ids, id]] : [s, ids])));
    setSpecs((st) => ({ ...st, [id]: spec2 }));
    setSel(id);
    setTplOpen(null);
  };
  const addLibrary = (entry) => {
    libSeq += 1;
    const id = "lib_" + libSeq;
    const def = { id, name: entry.name, ds: entry.ds, tpl: "sub_below", periods: [0, 1, 2, 3, 4], lib: true };
    const base = mkSpec(def);
    const spec2 = entry.mode === "summary"
      ? { ...base, rows: [{ key: entry.dim }], topN: 0, tpl: "custom" }
      : entry.mode === "topn"
      ? { ...base, rows: [{ key: "sku" }], topN: 10, tpl: "custom" }
      : { ...base, rows: [{ key: entry.dim, vis: "sub" }, { key: "sku" }], tpl: "custom" };
    setReports((r) => ({ ...r, [id]: def }));
    setRail((rl) => rl.map(([s, ids]) => (s === entry.section ? [s, [...ids, id]] : [s, ids])));
    setSpecs((st) => ({ ...st, [id]: spec2 }));
    setLibAdded((a) => [...a, entry.id]);
    setSel(id);
  };
  const insertPeriod = (pi) => patch({ periodsSel: [...effP.filter((x) => x !== pi), pi].sort((a, b) => a - b) });
  const union = (a, b) => [...new Set([...a, ...b])];
  const sweep = useRef<any>({ active: false, base: [], start: -1 });
  const anchor = useRef(null);
  const leafRange = (a, b) => {
    const lo = Math.min(a, b), hi = Math.max(a, b), out = [];
    for (let i = lo; i <= hi; i++) if (rows && rows[i] && rows[i].type === "leaf") out.push(rows[i].label);
    return out;
  };
  const sweepStart = (ri, e) => {
    if (!selectable || !rows || !rows[ri] || rows[ri].type !== "leaf") return;
    const label = rows[ri].label;
    if (e.shiftKey && anchor.current != null) {
      sweep.current = { active: true, base: [...selRows], start: anchor.current };
      setSelRows(union(selRows, leafRange(anchor.current, ri)));
    } else if (e.metaKey || e.ctrlKey) {
      if (selRows.includes(label)) { anchor.current = ri; setSelRows(selRows.filter((x) => x !== label)); return; }
      sweep.current = { active: true, base: [...selRows], start: ri };
      anchor.current = ri;
      setSelRows(union(selRows, [label]));
    } else {
      sweep.current = { active: true, base: [], start: ri };
      anchor.current = ri;
      setSelRows([label]);
    }
  };
  const sweepMove = (ri) => {
    if (!sweep.current.active || !rows) return;
    setSelRows(union(sweep.current.base, leafRange(sweep.current.start, ri)));
  };
  useEffect(() => {
    const up = () => { sweep.current.active = false; };
    const key = (e) => { if (e.key === "Escape") { setSelRows([]); (setSelCols([]), setColBlock(null)); } };
    window.addEventListener("mouseup", up);
    window.addEventListener("keydown", key);
    return () => { window.removeEventListener("mouseup", up); window.removeEventListener("keydown", key); };
  }, []);
  const skuUsed = spec.rows.some((r) => r.key === "sku");
  const tplName = spec.tpl === "custom" ? "Custom" : spec.tpl === "nwc" ? "NWC classes" : (TPLMAP[spec.tpl] || {}).name || spec.tpl;

  const onDragStart = (key) => (e) => { dragRef.key = key; e.dataTransfer.setData("text/plain", key); e.dataTransfer.effectAllowed = "move"; };
  const areaProps = (area) => ({
    onDragOver: (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; },
    onDragEnter: () => setHoverArea(area),
    onDragLeave: (e) => { if (e.currentTarget === e.target) setHoverArea(null); },
    onDrop: (e) => { e.preventDefault(); setHoverArea(null); if (dragRef.key) moveField(dragRef.key, area); dragRef.key = null; },
  });
  const chipDropProps = (area, beforeKey) => ({
    onDragOver: (e) => { e.preventDefault(); e.stopPropagation(); },
    onDrop: (e) => { e.preventDefault(); e.stopPropagation(); setHoverArea(null); if (dragRef.key && dragRef.key !== beforeKey) moveField(dragRef.key, area, beforeKey); dragRef.key = null; },
  });

  const showDatabookProgress = ws === "book"
    && databookGenerationState.status !== "ready"
    && !activeDatabookVersionId;

  return (
    <div className="h-screen flex flex-col bg-card text-foreground" style={UIF}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap');
        @keyframes tieflash { from { opacity: .25 } to { opacity: 1 } }
        @media (prefers-reduced-motion: reduce) { * { animation: none !important; transition: none !important; } }
        button, input, select, textarea { border-radius: 4px; }
        button { transition: background-color .12s ease, color .12s ease, border-color .12s ease, opacity .12s ease; }
        input, select, textarea { transition: border-color .12s ease, box-shadow .12s ease; }
        input:focus, select:focus, textarea:focus { box-shadow: 0 0 0 2px rgba(23,23,23,.10); }
        .pop { border-radius: 8px; box-shadow: 0 10px 30px rgba(23,23,23,.16); overflow: hidden; animation: popin .12s ease-out; }
        @keyframes popin { from { opacity: 0; transform: translateY(3px); } to { opacity: 1; transform: none; } }
        button:active { transform: translateY(0.5px); }
        tr[draggable="true"] { cursor: grab; }
        tr[draggable="true"]:active { cursor: grabbing; }
        table td, table th { user-select: none; }
        ::selection { background: hsl(var(--foreground)); color: hsl(var(--background)); }
      `}</style>

      <ProjectHeader title={projectName || undefined} />

      {/* top bar */}
      <div className={showDatabookProgress ? "bg-card border-b border-border/60 shrink-0" : "bg-card border-b border-border/60 px-5 py-3 flex flex-wrap items-center justify-between gap-3 shrink-0 lg:flex-nowrap"}>
        <div className={showDatabookProgress ? "mx-auto flex w-full max-w-[880px] items-center gap-3 px-8 py-3" : "contents"}>
        <div className={showDatabookProgress ? "hidden" : "flex items-center gap-2 min-w-0"}>
          {ws === "prototype-book" && (
          <button
            onClick={() => setRailOpen(!railOpen)}
            className={"rounded-lg px-2.5 py-1.5 flex items-center gap-1.5 border transition-colors " +
              (railOpen
                ? "text-muted-foreground border-border hover:bg-muted"
                : "text-foreground border-foreground/40 bg-foreground/[0.03] hover:bg-foreground/[0.06] font-semibold")}
            title={railOpen ? "Hide the reports panel" : "Show the reports panel"}
            style={{ fontSize: 11.5 }}
          >
            {railOpen ? <ChevronLeft size={12} /> : <ChevronRight size={12} />}
            <span>Reports</span>
          </button>
          )}
        </div>
        <div className={`min-w-0 max-w-full overflow-x-auto ${showDatabookProgress ? "flex-1" : ""}`}>
        <BuilderStageNavigation
          activeStage={ws === "analysis" ? "upload" : ws}
          stages={connectedStages}
          loading={stageNavigationLoading}
          loadFailed={stageNavigationLoadFailed}
          fullWidth={showDatabookProgress}
          questionsBadge={unansweredQuestionCount}
          onStageSelect={(stage) => {
            if (ws === "ents" && stage !== "ents" && legalStructureDirty) {
              toast({
                title: "Save legal structure changes",
                description: "Save or cancel the perimeter changes before leaving this stage.",
                variant: "destructive",
              });
              return;
            }
            if (stage === "ents") setFilesCtx(null);
            selectStage(stage);
          }}
        />
        </div>
        <div className={showDatabookProgress ? "flex shrink-0 items-center gap-3 empty:hidden" : "flex items-center gap-3"}>
          {ws === "book" && (databookGenerationState.status === "ready" || Boolean(activeDatabookVersionId)) && (
            <button
              type="button"
              onClick={() => {
                setSelectedDatabookVersionId(activeDatabookVersionId ?? null);
                void startBuilderDatabook();
              }}
              disabled={!canEdit || ["queued", "running", "finalizing", "reconnecting", "checking"].includes(databookGenerationState.status)}
              className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              title="Rebuild the databook"
            >
              <RotateCcw aria-hidden="true" size={13} />
              Rebuild
            </button>
          )}
          {(ws === "book" || ws === "qna") && (
            <DatabookVersionManagement
              projectId={projectId}
              canEdit={canEdit && !["queued", "running", "finalizing", "reconnecting", "checking"].includes(databookGenerationState.status)}
              selectedVersionId={activeDatabookVersionId}
              onView={setSelectedDatabookVersionId}
              onUploaded={refreshDatabookAfterVersionChange}
            />
          )}
          {ws === "prototype-book" && (
          <div key={JSON.stringify(spec) + sel}
            className="flex items-center gap-1.5 text-muted-foreground whitespace-nowrap"
            style={{ fontSize: 11.5, animation: "tieflash .45s ease-out" }}>
            <Check size={11} strokeWidth={3} /> Ties to source
          </div>
          )}
          {ws === "book" && activeDatabookVersionId && (
            <button
              onClick={handleBuilderDatabookDownload}
              disabled={databookDownloading}
              className="flex items-center gap-1.5 rounded-lg bg-foreground text-background font-semibold px-3 py-1.5 hover:opacity-90 shadow-sm"
              style={{ fontSize: 11.5 }}
              title="Export the databook"
            >
              <Download size={12} />
              {databookDownloading ? "Downloading…" : "Download databook"}
            </button>
          )}
        </div>
      </div>

      </div>

      {ws === "upload" && (
        <UploadStage
          projectId={projectId}
          canEdit={canEdit}
          permissionsLoading={permissionsLoading}
          documents={uploadDocuments}
          setDocuments={setUploadDocuments}
          isLoading={uploadDocumentsLoading}
          loadError={uploadDocumentsLoadError}
          reviewEnrolled={!!prebuildReview && prebuildReview.state !== "not_started"}
          reviewStarting={
            reviewBusy
            || prebuildReview?.state === "running"
          }
          reviewFailed={prebuildReview?.state === "failed"}
          reviewReady={[
            "questions_required",
            "ready",
          ].includes(prebuildReview?.state)}
          retryAnalysis={beginPrebuildReview}
          financialYearEnd={builderSettings.financialYearEnd}
          onFinancialYearEndChange={(value) =>
            changeBuilderSettings({ financialYearEnd: value })}
          scopeFrom={builderSettings.scopeFrom}
          onScopeFromChange={(value) =>
            changeBuilderSettings({ scopeFrom: value })}
          scopeTo={builderSettings.scopeTo}
          onScopeToChange={(value) =>
            changeBuilderSettings({ scopeTo: value })}
          includeYtd={builderSettings.includeYtd}
          onIncludeYtdChange={(value) =>
            changeBuilderSettings({ includeYtd: value })}
          includeForecast={builderSettings.includeForecast}
          onIncludeForecastChange={(value) =>
            changeBuilderSettings({ includeForecast: value })}
          forecastYears={builderSettings.forecastYears}
          onForecastYearsChange={(value) =>
            changeBuilderSettings({ forecastYears: value })}
          analysisSettingsLoading={
            builderSettingsLoading || builderSettingsSaving
          }
          goNext={
            prebuildReview?.state === "failed"
              ? retryReview
              : ["questions_required", "ready"].includes(prebuildReview?.state)
                ? () => {
                    if (coverageState.status !== "loaded") return;
                    selectStage(postAnalysisBuilderStage(
                      coverageState.coverage.recommend_not_build,
                    ));
                  }
              : beginPrebuildReview
          }
          dealContext={dealContext}
          setDealContext={setDealContext}
          dealContextLoading={dealContextLoading}
          onDealContextBlur={saveDealContext}
        />
      )}

      {ws === "coverage" && (
        <SourceCoverageStage
          coverage={
            coverageState.status === "loaded"
              ? coverageState.coverage
              : null
          }
          loading={coverageState.status === "loading"}
          error={coverageState.status === "failed"}
          onRetry={() => setCoverageReload((value) => value + 1)}
          onCreateRfi={() => navigate(
            "/revised-rfi" + (projectId ? `?projectId=${encodeURIComponent(projectId)}` : ""),
          )}
          onUploadRfi={() => navigate(
            "/rfi-tracker?mode=upload" + (projectId ? `&projectId=${encodeURIComponent(projectId)}` : ""),
          )}
          onBack={() => selectStage("upload")}
          onProceed={() => selectStage("ents")}
          proceedLabel="Continue →"
        />
      )}

      {ws === "questions" && (
        <QuestionsStage
          questions={reviewQuestions}
          answers={qAns}
          attachments={questionAttachments}
          uploadingQuestionIds={Object.keys(questionUploads)}
          processing={
            reviewBusy
            || (
              prebuildReview?.state === "running"
              && !!prebuildReview?.batch_id
            )
          }
          failed={
            prebuildReview?.state === "failed"
            && !!prebuildReview?.batch_id
          }
          canEdit={
            canEdit
            && (
              (
                prebuildReview?.state === "questions_required"
                && !questionAnswersSubmitted
              )
              || (
                prebuildReview?.state === "ready"
                && questionAnswersSubmitted
              )
            )
          }
          canRetry={canEdit}
          submitted={questionAnswersSubmitted}
          answersChanged={questionAnswersChanged}
          onAnswer={(questionId, answer) =>
            answerQ(questionId, answer)}
          onAttach={attachQuestionFiles}
          onDetach={(questionId, documentId) =>
            setQAttachmentIds((current) => ({
              ...current,
              [questionId]: (current[questionId] || []).filter(
                (id) => id !== documentId,
              ),
            }))}
          onDownloadAttachment={(attachment) =>
            downloadDocById(attachment.documentId, attachment.filename)}
          onContinue={
            prebuildReview?.state === "ready" && !questionAnswersChanged
              ? () => selectStage("basis")
              : submitQuestionAnswers
          }
          continueLabel={
            prebuildReview?.state === "ready"
              ? "Continue to basis of preparation →"
              : "Continue →"
          }
          onRetry={retryReview}
          onBack={() => selectStage("ents")}
          onDownloadSource={async (source) => {
            if (!projectId) return;
            try {
              await downloadPrebuildQuestionSource(projectId, source);
            } catch {
              toast({
                title: "Download failed",
                description: `${source.filename} could not be downloaded.`,
                variant: "destructive",
              });
            }
          }}
        />
      )}

      {ws === "ents" && (
        <LegalEntitiesStage
          key={
            legalEntitiesState.status === "loaded"
              ? legalEntitiesState.resource.revision_id
              : legalEntitiesState.status
          }
          state={legalEntitiesState}
          analysisInterval={{
            financialYearEndMonth: financialYearEndMonth(
              builderSettings.financialYearEnd,
            ),
            startFiscalYear: periodFiscalYear(builderSettings.scopeFrom),
            endFiscalYear: analysisPeriodEndFiscalYear(
              periodFiscalYear(builderSettings.scopeTo),
              builderSettings.includeYtd,
            ),
          }}
          onRetry={() => setEntityCatalogReload((current) => current + 1)}
          onDirtyChange={setLegalStructureDirty}
          onDownloadSource={(source) =>
            downloadDocById(source.document_id, source.filename)}
          onSave={async (payload) => {
            if (!projectId) throw new Error("Project is unavailable.");
            const resource = await updatePrebuildReviewLegalStructure(
              projectId,
              payload,
            );
            setLegalEntitiesState({ status: "loaded", resource });
            setBasisOptionsReload((current) => current + 1);
            return resource;
          }}
          onSaveWithAttachments={async (payload) => {
            if (!projectId) throw new Error("Project is unavailable.");
            return savePrebuildReviewLegalEntityAttachments(projectId, payload);
          }}
          onGetAttachmentStatus={async (jobId) => {
            if (!projectId) throw new Error("Project is unavailable.");
            return getPrebuildReviewLegalEntityAttachmentStatus(projectId, jobId);
          }}
          onUploadAttachments={async (files) => {
            if (!projectId) throw new Error("Project is unavailable.");
            const result = await uploadProjectDocuments(projectId, files);
            if (result.skipped.length > 0 || result.failed.length > 0) {
              toast({
                title: "Files processed with issues",
                description: documentBatchUploadFeedback(result),
                variant: result.documents.length === 0 && result.failed.length > 0
                  ? "destructive"
                  : "default",
              });
            }
            return result.documents.map((document) => ({
              documentId: document.id,
              filename: document.original_filename,
            }));
          }}
          onDownloadAttachment={(documentId, filename) =>
            void downloadDocById(documentId, filename)}
          onContinue={() => selectStage("basis")}
        />
      )}

      {ws === "basis" && (
        <BasisOfPreparationStage
          state={basisOptionsState}
          selectedOptionId={basisSelection.optionId}
          onSelectionChange={(optionId) =>
            setBasisSelection((current) => ({ ...current, optionId }))
          }
          onRetry={() => void retryBasisOptions()}
          onDownloadComponent={(component) =>
            void downloadBasisComponent(component)}
          onDownloadSource={(source) =>
            downloadDocById(source.document_id, source.filename)}
          onBack={() => selectStage("ents")}
          onNext={
            databookGenerationState.status === "not_started"
            || databookGenerationState.status === "start_failed"
            || databookGenerationState.status === "failed"
              ? () => {
                  selectStage("book");
                  void startBuilderDatabook();
                }
              : () => selectStage("book")
          }
          nextStageConnected
          nextDisabled={
            agentRequestActive
            || (
              !canEdit
              && (
                databookGenerationState.status === "not_started"
                || databookGenerationState.status === "start_failed"
                || databookGenerationState.status === "failed"
              )
            )
          }
          nextLabel={
            databookGenerationState.status === "ready"
              ? "View databook →"
              : [
                  "checking",
                  "reconnecting",
                  "finalizing",
                  "queued",
                  "running",
                ].includes(databookGenerationState.status)
                ? "View progress →"
                : (
                    databookGenerationState.status === "failed"
                    || databookGenerationState.status === "start_failed"
                  )
                  ? "Retry databook →"
                  : "Start databook →"
          }
        />
      )}

      {ws === "book" && databookVersionsQuery.isError && (
        <div role="alert" className="border-b border-border px-5 py-2 text-sm">
          Saved versions could not be loaded. <button type="button" className="underline" onClick={() => void databookVersionsQuery.refetch()}>Retry</button>
        </div>
      )}
      {(ws === "book" || ws === "qna") && historicalDatabookVersion && (
        <div className="border-b border-border bg-muted/30 px-5 py-2 text-xs font-semibold">
          Viewing version {historicalDatabookVersion.version_number}
        </div>
      )}
      {ws === "report" && <BuilderReportStage />}

      {ws === "qna" && (
        <DatabookQnaStage
          key={`${projectId}-${activeDatabookVersionId ?? "pending"}`}
          projectId={projectId}
          versionId={activeDatabookVersionId}
          sheetNames={databookSheetsQuery.data?.sheets}
          canEdit={canEdit}
          onBack={() => selectStage("book")}
          onOpenSheet={(sheet) => {
            setSelectedDatabookSheetRowOffset(0);
            setExpandedDatabookOutlineGroups([]);
            setExpandedDatabookColumnOutlineGroups([]);
            setSelectedDatabookSheetChoice(sheet);
            setDatabookCellSelection(null);
            selectStage("book");
          }}
        />
      )}
      {ws === "book" && (
        <div className="flex min-h-0 flex-1 flex-col overflow-auto lg:flex-row lg:overflow-hidden">
        <div className="flex min-h-96 min-w-0 flex-1 flex-col lg:min-h-0">
        <DatabookGenerationStage
          projectId={projectId}
          previewRevision={databookPreviewRevision}
          key={`${projectId}-${activeDatabookVersionId ?? "pending"}`}
          versionId={activeDatabookVersionId}
          onRetryWorkbook={retryDatabookVersion}
          state={databookGenerationState}
          versionOutcome={versionOutcomeQuery.data}
          sheetNames={databookSheetsQuery.data?.sheets ?? null}
          sheetItems={databookSheetsQuery.data?.items ?? null}
          alertSheetNames={databookSheetsQuery.data?.alert_sheets ?? []}
          sheetListError={databookSheetsQuery.isError}
          selectedSheetName={selectedDatabookSheetName}
          sheetPreview={databookSheetQuery.data ?? null}
          sheetPreviewLoading={databookSheetQuery.isLoading || databookPreviewRefreshing}
          sheetPreviewFetching={databookSheetQuery.isFetching}
          sheetPreviewError={databookSheetQuery.isError}
          selection={databookCellSelection}
          canEdit={canEditDisplayedDatabook}
          canRetry={canEdit}
          onRetry={async () => {
            const outcome = databookGenerationState.status === "failed" ? databookGenerationState.outcome : (activeDatabookVersionId ? versionOutcomeQuery.data : databookGenerationState.outcome);
            if (!outcome?.job_id || !outcome.retry.allowed) return;
            try { await retryBuilderDatabook(outcome.job_id); }
            catch { toast.error("This retry is unavailable. Refresh the page to check its status."); }
          }}
          onBack={() => selectStage("basis")}
          onSelectSheet={(sheetName) => {
            setSelectedDatabookSheetRowOffset(0);
            setExpandedDatabookOutlineGroups([]);
            setExpandedDatabookColumnOutlineGroups([]);
            setSelectedDatabookSheetChoice(sheetName);
            setDatabookCellSelection(null);
          }}
          onSelectSheetPage={(rowOffset) => {
            setSelectedDatabookSheetChoice(selectedDatabookSheetName);
            setSelectedDatabookSheetRowOffset(rowOffset);
            setDatabookCellSelection(null);
          }}
          onToggleOutlineGroup={(groupId) => {
            setExpandedDatabookOutlineGroups((expandedGroupIds) =>
              toggleDatabookOutlineGroup(
                expandedGroupIds,
                groupId,
                databookSheetQuery.data?.outline_groups ?? [],
              )
            );
            setDatabookCellSelection(null);
          }}
          onToggleColumnOutlineGroup={(groupId) => {
            setExpandedDatabookColumnOutlineGroups((expandedGroupIds) =>
              toggleDatabookOutlineGroup(
                expandedGroupIds,
                groupId,
                databookSheetQuery.data?.column_outline_groups ?? [],
              )
            );
            setDatabookCellSelection(null);
          }}
          onRetrySheet={() => void databookSheetQuery.refetch()}
          onDeletionApplied={refreshDatabookAfterVersionChange}
          onSelectionChange={
            databookSheetQuery.isFetching
              ? () => undefined
              : setDatabookCellSelection
          }
        />
        </div>
        {showConstruction && construction.query.isError && <aside role="alert" className="w-full border-l p-4 text-sm lg:w-96">
          Construction chat could not be loaded. <button className="underline" onClick={() => void construction.query.refetch()}>Retry</button>
        </aside>}
        {showConstruction && construction.query.data && <AgentDatabookChat
          state={construction.query.data}
          canEdit={canEdit}
          sending={construction.mutation.isPending}
          error={construction.mutation.error}
          baseVersionLabel={constructionBaseVersion ? `Version ${constructionBaseVersion.version_number}` : undefined}
          onSend={construction.mutation.mutateAsync}
        />}
        </div>
      )}

      {!(ws === "book" && showConstruction) && LOCAL_STAGE_CHATS[ws] && (
        <BuilderStageChat
          key={`${ws}-${askDillyAvailable ? "ready" : "disabled"}`}
          title={LOCAL_STAGE_CHATS[ws].title}
          intro={
            askDillyAvailable
              ? LOCAL_STAGE_CHATS[ws].intro
              : "Source analysis must complete before Ask Dilli is available."
          }
          placeholder={LOCAL_STAGE_CHATS[ws].placeholder}
          disabled={
            !askDillyAvailable
            || agentRequestActive
            || (
              ws === "book"
              && (
                !canEditDisplayedDatabook
                || !selectedDatabookSheetName
              )
            )
          }
          onSend={askDilly}
          pendingLabel="Thinking…"
          errorMessage="I couldn't process that question. Please try again."
          messages={builderChatMessages(askDillyHistoryQuery.data)}
          historyLoading={askDillyHistoryQuery.isLoading}
          historyError={
            askDillyHistoryQuery.isError
              ? "The Ask Dilli conversation could not be loaded."
              : undefined
          }
          requestPending={
            currentAgentRequest?.channel === "ask_dilly"
            && agentRequestActive
          }
          requestError={askDillyRequestError ?? undefined}
          contextLabel={
            ws === "book" ? selectedDatabookContextLabel : undefined
          }
          onClearContext={
            ws === "book" && selectedDatabookContextLabel
              ? () => setDatabookCellSelection(null)
              : undefined
          }
        />
      )}

      <div className={"flex-1 flex-row min-h-0 " + (ws === "prototype-book" ? "flex" : "hidden")}>
        {/* LHS reports */}
        {railOpen && (
          <div className="w-56 min-w-56 border-r border-border/60 bg-card p-3 space-y-2 overflow-y-auto shrink-0">
            <button onClick={() => setMode(mode === "agent" ? "det" : "agent")}
              className={"w-full flex items-center gap-2 rounded-lg border px-2.5 py-2 font-medium " + (mode === "agent" ? "bg-foreground text-background border-foreground" : "border-border text-foreground/80 hover:bg-muted/50")}
              style={{ fontSize: 12 }}>
              <Sparkles size={12} /> New analysis
            </button>
            <button onClick={() => setLibOpen(true)}
              className="w-full flex items-center gap-2 rounded-lg border border-border px-2.5 py-2 font-medium text-foreground/80 hover:bg-muted/50"
              style={{ fontSize: 12 }}>
              <Plus size={12} /> Add reports
            </button>
            <button onClick={() => setTplOpen({})}
              className="w-full flex items-center justify-center gap-1.5 rounded-lg border border-border px-2 py-1.5 text-muted-foreground hover:border-foreground" style={{ fontSize: 11 }}>
              <LayoutTemplate size={12} /> New from template
            </button>
            <div className="text-muted-foreground/70 px-1" style={{ fontSize: 9.5, lineHeight: 1.5 }}>Built automatically from the files in use. Adjust any tab with Customise.</div>
            <div className="flex items-center gap-1 bg-muted rounded-lg px-2.5 py-1.5">
              <Search size={11} className="text-muted-foreground/70" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find" className="w-full outline-none bg-transparent" style={{ fontSize: 11.5 }} />
            </div>
            {pendingPass && (
              <div className="rounded-lg border border-foreground/25 bg-foreground/[0.04] p-2.5 space-y-1.5">
                <div className="flex items-center gap-1.5 font-semibold" style={{ fontSize: 11 }}>
                  <Sparkles size={11} /> {pendingPass.files.length} new files landed
                </div>
                <div className="text-muted-foreground" style={{ fontSize: 10, lineHeight: 1.5 }}>
                  {Object.keys(pendingPass.impacts).length} reports will update, {pendingPass.adds.length} new will be added. Everything else stays exactly as it is.
                </div>
                <button onClick={applyPass} className="w-full rounded-md bg-foreground text-background font-medium py-1 hover:opacity-90" style={{ fontSize: 10.5 }}>
                  Recalculate now
                </button>
              </div>
            )}
            {rail.map(([section, ids]) => {
              const items = ids.filter((id) => reports[id].name.toLowerCase().includes(q.toLowerCase()));
              const ghosts = pendingPass ? pendingPass.adds.filter((a) => a.section === section && a.name.toLowerCase().includes(q.toLowerCase())) : [];
              if (!items.length && !ghosts.length) return null;
              return (
                <div key={section}>
                  <div className="text-muted-foreground/70 uppercase tracking-wider mt-2 mb-1" style={{ fontSize: 9 }}>{section}</div>
                  {items.map((id) => (
                    <div key={id}
                      onClick={() => { setSel(id); setSelRows([]); (setSelCols([]), setColBlock(null)); setCtx(null); setMode("det"); }}
                      onContextMenu={(e) => { e.preventDefault(); setRepMenu({ x: Math.min(e.clientX, window.innerWidth - 280), y: Math.min(e.clientY, window.innerHeight - 210), id }); }}
                      className={"group w-full text-left rounded-lg px-2 py-1.5 flex items-center gap-1.5 cursor-pointer " +
                        (sel === id ? "bg-muted font-medium text-foreground" : "text-muted-foreground hover:bg-muted/50")}
                      style={{ fontSize: 12 }}>
                      <span className="flex-1 truncate">{reports[id].name}</span>
                      {pendingPass && pendingPass.impacts[id] && (
                        <span title={pendingPass.impacts[id]}
                          className="rounded-sm border border-amber-500/40 bg-amber-500/10 text-amber-800 px-1 font-semibold shrink-0"
                          style={{ fontSize: 8.5, lineHeight: "13px" }}>Will update</span>
                      )}
                      <button onClick={(e) => { e.stopPropagation(); duplicateReport(id); }}
                        title="Duplicate this report"
                        className="shrink-0 p-0.5 rounded text-muted-foreground/60 hover:text-foreground hover:bg-background/60 opacity-0 group-hover:opacity-100">
                        <Copy size={11} />
                      </button>
                      <Pill st={statusOf(id)} />
                    </div>
                  ))}
                  {ghosts.map((a) => (
                    <div key={a.id} title={"Will be built from " + a.from + " when you recalculate"}
                      className="w-full rounded-lg px-2 py-1.5 flex items-center gap-1.5 border border-dashed border-border text-muted-foreground/80"
                      style={{ fontSize: 12 }}>
                      <span className="flex-1 truncate italic">{a.name}</span>
                      <span className="rounded-sm bg-foreground text-background px-1 font-semibold shrink-0" style={{ fontSize: 8.5, lineHeight: "13px" }}>New</span>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}

        {/* centre */}
        <div className="flex-1 min-w-0 p-6 overflow-auto bg-muted/50">
          {mode === "agent" ? (
            <div className="bg-card border border-border rounded-xl shadow-sm p-6" style={{ maxWidth: 660 }}>
              <AgentPane rail={rail} onBuild={handleAgentBuild} />
            </div>
          ) : (
          <>
          <div className="bg-card border border-border rounded-xl shadow-sm p-6" style={{ minWidth: "fit-content" }}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold tracking-tight" style={{ fontSize: 19 }}>{rep.name}</span>
            <button onClick={(e) => setRepMenu({ x: Math.min(e.clientX, window.innerWidth - 280), y: Math.min(e.clientY, window.innerHeight - 210), id: sel })}
              title="Templates and attached files, also on right-click of any report in the rail"
              className="rounded-lg border border-border px-2 py-0.5 text-muted-foreground hover:border-foreground flex items-center gap-1" style={{ fontSize: 10 }}>
              <LayoutTemplate size={10} /> Template
            </button>
            <Pill st={statusOf(sel)} />
            {pendingPass && pendingPass.impacts[sel] && (
              <span className="rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-800 px-2 py-0.5" style={{ fontSize: 10.5 }}>
                Will update with new files — {pendingPass.impacts[sel]}
              </span>
            )}
            {rep.filter && (
              <span className="border border-border text-muted-foreground px-1.5" style={{ fontSize: 10 }}>
                Filtered: {dimLabel(ds, rep.filter[0])} = {rep.filter[1]}
              </span>
            )}
            <span className="flex-1" />
            <button onClick={() => { const v = !customize; setCustomize(v); setFieldsOpen(v); if (!v) setSelRows([]); }}
              className={"rounded-lg border px-2.5 py-1.5 " + (customize ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground hover:bg-muted/50")}
              style={{ fontSize: 11.5 }}>
              {customize ? "Done" : "Customise"}
            </button>
          </div>

          <SourcesStrip ds={ds} spec={spec} patch={patch} onPreview={(sid) => setSrcModal(sid)} />

          {customize && (
          <div className="flex items-center gap-1.5 mb-2.5">
            <button onClick={() => setGalleryOpen(true)}
              className="flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-foreground/80 hover:bg-muted/50"
              style={{ fontSize: 11.5 }}>
              <LayoutTemplate size={11} /> Templates
            </button>
            <span className="text-muted-foreground/70 px-1" style={{ fontSize: 11.5 }}>{tplName}</span>
            {spec.colField && (
              <Seg value={spec.colMode} onChange={(v) => patch({ colMode: v })}
                opts={[["single", "Single period"], ["bands", "Side by side"]]} />
            )}
            {spec.colField && spec.colMode === "single" && (
              <select value={spec.colPeriod} onChange={(e) => patch({ colPeriod: Number(e.target.value) })}
                className="border border-border px-2 py-1 bg-card" style={{ fontSize: 11.5 }}>
                {P.map((p, i) => <option key={p} value={i}>{p}</option>)}
              </select>
            )}
            <span className="flex-1" />
            <button onClick={applySimilar} title="Copy this layout to every report built from the same data, so you do not fix each tab by hand"
              className="rounded-lg border border-border px-2.5 py-1.5 text-muted-foreground hover:bg-muted/50" style={{ fontSize: 11 }}>
              Apply layout to similar reports
            </button>
          </div>
          )}

          {selCols.length > 0 && (
            <div className="flex items-center gap-2 mb-2 bg-muted rounded-lg px-2.5 py-1.5 flex-wrap" style={{ fontSize: 11 }}>
              <span className="font-semibold">{selCols.length} column{selCols.length === 1 ? "" : "s"} selected, {[...selCols].sort((a, b) => a - b).map((p) => P[p]).join(", ")}</span>
              <button onClick={() => setAttachFor({ id: sel, colPis: [...selCols] })}
                className="border border-foreground bg-foreground text-background px-2 py-1 hover:bg-card hover:text-foreground">Fill from a file</button>
              <button disabled={effP.length - selCols.length < 1} onClick={() => { patch({ periodsSel: effP.filter((p) => !selCols.includes(p)) }); (setSelCols([]), setColBlock(null)); }}
                className={"border px-2 py-1 " + (effP.length - selCols.length < 1 ? "border-border text-muted-foreground/50" : "border-border hover:border-foreground")}>Delete</button>
              <button onClick={() => { patch({ periodsSel: [...selCols].sort((a, b) => a - b) }); (setSelCols([]), setColBlock(null)); }}
                className="border border-border px-2 py-1 hover:border-foreground">Keep only these</button>
              <button onClick={() => (setSelCols([]), setColBlock(null))} className="border border-border px-2 py-1 hover:border-foreground">Clear</button>
              <span className="text-muted-foreground/70" style={{ fontSize: 10 }}>Shift extends the range, Cmd adds, Esc clears</span>
            </div>
          )}

          {selectable && selRows.length > 0 && (
            <div className="flex items-center gap-2 bg-muted rounded-lg px-2.5 py-1.5 mb-2 flex-wrap" style={{ fontSize: 11.5 }}>
              <span className="font-semibold">{selRows.length} rows selected</span>
              <input value={groupName} onChange={(e) => setGroupName(e.target.value)}
                className="border border-border px-2 py-1 bg-card outline-none focus:border-foreground" style={{ fontSize: 11.5, width: 140 }} />
              <button onClick={createGroup} className="border border-foreground bg-foreground text-background px-2 py-1 hover:bg-card hover:text-foreground">
                Group into subtotal
              </button>
              <button onClick={() => setAttachFor({ id: sel, rows: [...selRows] })} className="border border-border px-2 py-1 hover:border-foreground">
                Fill from a file
              </button>
              <button onClick={() => setSelRows([])} className="border border-border px-2 py-1 hover:border-foreground">Clear</button>
              <span className="text-muted-foreground/70" style={{ fontSize: 10 }}>Drag across rows to select, Shift extends, Cmd adds, drag a selected row to move the group, Esc clears</span>
            </div>
          )}

          {spec.tpl === "pvm"
            ? <PVMTable spec={spec} />
            : spec.split
            ? <SplitTables ds={ds} rep={repEff} spec={spec} scoped={scoped} measures={shownMeasures} source={SOURCE}
                innerRows={innerRows} onCycle={customize ? cycleRow : null} onOpen={toggleOpen} fullScope={fullScope} mask={mask}
                onRecon={(pi) => setReconOpen(pi)} recons={spec.recons || {}}
                renames={spec.renames || {}} catRenames={spec.catRenames || {}} onRename={renameRow} onPeriodMenu={(m) => setPerMenu(m)}
                selCols={selCols} onColClick={colClick} colBlock={colBlock} />
            : spec.colField && spec.colMode === "bands"
            ? <BandsTable ds={ds} rep={repEff} rows={rows} spec={spec} source={SOURCE} axis={spec.colField} scoped={scoped} mask={mask} onPeriodMenu={(m) => setPerMenu(m)} selCols={selCols} onColClick={colClick} colBlock={colBlock} />
            : spec.colField
            ? <AxisColsTable ds={ds} rows={rows} spec={spec} source={SOURCE} axis={spec.colField} scoped={scoped} />
            : <TreeTable ds={ds} rows={rows} rep={repEff} measures={shownMeasures} spec={spec} source={SOURCE}
                onCycle={customize ? cycleRow : null} onOpen={toggleOpen} fullScope={fullScope} showChecks totalLabel="Total" mask={mask}
                selRows={selRows} onSelRow={selectable ? (l) => setSelRows([l]) : null} onCtx={customize ? (c) => setCtx(c) : null}
                onSweepStart={selectable ? sweepStart : null} onSweepMove={selectable ? sweepMove : null}
                onRecon={(pi) => setReconOpen(pi)} recons={spec.recons || {}}
                renames={spec.renames || {}} catRenames={spec.catRenames || {}} onRename={customize ? renameRow : null}
                onRowMove={customize ? moveRow : null} onPeriodMenu={(m) => setPerMenu(m)}
                selCols={selCols} onColClick={colClick} colBlock={colBlock} />}

          <div className="mt-2.5 text-muted-foreground/70" style={{ fontSize: 10.5 }}>
            Chevrons collapse to Excel outline groups on export. Removing a source empties its periods and every total recomputes.
          </div>
          </div>
          </>
          )}
        </div>

        {/* RHS pivot fields */}
        {mode === "det" && fieldsOpen && (
          <div className="w-64 min-w-64 border-l border-border/60 bg-card p-3 flex flex-col gap-3 overflow-y-auto shrink-0">
            <button onClick={() => setGalleryOpen(true)}
              className="flex items-center justify-center gap-2 rounded-lg border border-border py-2 text-foreground/80 hover:bg-muted/50 font-medium"
              style={{ fontSize: 12 }}>
              <LayoutTemplate size={12} /> Start from a ready-made layout
            </button>

            {fieldsSimple ? (() => {
              // Plain-question builder. Same spec underneath — no pivot
              // vocabulary, no dragging. Rows always keep the item leaf.
              const rowDims = spec.rows.filter((r) => r.key !== "sku").map((r) => r.key);
              const pickable = dimFields.filter((k) => k !== "sku");
              const setRowDims = (first, second) => {
                const rows = [];
                if (first) rows.push({ key: first, vis: "sub" });
                if (second && second !== first) rows.push({ key: second, vis: "sub" });
                rows.push({ key: "sku" });
                patch({ rows, tpl: "custom", overrides: {}, open: {} });
              };
              const setAcross = (k) =>
                k ? patch({ colField: k, colMode: "bands", split: null, tpl: "custom" })
                  : patch({ colField: null, tpl: "custom" });
              const qLabel = { fontSize: 10, fontWeight: 600, letterSpacing: "0.04em" };
              const sel = { fontSize: 11.5 };
              return (
                <>
                  <div className="space-y-1">
                    <div className="text-muted-foreground uppercase" style={qLabel}>1 · What do you want to see?</div>
                    <div className="border border-border rounded-lg overflow-hidden">
                      {ds.measures.map((m) => {
                        const on = spec.values.includes(m.k);
                        return (
                          <button key={m.k} onClick={() => toggleField(m.k)}
                            className={"w-full flex items-center gap-2 px-2 py-1.5 text-left border-b border-border/40 last:border-b-0 " + (on ? "bg-muted/60 font-medium" : "hover:bg-muted/40 text-muted-foreground")}
                            style={{ fontSize: 11.5 }}>
                            <span className={"inline-flex items-center justify-center rounded border shrink-0 " + (on ? "bg-foreground border-foreground text-background" : "border-border")} style={{ width: 13, height: 13 }}>
                              {on && <Check size={9} strokeWidth={3} />}
                            </span>
                            <span className="truncate">{m.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="space-y-1">
                    <div className="text-muted-foreground uppercase" style={qLabel}>2 · Break the rows down by</div>
                    <select value={rowDims[0] || ""} onChange={(e) => setRowDims(e.target.value || null, rowDims[1])}
                      className="w-full border border-border rounded-md px-1.5 py-1 bg-card" style={sel}>
                      <option value="">Just the items, no grouping</option>
                      {pickable.map((k) => <option key={k} value={k}>{dimLabel(ds, k)}</option>)}
                    </select>
                    {rowDims[0] && (
                      <select value={rowDims[1] || ""} onChange={(e) => setRowDims(rowDims[0], e.target.value || null)}
                        className="w-full border border-border rounded-md px-1.5 py-1 bg-card" style={sel}>
                        <option value="">No second level</option>
                        {pickable.filter((k) => k !== rowDims[0]).map((k) => <option key={k} value={k}>then {dimLabel(ds, k)}</option>)}
                      </select>
                    )}
                    <div className="text-muted-foreground/70" style={{ fontSize: 9.5 }}>Every group gets its own subtotal line.</div>
                  </div>

                  <div className="space-y-1">
                    <div className="text-muted-foreground uppercase" style={qLabel}>3 · Across the top</div>
                    <select value={spec.colField || ""} onChange={(e) => setAcross(e.target.value || null)}
                      className="w-full border border-border rounded-md px-1.5 py-1 bg-card" style={sel}>
                      <option value="">The periods, {P[0]} to {P[P.length - 1]}</option>
                      {pickable.map((k) => <option key={k} value={k}>{dimLabel(ds, k)}, side by side</option>)}
                    </select>
                  </div>

                  <div className="space-y-1.5 border-t border-border pt-2">
                    {skuUsed && ds.orderBy === "value" && (
                      <label className="flex items-center justify-between gap-2" style={{ fontSize: 11.5 }}>
                        <span className="text-muted-foreground">How many items</span>
                        <select value={spec.topN} onChange={(e) => patch({ topN: Number(e.target.value) })}
                          className="border border-border px-1 py-0.5 bg-card" style={{ fontSize: 11.5 }}>
                          <option value={10}>Top 10 + Other</option>
                          <option value={15}>Top 15 + Other</option>
                          <option value={20}>Top 20 + Other</option>
                          <option value={0}>All of them</option>
                        </select>
                      </label>
                    )}
                    <label className="flex items-center justify-between gap-2" style={{ fontSize: 11.5 }}>
                      <span className="text-muted-foreground">One table per</span>
                      <select value={spec.split || ""} onChange={(e) => setSplit(e.target.value)}
                        className="border border-border px-1 py-0.5 bg-card" style={{ fontSize: 11.5 }}>
                        <option value="">Keep as one table</option>
                        <option value="entity">Legal entity</option>
                        <option value="site">Site</option>
                        {Object.keys(ds.dims).map((k) => <option key={k} value={k}>{ds.dims[k].label}</option>)}
                      </select>
                    </label>
                  </div>

                  <button onClick={() => setFieldsSimple(false)}
                    className="flex items-center justify-center gap-2 rounded-lg border border-border py-2 text-foreground/80 hover:bg-muted/50 font-medium"
                    style={{ fontSize: 12 }}>
                    <GripVertical size={12} /> Pivot table constructor
                  </button>
                </>
              );
            })() : (
            <>
            <button onClick={() => setFieldsSimple(true)}
              className="text-muted-foreground hover:text-foreground text-left" style={{ fontSize: 10.5 }}>
              ← Back to the simple builder
            </button>

            <div className="flex items-center gap-1 bg-muted rounded-lg px-2.5 py-1.5">
              <Search size={11} className="text-muted-foreground/70" />
              <input value={fq} onChange={(e) => setFq(e.target.value)} placeholder="Search fields" className="w-full outline-none bg-transparent" style={{ fontSize: 11.5 }} />
            </div>

            <div className="bg-card border border-border rounded-lg overflow-y-auto" style={{ minHeight: 100, maxHeight: 210 }}>
              <div className="px-2 pt-1.5 pb-0.5 text-muted-foreground/70" style={{ fontSize: 9.5 }}>Group the report by</div>
              {dimFields.filter((k) => dimLabel(ds, k).toLowerCase().includes(fq.toLowerCase())).map((k) => {
                const where = inArea(k);
                const wl = where === "rows" ? "In Rows" : where === "columns" ? "In Columns" : where === "values" ? "In Values" : null;
                const ai = k !== "sku" && k !== "entity" && k !== "site" && ds.dims[k] && ds.dims[k].prov === "inferred";
                return (
                  <div key={k} draggable onDragStart={onDragStart(k)} onClick={() => toggleField(k)}
                    title={wl ? "Click to remove from the report" : "Click to add to the report, or drag it into an area below"}
                    className="group flex items-center gap-2 px-2 py-1.5 hover:bg-muted/50 cursor-pointer">
                    <span className="flex-1 truncate" style={{ fontSize: 12 }}>{dimLabel(ds, k)}</span>
                    {ai && <span className="text-muted-foreground/50" style={{ fontSize: 9 }} title="Read from item names by the classifier">AI</span>}
                    {wl
                      ? <span className="rounded-sm bg-muted text-muted-foreground px-1.5 py-0.5" style={{ fontSize: 9 }}>{wl}</span>
                      : <Plus size={11} className="text-muted-foreground/50 opacity-0 group-hover:opacity-100" />}
                  </div>
                );
              })}
              <div className="px-2 pt-1.5 pb-0.5 text-muted-foreground/70 border-t border-border/60" style={{ fontSize: 9.5 }}>Values to show</div>
              {ds.measures.filter((m) => m.label.toLowerCase().includes(fq.toLowerCase())).map((m) => {
                const on = spec.values.includes(m.k);
                return (
                  <div key={m.k} draggable onDragStart={onDragStart(m.k)} onClick={() => toggleField(m.k)}
                    title={on ? "Click to remove this measure" : "Click to show this measure"}
                    className="group flex items-center gap-2 px-2 py-1.5 hover:bg-muted/50 cursor-pointer">
                    <span className="flex-1 truncate" style={{ fontSize: 12 }}>{m.label}</span>
                    {on
                      ? <span className="rounded-sm bg-muted text-muted-foreground px-1.5 py-0.5" style={{ fontSize: 9 }}>Shown</span>
                      : <Plus size={11} className="text-muted-foreground/50 opacity-0 group-hover:opacity-100" />}
                  </div>
                );
              })}
            </div>

            <div className="text-muted-foreground/70" style={{ fontSize: 10 }}>Drag fields between areas below</div>

            <div className="grid grid-cols-2 gap-1.5">
              <AreaBox title="Columns" icon={<GripVertical size={10} />} hover={hoverArea === "columns"} {...areaProps("columns")}>
                {!spec.colField && spec.values.length > 1 && <div className="border border-dashed border-border text-muted-foreground px-1.5 py-1 mb-1" style={{ fontSize: 11 }}>Σ Values</div>}
                {spec.colField && (
                  <FieldChip label={dimLabel(ds, spec.colField)} sub={spec.colMode === "bands" ? "bands" : "single"} drag={onDragStart(spec.colField)}
                    onRemove={() => moveField(spec.colField, "off")}
                    menu={[
                      ["Single period columns", () => patch({ colMode: "single" }), spec.colMode === "single"],
                      ["Side by side bands", () => patch({ colMode: "bands" }), spec.colMode === "bands"],
                      ["Move to Rows", () => moveField(spec.colField, "rows")],
                    ]} />
                )}
              </AreaBox>

              <AreaBox title="Rows" icon={<GripVertical size={10} />} hover={hoverArea === "rows"} {...areaProps("rows")}>
                {spec.rows.map((r) => (
                  <div key={r.key} {...chipDropProps("rows", r.key)}>
                    <FieldChip label={dimLabel(ds, r.key)} sub={r.key === "sku" ? "leaf" : (r.vis || "sub")} drag={onDragStart(r.key)}
                      onRemove={() => moveField(r.key, "off")}
                      menu={r.key === "sku" ? [] : [
                        ["Subtotal line", () => setVis(r.key, "sub"), (r.vis || "sub") === "sub"],
                        ["Off, accounts flat", () => setVis(r.key, "hidden"), r.vis === "hidden"],
                        ["Advanced, hierarchy", () => setVis(r.key, "hier"), r.vis === "hier"],
                        ["Advanced, label heading", () => setVis(r.key, "label"), r.vis === "label"],
                        ["Move to Columns", () => moveField(r.key, "columns")],
                      ]} />
                  </div>
                ))}
              </AreaBox>

              <div className="col-span-2">
              <AreaBox title="Values" icon={<Sigma size={10} />} hover={hoverArea === "values"} {...areaProps("values")}>
                {spec.values.map((v) => (
                  <div key={v} {...chipDropProps("values", v)}>
                    <FieldChip label={ds.measures.find((m) => m.k === v).label} drag={onDragStart(v)}
                      onRemove={() => moveField(v, "off")} menu={[]} small />
                  </div>
                ))}
              </AreaBox>
              </div>
            </div>

            <SubtotalsPanel ds={ds} spec={spec} patch={patch} setVis={setVis} />

            <div className="space-y-1.5 border-t border-border pt-2">
              {skuUsed && ds.orderBy === "value" && (
                <label className="flex items-center justify-between gap-2" style={{ fontSize: 11.5 }}>
                  <span className="text-muted-foreground">Show top items</span>
                  <select value={spec.topN} onChange={(e) => patch({ topN: Number(e.target.value) })}
                    className="border border-border px-1 py-0.5 bg-card" style={{ fontSize: 11.5 }}>
                    <option value={10}>Top 10 + Other</option>
                    <option value={15}>Top 15 + Other</option>
                    <option value={20}>Top 20 + Other</option>
                    <option value={0}>All items</option>
                  </select>
                </label>
              )}
              <label className="flex items-center justify-between gap-2" style={{ fontSize: 11.5 }}>
                <span className="text-muted-foreground">Split into tables by</span>
                <select value={spec.split || ""} onChange={(e) => setSplit(e.target.value)}
                  className="border border-border px-1 py-0.5 bg-card" style={{ fontSize: 11.5 }}>
                  <option value="">None</option>
                  <option value="entity">Legal entity</option>
                  <option value="site">Site</option>
                  {Object.keys(ds.dims).map((k) => <option key={k} value={k}>{ds.dims[k].label}</option>)}
                </select>
              </label>
            </div>
            </>
            )}

          </div>
        )}
      </div>

      {/* right-click AI groupings */}
      {ctx && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setCtx(null)} onContextMenu={(e) => { e.preventDefault(); setCtx(null); }} />
          <div className="pop fixed z-30 bg-card border border-border" style={{ left: Math.min(ctx.x, (typeof window !== "undefined" ? window.innerWidth : 1100) - 320), top: ctx.y, width: 300 }}>
            <div className="px-3 pt-2.5 pb-1 font-semibold truncate" style={{ fontSize: 12 }}>{ctx.label}</div>
            <div className="px-3 pb-2 text-muted-foreground border-b border-border/60" style={{ fontSize: 10 }}>
              Regroup the whole report by an attribute the classifier read from this item. The percentage shows how much of {ds.measures.find((m) => m.k === ds.primary).label.toLowerCase()} has that attribute detected.
            </div>
            {["entity", "site", ...Object.keys(ds.dims)]
              .map((k) => ({ k, v: k === "entity" ? ctx.atom.entity : k === "site" ? ctx.atom.site : ctx.atom.attrs[k], cov: coverage(ds, k) }))
              .sort((a, b) => b.cov - a.cov)
              .map(({ k, v, cov }) => (
                <button key={k} onClick={() => groupByDim(k)}
                  className="w-full text-left px-3 py-1.5 hover:bg-muted/50 flex items-center gap-2 border-b border-border/60">
                  <span className="flex-1 min-w-0">
                    <span className="block font-medium" style={{ fontSize: 11 }}>Group report by {dimLabel(ds, k).toLowerCase()}</span>
                    <span className={"block truncate " + (v == null ? "text-muted-foreground/50 italic" : "text-muted-foreground")} style={{ fontSize: 9.5 }}>
                      {v == null ? "not detected on this item" : "this item: " + memberLabel(ds, k, v)}
                    </span>
                  </span>
                  <span className="text-muted-foreground/70 shrink-0" style={{ fontSize: 10 }}>{cov}%</span>
                </button>
              ))}
            <div className="flex gap-2 px-3 py-2">
              <button onClick={() => { toggleSelRow(ctx.label); setCtx(null); }}
                className="rounded-md border border-border px-2 py-1 text-foreground/80 hover:bg-muted/50" style={{ fontSize: 10 }}
                title="Selects this row. Select a few, then use the bar above the table to group them into a named subtotal.">
                Add to custom subtotal selection
              </button>
              <button onClick={() => setCtx(null)} className="rounded-md border border-border px-2 py-1 text-foreground/80 hover:bg-muted/50" style={{ fontSize: 10 }}>Close</button>
            </div>
          </div>
        </>
      )}

      {/* variance reconciliation */}
      {reconOpen != null && (
        <ReconModal ds={ds} pi={reconOpen} spec={spec} patch={patch} onClose={() => setReconOpen(null)} />
      )}

      {/* source preview and row selection */}
      {srcModal && (
        <SourceModal ds={ds} sid={srcModal} spec={spec} patch={patch} onClose={() => setSrcModal(null)} />
      )}

      {/* add reports library */}
      {libOpen && (
        <LibraryModal added={libAdded} onAdd={addLibrary} onClose={() => setLibOpen(false)} />
      )}
      {repMenu && (
        <div className="fixed inset-0 z-40" onClick={() => setRepMenu(null)} onContextMenu={(e) => { e.preventDefault(); setRepMenu(null); }}>
          <div className="pop absolute bg-card border border-border rounded-xl shadow-2xl p-1" style={{ left: repMenu.x, top: repMenu.y, width: 264 }} onClick={(e) => e.stopPropagation()}>
            <div className="px-2.5 py-1 text-muted-foreground/70 truncate" style={{ fontSize: 9 }}>{reports[repMenu.id].name}</div>
            {[
              ["Attach a file to fill it out", "its numbers and periods join this report", () => { setAttachFor({ id: repMenu.id, replace: false }); setRepMenu(null); }],
              ["Re-populate from a different file", "replaces the attached source, everything recomputes", () => { setAttachFor({ id: repMenu.id, replace: true }); setRepMenu(null); }],
              ["Change the table layout", "subtotals, hierarchy, bands, matrix and more", () => { setSel(repMenu.id); setCustomize(true); setGalleryOpen(true); setRepMenu(null); }],
              ["Price volume mix template", "turn this report into a PVM bridge, pick the columns", () => { setSel(repMenu.id); setTplOpen((specs[repMenu.id] || {}).tpl === "pvm" ? { editId: repMenu.id, ...specs[repMenu.id] } : { convertId: repMenu.id }); setRepMenu(null); }],
              ["Duplicate this report", "make a copy to build a variant from", () => { duplicateReport(repMenu.id); setRepMenu(null); }],
            ].map(([label, sub, act]) => (
              <button key={label} onClick={act} className="w-full text-left rounded-lg px-2.5 py-1.5 hover:bg-muted/50">
                <span className="block font-medium" style={{ fontSize: 11 }}>{label}</span>
                <span className="block text-muted-foreground" style={{ fontSize: 9.5 }}>{sub}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {attachFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6" style={{ background: "rgba(23,23,23,0.35)" }} onClick={() => setAttachFor(null)}>
          <div className="bg-card rounded-xl border border-border shadow-2xl p-4" style={{ width: 460 }} onClick={(e) => e.stopPropagation()}>
            <div className="font-semibold mb-0.5" style={{ fontSize: 13 }}>
              {attachFor.colPis ? "Fill the " + attachFor.colPis.map((p) => P[p]).join(", ") + (attachFor.colPis.length === 1 ? " column" : " columns") + " from a file"
                : attachFor.colPi != null ? "Fill the " + P[attachFor.colPi] + " column from a file"
                : attachFor.rows ? "Fill " + attachFor.rows.length + " selected rows from a file"
                : attachFor.replace ? "Re-populate from a different file" : "Attach a file to fill it out"}
            </div>
            <div className="text-muted-foreground mb-2.5" style={{ fontSize: 10.5 }}>
              {reports[attachFor.id].name}.{" "}
              {attachFor.colPis || attachFor.colPi != null ? "The file becomes the source for " + (attachFor.colPis && attachFor.colPis.length > 1 ? "these periods" : "this period") + ", any exclusions covering it heal, and the columns recompute."
                : attachFor.rows ? "The file feeds the selected lines and their totals recompute."
                : attachFor.replace ? "The chosen file replaces the current attachment and every number re-derives from it."
                : "The file's numbers and periods join the report, and everything recomputes."}
            </div>
            <div className="border border-border/60 rounded-lg overflow-y-auto mb-3" style={{ maxHeight: 240 }}>
              <button onClick={() => attachFile(attachFor.id, "uploaded_new_file.xlsx", attachFor)}
                className="w-full text-left flex items-center gap-1.5 rounded-lg px-2 py-1.5 hover:bg-muted/50 border-b border-border/60" style={{ fontSize: 11 }}>
                <Plus size={10} className="text-muted-foreground" />
                <span className="flex-1">Upload a new file</span>
                <span className="text-muted-foreground/70" style={{ fontSize: 9 }}>lands in Files, extracted, then fills this</span>
              </button>
              {ALLFILES.map((f) => (
                <button key={f.id} onClick={() => attachFile(attachFor.id, f.label, attachFor)}
                  className="w-full text-left flex items-center gap-1.5 rounded-lg px-2 py-1.5 hover:bg-muted/50" style={{ fontSize: 11 }}>
                  <FIcon label={f.label} size={10} />
                  <span className="truncate flex-1">{f.label}</span>
                  <span className="text-muted-foreground/70" style={{ fontSize: 9 }}>{(f.periods || []).map((pi) => P[pi]).join(", ")}</span>
                </button>
              ))}
            </div>
            <div className="flex justify-end">
              <button onClick={() => setAttachFor(null)} className="rounded-lg border border-border px-3 py-1.5 text-muted-foreground hover:border-foreground" style={{ fontSize: 11.5 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {tplOpen && (
        <TemplateModal init={tplOpen}
          onSave={(cfg) => { if (tplOpen.editId || tplOpen.convertId) { patch({ tpl: "pvm", ...cfg }); setTplOpen(null); } else addTemplate(cfg); }}
          onClose={() => setTplOpen(null)} />
      )}

      {/* period column menu */}
      {perMenu && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setPerMenu(null)} onContextMenu={(e) => { e.preventDefault(); setPerMenu(null); }} />
          <div className="pop fixed z-30 bg-card border border-border" style={{ left: Math.min(perMenu.x, (typeof window !== "undefined" ? window.innerWidth : 1100) - 230), top: perMenu.y, width: 208 }}>
            <div className="px-2.5 py-1.5 border-b border-border/60 font-semibold" style={{ fontSize: 11.5 }}>
              {selCols.length > 1 && selCols.includes(perMenu.pi) ? selCols.length + " columns, " + [...selCols].sort((a, b) => a - b).map((p) => P[p]).join(", ") : P[perMenu.pi] + " column"}
            </div>
            <button disabled={effP.length < 2} onClick={() => { dropPeriod(perMenu.pi); setPerMenu(null); }}
              className={"w-full text-left px-2.5 py-1.5 " + (effP.length < 2 ? "text-muted-foreground/50" : "hover:bg-muted")} style={{ fontSize: 11.5 }}>
              Delete column
            </button>
            <button disabled={effP.length < 2} onClick={() => { patch({ periodsSel: [perMenu.pi] }); setPerMenu(null); }}
              className={"w-full text-left px-2.5 py-1.5 " + (effP.length < 2 ? "text-muted-foreground/50" : "hover:bg-muted")} style={{ fontSize: 11.5 }}>
              Keep only this column
            </button>
            <button onClick={() => {
                const many = selCols.length > 1 && selCols.includes(perMenu.pi);
                setAttachFor({ id: sel, colPis: many ? [...selCols] : [perMenu.pi] });
                setPerMenu(null);
              }}
              className="w-full text-left px-2.5 py-1.5 hover:bg-muted" style={{ fontSize: 11.5 }}>
              {selCols.length > 1 && selCols.includes(perMenu.pi) ? "Fill these columns from a file" : "Fill this column from a file"}
              <span className="block text-muted-foreground/70" style={{ fontSize: 9 }}>attach a source, exclusions on the periods heal</span>
            </button>
            <div className="px-2.5 pt-1.5 pb-0.5 text-muted-foreground/70 uppercase tracking-wider border-t border-border/60" style={{ fontSize: 8.5 }}>Insert column, fill with</div>
            {P.map((_, i) => i).filter((i) => !effP.includes(i)).map((i) => (
              <button key={i} onClick={() => { insertPeriod(i); setPerMenu(null); }}
                className="w-full text-left px-2.5 py-1 hover:bg-muted" style={{ fontSize: 11.5 }}>{P[i]}</button>
            ))}
            {P.every((_, i) => effP.includes(i)) && (
              <div className="px-2.5 py-1.5 text-muted-foreground/50 italic" style={{ fontSize: 10.5 }}>All periods already shown</div>
            )}
          </div>
        </>
      )}

      {/* gallery with real previews */}
      {galleryOpen && (
        <div className="fixed inset-0 z-20 flex items-center justify-center" style={{ background: "rgba(23,23,23,0.35)" }}
          onClick={() => setGalleryOpen(false)}>
          <div className="bg-card border border-border rounded-xl shadow-2xl p-4 max-w-4xl w-full mx-4 overflow-y-auto" style={{ maxHeight: "90vh" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <span className="font-bold" style={{ fontSize: 15 }}>Table templates, previewing {rep.name}</span>
              <button onClick={() => setGalleryOpen(false)} className="text-muted-foreground hover:text-foreground"><X size={15} /></button>
            </div>
            {[["Core layouts", TPLS.filter((t) => CORE_TPL.includes(t.id))], ["More layouts", TPLS.filter((t) => !CORE_TPL.includes(t.id))]].map(([head, list]) => (
              <div key={head} className="mb-3">
                <div className="text-muted-foreground/70 uppercase tracking-wider mb-1.5" style={{ fontSize: 9.5 }}>{head}</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {list.map((t) => (
                    <button key={t.id} onClick={() => applyTemplate(t)}
                      className={"text-left border rounded-lg p-2 hover:border-foreground " + (spec.tpl === t.id ? "border-foreground bg-muted/50" : "border-border")}>
                      <MiniPreview ds={ds} rep={rep} atoms={scoped} t={t} />
                      <div className="font-semibold mt-1.5" style={{ fontSize: 12 }}>{t.name}</div>
                      <div className="text-muted-foreground" style={{ fontSize: 10.5 }}>{t.desc}</div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <div className="mt-3 text-muted-foreground/70" style={{ fontSize: 10.5 }}>
              Previews use this report's live data. Templates only change structure, every one re-sums the same atoms, so checks always tie.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================================================================
// Sources strip
// ==================================================================
function SourcesStrip({ ds, spec, patch, onPreview }) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const inc = ds.sources.filter((s) => spec.sources.includes(s.id));
  const excl = ds.sources.filter((s) => !spec.sources.includes(s.id));
  const pset = [...new Set(flat(inc.map((s) => s.periods)))].sort((a, b) => a - b);
  const cover = pset.length ? (pset.length > 1 ? P[pset[0]] + " to " + P[pset[pset.length - 1]] : P[pset[0]]) : "no periods";
  const nPartial = inc.filter((s) => spec.srcSel && spec.srcSel[s.id] && spec.srcSel[s.id].excluded && spec.srcSel[s.id].excluded.length > 0).length;
  const nQueued = inc.reduce((n, s) => n + ((spec.srcSel && spec.srcSel[s.id] && spec.srcSel[s.id].tables) ? spec.srcSel[s.id].tables.length : 0), 0);
  return (
    <div className="mt-0.5 mb-2.5 relative" style={{ fontSize: 10.5 }}>
      <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-1 text-muted-foreground hover:text-foreground">
        {expanded ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
        <FileSpreadsheet size={9} />
        <span>
          {inc.length} source {inc.length === 1 ? "file" : "files"}, {cover}
          {nPartial > 0 ? ", " + nPartial + " partial" : ""}
          {nQueued > 0 ? ", " + nQueued + " table" + (nQueued > 1 ? "s" : "") + " queued" : ""}
        </span>
      </button>
      {Object.entries(spec.colSrc || {}).map(([pi, lb]) => (
        <span key={"c" + pi} className="inline-flex items-center gap-1 rounded-lg border border-border bg-card px-1.5 py-0.5 ml-1.5" style={{ fontSize: 9.5 }}>
          <FileSpreadsheet size={9} className="text-muted-foreground" />{P[Number(pi)]} column from {lb}<span className="text-muted-foreground/70">, attached by you</span>
          <button onClick={() => { const n = { ...spec.colSrc }; delete n[pi]; patch({ colSrc: n }); }} className="text-muted-foreground/70 hover:text-foreground"><X size={9} /></button>
        </span>
      ))}
      {(spec.rowSrc || []).map((rs, i) => (
        <span key={"r" + i} className="inline-flex items-center gap-1 rounded-lg border border-border bg-card px-1.5 py-0.5 ml-1.5" style={{ fontSize: 9.5 }}>
          <FileSpreadsheet size={9} className="text-muted-foreground" />{rs.rows.length} rows from {rs.label}<span className="text-muted-foreground/70">, attached by you</span>
          <button onClick={() => patch({ rowSrc: (spec.rowSrc || []).filter((_, j) => j !== i) })} className="text-muted-foreground/70 hover:text-foreground"><X size={9} /></button>
        </span>
      ))}
      {(spec.extraSrc || []).map((lb) => (
        <span key={lb} className="inline-flex items-center gap-1 rounded-lg border border-border bg-card px-1.5 py-0.5 ml-1.5" style={{ fontSize: 9.5 }}>
          <FIcon label={lb} size={9} />{lb}<span className="text-muted-foreground/70">, attached by you</span>
          <button onClick={() => patch({ extraSrc: (spec.extraSrc || []).filter((x) => x !== lb) })} className="text-muted-foreground/70 hover:text-foreground"><X size={9} /></button>
        </span>
      ))}
      <span className="relative inline-block ml-1.5">
        <button onClick={() => setAttachOpen(!attachOpen)} className="text-muted-foreground/70 hover:text-foreground underline underline-offset-2" style={{ fontSize: 9.5 }}>
          attach a file
        </button>
        {attachOpen && (
          <span className="pop absolute left-0 top-5 z-30 bg-card border border-border rounded-xl shadow-xl p-1 block overflow-y-auto" style={{ width: 300, maxHeight: 180 }}>
            {ALLFILES.map((f) => (
              <button key={f.id} onClick={() => { patch({ extraSrc: [...(spec.extraSrc || []), f.label] }); setAttachOpen(false); }}
                className="w-full text-left flex items-center gap-1.5 rounded-lg px-2 py-1 hover:bg-muted/50" style={{ fontSize: 10.5 }}>
                <FIcon label={f.label} size={9} />
                <span className="truncate flex-1">{f.label}</span>
                <span className="text-muted-foreground/70" style={{ fontSize: 8.5 }}>{(f.periods || []).map((pi) => P[pi]).join(", ")}</span>
              </button>
            ))}
          </span>
        )}
      </span>
      {expanded && (
        <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
          {inc.map((s) => (
            <span key={s.id} className="flex items-center gap-1 bg-card border border-border rounded-md px-2 py-1" style={{ fontSize: 10.5 }} title={s.label}>
              <FileSpreadsheet size={9} className="text-muted-foreground" />
              <span className="truncate" style={{ maxWidth: 150 }}>{s.label}</span>
              <span className="text-muted-foreground/70" style={{ fontSize: 9.5 }}>{perRange(s.periods)}</span>
              {spec.srcSel && spec.srcSel[s.id] && spec.srcSel[s.id].excluded && spec.srcSel[s.id].excluded.length > 0 && (
                <span className="border border-border border-dashed rounded-sm text-muted-foreground px-0.5" style={{ fontSize: 8.5 }}>partial</span>
              )}
              {spec.srcSel && spec.srcSel[s.id] && spec.srcSel[s.id].tables && spec.srcSel[s.id].tables.length > 0 && (
                <span className="border border-foreground border-dashed rounded-sm text-foreground/80 px-0.5" style={{ fontSize: 8.5 }}>+{spec.srcSel[s.id].tables.length} queued</span>
              )}
              <button onClick={() => onPreview(s.id)} className="text-muted-foreground/70 hover:text-foreground" title="Preview the file and select the tables and rows to use">
                <Eye size={9} />
              </button>
              <button onClick={() => patch({ sources: spec.sources.filter((x) => x !== s.id) })}
                className="text-muted-foreground/70 hover:text-foreground" title="Remove source from this report"><X size={9} /></button>
            </span>
          ))}
          <button onClick={() => setOpen(!open)}
            className="flex items-center gap-1 border border-dashed border-border rounded-md px-2 py-1 hover:border-foreground" style={{ fontSize: 10.5 }}>
            <Plus size={9} /> Add source
          </button>
          {open && (
            <div className="pop absolute top-full left-0 z-10 mt-1 bg-card border border-border" style={{ minWidth: 260 }} onMouseLeave={() => setOpen(false)}>
              {excl.length === 0 && <div className="px-2 py-1 text-muted-foreground/70" style={{ fontSize: 10.5 }}>All extracted files are included</div>}
              {excl.map((s) => (
                <button key={s.id} onClick={() => { patch({ sources: [...spec.sources, s.id] }); setOpen(false); }}
                  className="w-full text-left px-2 py-1 hover:bg-muted flex items-center gap-1.5" style={{ fontSize: 10.5 }}>
                  <FileSpreadsheet size={9} className="text-muted-foreground" /> {s.label}
                  <span className="text-muted-foreground/70" style={{ fontSize: 9.5 }}>{perRange(s.periods)}</span>
                </button>
              ))}
              <div className="px-2 py-1 text-muted-foreground/50 border-t border-border" style={{ fontSize: 10 }}>Upload new file, in product only</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ==================================================================
// Mini preview for the gallery, real data
// ==================================================================
function MiniPreview({ ds, rep, atoms, t }) {
  const conf = t.mk(ds);
  const prim = ds.primary;
  const primLabel = ds.measures.find((m) => m.k === prim).label;
  const pis = rep.periods.slice(-3);
  const cs = { ...MONO, fontSize: 8.5, minWidth: 40 };
  const hs = { fontSize: 8.5 };
  const rowsFor = (subset, levels, topN) => {
    const out = [];
    buildRows(ds, subset, levels, 0, 0, "pv", { topN, overrides: {}, open: {}, groupsMap: {} }, out);
    return out;
  };
  const rowCls = (r) =>
    r.type === "hier" ? "bg-muted font-semibold" :
    r.type === "sub" ? "font-semibold" :
    r.type === "other" ? "italic text-muted-foreground" : "";
  const rowStyle = (r) => (r.type === "sub" ? { borderTop: "1px solid hsl(var(--foreground))" } : r.type === "hier" ? { borderTop: "1px solid hsl(var(--muted-foreground))" } : {});
  const Label = ({ r }) => (
    <td className={"px-1.5 truncate " + (r.type === "band" ? "uppercase text-muted-foreground/70" : "")}
      style={{ paddingLeft: 6 + r.depth * 8, fontSize: r.type === "band" ? 7 : 8.5, maxWidth: 130 }}>{r.label}</td>
  );
  const Head = ({ cols, label }) => (
    <thead>
      <tr>
        <th className="bg-foreground text-background" style={hs} />
        <th className="bg-foreground text-background font-semibold px-1.5 py-0.5 text-center" colSpan={cols.length} style={{ ...hs, borderLeft: "1.5px solid hsl(var(--background))" }}>{label || primLabel}</th>
      </tr>
      <tr>
        <th className="bg-foreground text-background text-left px-1.5 py-0.5 font-semibold" style={{ ...hs, minWidth: 110 }}>Line</th>
        {cols.map((c, i) => (
          <th key={i} className="bg-foreground text-background text-right px-1.5 py-0.5 font-semibold" style={{ ...hs, borderLeft: i === 0 ? "1.5px solid hsl(var(--background))" : undefined }}>{c}</th>
        ))}
      </tr>
    </thead>
  );
  const TotalRow = ({ vals, label }) => (
    <tr style={{ borderTop: "1.5px solid hsl(var(--foreground))" }}>
      <td className="px-1.5 font-bold" style={{ fontSize: 8.5, borderBottom: "2.5px double hsl(var(--foreground))" }}>{label || "Total"}</td>
      {vals.map((v, i) => (
        <td key={i} className="px-1.5 text-right tabular-nums font-bold" style={{ ...cs, borderBottom: "2.5px double hsl(var(--foreground))" }}>{fmtN(v)}</td>
      ))}
    </tr>
  );
  const box = "border border-border rounded-md overflow-hidden";
  const mvOf = (a, v) => String(getv(a, conf.colField) == null ? "__u__" : getv(a, conf.colField)) === v;

  if (conf.split) {
    const members = axisMembers(ds, conf.split, atoms, 4).slice(0, 2);
    return (
      <div className="space-y-1.5">
        {members.map((m) => {
          const subset = atoms.filter((a) => String(getv(a, conf.split) == null ? "__u__" : getv(a, conf.split)) === m);
          const rs = rowsFor(subset, conf.rows.filter((r) => r.key !== conf.split), 2).slice(0, 3);
          const tot = sumAgg(ds, subset);
          const nm = m === "__u__" ? "No value" : m;
          return (
            <div key={m}>
              <div className="font-semibold mb-0.5" style={{ fontSize: 8.5 }}>{nm}</div>
              <div className={box}>
                <table className="border-collapse w-full">
                  <Head cols={pis.map((pi) => P[pi])} />
                  <tbody>
                    {rs.map((r, i) => (
                      <tr key={i} className={"border-b border-border/60 " + rowCls(r)} style={rowStyle(r)}>
                        <Label r={r} />
                        {pis.map((pi) => <td key={pi} className="px-1.5 text-right tabular-nums" style={cs}>{r.type === "band" ? "" : fmtN(mval(ds, r.agg, prim, pi))}</td>)}
                      </tr>
                    ))}
                    <TotalRow vals={pis.map((pi) => mval(ds, tot, prim, pi))} label={nm + " total"} />
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    );
  }
  if (conf.colField && conf.colMode === "bands") {
    const members = axisMembers(ds, conf.colField, atoms, 4).slice(0, 2);
    const bp = rep.periods.slice(-2);
    const rs = rowsFor(atoms, conf.rows, 2).filter((r) => r.type !== "band").slice(0, 4);
    const cell = (ms, m, pi) => ms.filter((a) => mvOf(a, m)).reduce((s, a) => s + a.v[prim][pi], 0);
    const colTot = (m, pi) => atoms.filter((a) => mvOf(a, m)).reduce((s, a) => s + a.v[prim][pi], 0);
    return (
      <div className={box}>
        <table className="border-collapse w-full">
          <thead>
            <tr>
              <th className="bg-foreground text-background" />
              {members.map((m) => (
                <th key={m} colSpan={bp.length} className="bg-foreground text-background font-semibold px-1 py-0.5 text-center" style={{ ...hs, borderLeft: "1.5px solid hsl(var(--background))" }}>{m}</th>
              ))}
            </tr>
            <tr>
              <th className="bg-foreground text-background text-left px-1.5 py-0.5 font-semibold" style={{ ...hs, minWidth: 100 }}>Line</th>
              {members.map((m) => bp.map((pi, i) => (
                <th key={m + pi} className="bg-foreground text-background text-right px-1 py-0.5 font-semibold" style={{ ...hs, borderLeft: i === 0 ? "1.5px solid hsl(var(--background))" : undefined }}>{P[pi]}</th>
              )))}
            </tr>
          </thead>
          <tbody>
            {rs.map((r, i) => (
              <tr key={i} className={"border-b border-border/60 " + rowCls(r)} style={rowStyle(r)}>
                <Label r={r} />
                {members.map((m) => bp.map((pi) => (
                  <td key={m + pi} className="px-1 text-right tabular-nums" style={{ ...cs, minWidth: 34 }}>{fmtN(cell(r.ms, m, pi))}</td>
                )))}
              </tr>
            ))}
            <TotalRow vals={flat(members.map((m) => bp.map((pi) => colTot(m, pi))))} />
          </tbody>
        </table>
      </div>
    );
  }
  if (conf.colField) {
    const members = axisMembers(ds, conf.colField, atoms, 4).slice(0, 3);
    const rs = rowsFor(atoms, conf.rows, 3).filter((r) => r.type !== "band").slice(0, 4);
    const cell = (ms, m) => ms.filter((a) => mvOf(a, m)).reduce((s, a) => s + a.v[prim][4], 0);
    const colTot = (m) => atoms.filter((a) => mvOf(a, m)).reduce((s, a) => s + a.v[prim][4], 0);
    const tot = sumAgg(ds, atoms);
    return (
      <div className={box}>
        <table className="border-collapse w-full">
          <thead>
            <tr>
              <th className="bg-foreground text-background text-left px-1.5 py-0.5 font-semibold" style={{ ...hs, minWidth: 100 }}>{primLabel}, {P[4]}</th>
              {members.map((m) => (
                <th key={m} className="bg-foreground text-background text-right px-1 py-0.5 font-semibold" style={hs}>{m === "__u__" ? "None" : m}</th>
              ))}
              <th className="bg-foreground text-background text-right px-1 py-0.5 font-semibold" style={{ ...hs, borderLeft: "1.5px solid hsl(var(--background))" }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {rs.map((r, i) => (
              <tr key={i} className={"border-b border-border/60 " + rowCls(r)} style={rowStyle(r)}>
                <Label r={r} />
                {members.map((m) => <td key={m} className="px-1 text-right tabular-nums" style={{ ...cs, minWidth: 36 }}>{fmtN(cell(r.ms, m))}</td>)}
                <td className="px-1 text-right tabular-nums font-semibold" style={{ ...cs, minWidth: 36, borderLeft: "1px solid hsl(var(--border))" }}>{fmtN(r.agg[prim][4])}</td>
              </tr>
            ))}
            <TotalRow vals={[...members.map(colTot), tot[prim][4]]} />
          </tbody>
        </table>
      </div>
    );
  }
  const rs = rowsFor(atoms, conf.rows, conf.topN ? Math.min(conf.topN, 4) : 0).slice(0, 7);
  const tot = sumAgg(ds, atoms);
  return (
    <div className={box}>
      <table className="border-collapse w-full">
        <Head cols={pis.map((pi) => P[pi])} />
        <tbody>
          {rs.map((r, i) => (
            <tr key={i} className={"border-b border-border/60 " + rowCls(r)} style={rowStyle(r)}>
              <Label r={r} />
              {pis.map((pi) => <td key={pi} className="px-1.5 text-right tabular-nums" style={cs}>{r.type === "band" ? "" : fmtN(mval(ds, r.agg, prim, pi))}</td>)}
            </tr>
          ))}
          <TotalRow vals={pis.map((pi) => mval(ds, tot, prim, pi))} />
        </tbody>
      </table>
    </div>
  );
}

// ==================================================================
// Subtotal placement
// ==================================================================
function SubtotalsPanel({ ds, spec, patch, setVis }) {
  const levels = spec.rows.filter((r, i) => r.key !== "sku" && r.key !== "__custom__" && i < spec.rows.length - 1);
  if (!levels.length) return null;
  return (
    <details className="border border-border rounded-lg bg-card p-2">
      <summary className="text-muted-foreground uppercase tracking-wider cursor-pointer select-none" style={{ fontSize: 9.5 }}>Subtotal lines by category</summary>
      <div className="mt-1.5">
      {levels.map((l) => {
        const vals = membersOf(ds, l.key, true);
        const eff = (v) => (spec.overrides[l.key + "::" + v] || l.vis || "sub") === "sub";
        const toggle = (v) => patch({ overrides: { ...spec.overrides, [l.key + "::" + v]: eff(v) ? "hidden" : "sub" }, tpl: "custom" });
        return (
          <div key={l.key} className="mb-1.5">
            <div className="flex items-center justify-between">
              <span className="font-medium" style={{ fontSize: 11 }}>{dimLabel(ds, l.key)}</span>
              <span className="flex gap-1">
                <button onClick={() => setVis(l.key, "sub")} className="border border-border px-1 hover:border-foreground" style={{ fontSize: 9.5 }}>All on</button>
                <button onClick={() => setVis(l.key, "hidden")} className="border border-border px-1 hover:border-foreground" style={{ fontSize: 9.5 }}>All off</button>
              </span>
            </div>
            <div className="overflow-y-auto mt-0.5" style={{ maxHeight: 96 }}>
              {vals.map((v) => (
                <label key={v} className="flex items-center gap-1.5 py-0.5">
                  <input type="checkbox" checked={eff(v)} onChange={() => toggle(v)} />
                  <span style={{ fontSize: 11 }}>{v === "__u__" ? "No " + dimLabel(ds, l.key).toLowerCase() : memberLabel(ds, l.key, v)}</span>
                </label>
              ))}
            </div>
          </div>
        );
      })}
      </div>
    </details>
  );
}

// ==================================================================
// Split tables
// ==================================================================
function SplitTables({ ds, rep, spec, scoped, measures, source, innerRows, onCycle, onOpen, fullScope, mask, onRecon, recons, renames, catRenames, onRename, onPeriodMenu , selCols = [], onColClick, colBlock = null }) {
  const members = axisMembers(ds, spec.split, scoped, 4);
  const mlabel = (v) => (v === "__u__" ? "No " + dimLabel(ds, spec.split).toLowerCase() : memberLabel(ds, spec.split, v));
  return (
    <div className="space-y-4">
      {members.map((m) => {
        const subset = scoped.filter((a) => String(getv(a, spec.split) == null ? "__u__" : getv(a, spec.split)) === m);
        const out = [];
        buildRows(ds, subset, innerRows, 0, 0, "r|" + m, { topN: spec.topN, pos: spec.pos, overrides: spec.overrides, open: spec.open, seq: spec.seq }, out);
        return (
          <div key={m}>
            <div className="font-semibold mb-1" style={{ fontSize: 12 }}>{mlabel(m)}</div>
            <TreeTable ds={ds} rows={out} rep={rep} measures={measures} spec={spec} source={sumAgg(ds, subset)}
              onCycle={onCycle} onOpen={onOpen} fullScope={false} showChecks={false} totalLabel={mlabel(m) + " total"} mask={mask}
              renames={renames} catRenames={catRenames} onRename={onRename} onPeriodMenu={onPeriodMenu} selCols={selCols} onColClick={onColClick} colBlock={colBlock} blockScope={"s:" + m} />
          </div>
        );
      })}
      <div>
        <div className="font-semibold mb-1" style={{ fontSize: 12 }}>All, consolidated</div>
        <TreeTable ds={ds} rows={[]} rep={rep} measures={measures} spec={spec} source={source}
          onCycle={onCycle} onOpen={onOpen} fullScope={fullScope} showChecks totalLabel="Total, all" mask={mask}
          onRecon={onRecon} recons={recons} />
      </div>
    </div>
  );
}

// ==================================================================
// Side by side bands table
// ==================================================================
function BandsTable({ ds, rep, rows, spec, source, axis, scoped, mask, onPeriodMenu , selCols = [], onColClick, colBlock = null }) {
  const num = { ...MONO, fontSize: 11, minWidth: 58 };
  const members = axisMembers(ds, axis, scoped, 4);
  const bands = [...members, "__total__"];
  const mlabel = (v) => (v === "__total__" ? "Total" : v === "__u__" ? "No " + dimLabel(ds, axis).toLowerCase() : memberLabel(ds, axis, v));
  const match = (a, v) => String(getv(a, axis) == null ? "__u__" : getv(a, axis)) === v;
  const cell = (r, b, pi) => (b === "__total__" ? r.agg[ds.primary][pi] : r.ms.filter((a) => match(a, b)).reduce((s, a) => s + a.v[ds.primary][pi], 0));
  const colTotal = (b, pi) => (b === "__total__" ? source[ds.primary][pi] : scoped.filter((a) => match(a, b)).reduce((s, a) => s + a.v[ds.primary][pi], 0));
  return (
    <div className="border border-border rounded-lg overflow-auto" style={{ maxHeight: 460 }}>
      <table className="border-collapse">
        <thead className="sticky top-0">
          <tr>
            <th className="bg-card" />
            {bands.map((b) => (
              <th key={b} colSpan={rep.periods.length}
                className="bg-foreground text-background font-semibold px-2 py-1 text-center"
                style={{ fontSize: 11.5, borderLeft: "2px solid hsl(var(--background))" }}>
                {mlabel(b)}
              </th>
            ))}
          </tr>
          <tr>
            <th className="bg-foreground text-background text-left px-2 py-1 font-semibold" style={{ fontSize: 11.5, minWidth: 180 }}>
              {ds.measures.find((m) => m.k === ds.primary).label}
            </th>
            {bands.map((b) =>
              rep.periods.map((pi, i) => (
                <th key={b + pi}
                  onContextMenu={onPeriodMenu ? (e) => { e.preventDefault(); onPeriodMenu({ x: e.clientX, y: e.clientY, pi }); } : undefined}
                  onClick={onColClick ? (e) => onColClick(pi, e, "b:" + b) : undefined}
                  title={onPeriodMenu ? "Click to select, Shift extends, right click for options" : undefined}
                  className={(selCols.includes(pi) && (!colBlock || colBlock === "b:" + b) ? "bg-card text-foreground" : "bg-foreground text-background") + " text-right px-2 py-1 font-semibold select-none"}
                  style={{ fontSize: 11.5, borderLeft: i === 0 ? "2px solid hsl(var(--background))" : undefined, cursor: onColClick ? "pointer" : undefined }}>
                  <span className="inline-flex items-center gap-1">
                    {spec.colSrc && spec.colSrc[pi] && <FileSpreadsheet size={8} title={"Filled from " + spec.colSrc[pi]} />}
                    {P[pi]}
                  </span>
                </th>
              ))
            )}
          </tr>
        </thead>
        <tbody>
          {rows.filter((r) => r.type !== "band").map((r, ri) => (
            <tr key={ri} className={"border-b border-border hover:bg-muted/50 " + (r.type === "hier" ? "bg-muted font-semibold" : r.type === "sub" ? "font-semibold border-t border-foreground" : r.type === "other" ? "italic text-muted-foreground" : "")}>
              <td className="py-1 pr-2 whitespace-nowrap" style={{ paddingLeft: 8 + r.depth * 13, fontSize: 12 }}>{(spec.renames || {})[r.label] || r.label}</td>
              {bands.map((b) =>
                rep.periods.map((pi, i) => (
                  <td key={b + pi} className={"px-2 py-1 text-right tabular-nums " + (b === "__total__" ? "font-semibold" : "")}
                    style={{ ...num, borderLeft: i === 0 ? "1px solid hsl(var(--border))" : undefined }}>
                    {fmtN(cell(r, b, pi))}
                  </td>
                ))
              )}
            </tr>
          ))}
          <tr style={{ borderTop: "1.5px solid hsl(var(--foreground))" }}>
            <td className="px-2 py-1 font-bold whitespace-nowrap" style={{ fontSize: 12, borderBottom: "3px double hsl(var(--foreground))" }}>Total</td>
            {bands.map((b) =>
              rep.periods.map((pi, i) => (
                <td key={b + pi} className="px-2 py-1 text-right tabular-nums font-bold"
                  style={{ ...num, borderBottom: "3px double hsl(var(--foreground))", borderLeft: i === 0 ? "1px solid hsl(var(--border))" : undefined }}>
                  {fmtN(colTotal(b, pi))}
                </td>
              ))
            )}
          </tr>
          <tr>
            <td className="px-2 pt-1.5 pb-1.5 italic" style={{ fontSize: 11, color: "hsl(var(--destructive))" }}>Check to source</td>
            {bands.map((b) =>
              rep.periods.map((pi) => (
                <td key={b + pi} className="px-2 pt-1.5 pb-1.5 text-right italic tabular-nums" style={{ ...MONO, fontSize: 11, color: "hsl(var(--destructive))" }}>
                  {b === "__total__" && mask.has(pi) ? "-" : ""}
                </td>
              ))
            )}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ==================================================================
// Tables
// ==================================================================
function TreeTable({ ds, rows, rep, measures, spec, source, onCycle, onOpen, fullScope, showChecks, totalLabel, mask, selRows = [], onSelRow, onCtx, onRecon, recons = {}, renames = {}, catRenames = {}, onRename, onRowMove, onPeriodMenu, onSweepStart, onSweepMove, selCols = [], onColClick, colBlock = null, blockScope = null }) {
  const th = { fontSize: 11.5 };
  const num = { ...MONO, fontSize: 11.5, minWidth: 64 };
  const mdef = Object.fromEntries(ds.measures.map((m) => [m.k, m]));
  const [overRow, setOverRow] = useState<any>(null);
  return (
    <div className="border border-border rounded-lg overflow-auto" style={{ maxHeight: 460 }}>
      <table className="w-full border-collapse">
        <thead className="sticky top-0">
          <tr>
            <th className="bg-card" />
            {measures.map((mk) => (
              <th key={mk} colSpan={rep.periods.length}
                className="bg-foreground text-background font-semibold px-2 py-1 text-center" style={{ ...th, borderLeft: "2px solid hsl(var(--background))" }}>
                {mdef[mk].label}
              </th>
            ))}
          </tr>
          <tr>
            <th className="bg-foreground text-background text-left px-2 py-1 font-semibold" style={{ ...th, minWidth: 200 }}>Line</th>
            {measures.map((mk) =>
              rep.periods.map((pi, i) => (
                <th key={mk + pi}
                  onContextMenu={onPeriodMenu ? (e) => { e.preventDefault(); onPeriodMenu({ x: e.clientX, y: e.clientY, pi }); } : undefined}
                  onClick={onColClick ? (e) => onColClick(pi, e, (blockScope ? blockScope + "|" : "") + "m:" + mk) : undefined}
                  title={onPeriodMenu ? "Click to select, Shift extends, right click for options" : undefined}
                  className={(selCols.includes(pi) && (!colBlock || colBlock === (blockScope ? blockScope + "|" : "") + "m:" + mk) ? "bg-card text-foreground" : "bg-foreground text-background") + " text-right px-2 py-1 font-semibold select-none"}
                  style={{ ...th, borderLeft: i === 0 ? "2px solid hsl(var(--background))" : undefined, cursor: onColClick ? "pointer" : undefined }}>
                  <span className="inline-flex items-center gap-1">
                    {spec.colSrc && spec.colSrc[pi] && <FileSpreadsheet size={8} title={"Filled from " + spec.colSrc[pi]} />}
                    {P[pi]}
                  </span>
                </th>
              ))
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <TRow key={ri} ds={ds} r={r} rep={rep} measures={measures} mdef={mdef} onCycle={onCycle} onOpen={onOpen}
              selected={r.type === "leaf" && selRows.includes(r.label)} onSelRow={onSelRow} onCtx={onCtx}
              renames={renames} catRenames={catRenames} onRename={onRename} ri={ri}
              onRowMove={onRowMove} overRow={overRow} setOverRow={setOverRow}
              onSweepStart={onSweepStart} onSweepMove={onSweepMove} />
          ))}
          <tr style={{ borderTop: "1.5px solid hsl(var(--foreground))" }}>
            <td className="px-2 py-1 font-bold" style={{ fontSize: 12, borderBottom: "3px double hsl(var(--foreground))" }}>{totalLabel}</td>
            {measures.map((mk) =>
              rep.periods.map((pi, i) => (
                <td key={mk + pi} className="px-2 py-1 text-right font-bold tabular-nums"
                  style={{ ...num, borderBottom: "3px double hsl(var(--foreground))", borderLeft: i === 0 ? "1px solid hsl(var(--border))" : undefined }}>
                  {fmtBy(mdef[mk].kind)(mval(ds, source, mk, pi))}
                </td>
              ))
            )}
          </tr>
          {showChecks && (
            <tr>
              <td className="px-2 pt-1.5 italic" style={{ fontSize: 11, color: "hsl(var(--destructive))" }}>Check to source</td>
              {measures.map((mk) =>
                rep.periods.map((pi) => (
                  <td key={mk + pi} className="px-2 pt-1.5 text-right italic tabular-nums" style={{ ...MONO, fontSize: 11, color: "hsl(var(--destructive))" }}>
                    {ds.derived[mk] ? "" : mask.has(pi) ? "-" : ""}
                  </td>
                ))
              )}
            </tr>
          )}
          {showChecks && rep.plCheck && fullScope && (
            <tr>
              <td className="px-2 pb-1.5 italic" style={{ fontSize: 11, color: "hsl(var(--destructive))" }}>Check to Reported P&L</td>
              {measures.map((mk) =>
                rep.periods.map((pi) => (
                  <td key={mk + pi} className="px-2 pb-1.5 text-right italic tabular-nums" style={{ ...MONO, fontSize: 11, color: "hsl(var(--destructive))" }}>
                    {mk === ds.primary && mask.has(pi) ? (
                      <button onClick={() => onRecon && onRecon(pi)} title="Click to attach a file and reconcile this variance"
                        className="italic inline-flex items-center gap-0.5" style={{ color: "hsl(var(--destructive))", textDecoration: "underline dotted" }}>
                        {"(" + PL_OFFSET[pi].toLocaleString("en-NZ") + ")"}
                        {recons[pi] && <Check size={8} strokeWidth={3} />}
                      </button>
                    ) : ""}
                  </td>
                ))
              )}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function TRow({ ds, r, rep, measures, mdef, onCycle, onOpen, selected, onSelRow, onCtx, renames = {}, catRenames = {}, onRename, ri, onRowMove, overRow, setOverRow, onSweepStart, onSweepMove }) {
  const [edit, setEdit] = useState<any>(null);
  const num = { ...MONO, fontSize: 11.5 };
  const pad = 8 + r.depth * 13;
  const isSub = r.type === "sub";
  const isHier = r.type === "hier";
  const isBand = r.type === "band";
  const isLeaf = r.type === "leaf";
  const canRename = !!onRename && (isLeaf || !!r.gk);
  const disp = isLeaf
    ? renames[r.label] || r.label
    : r.gk && catRenames[r.gk]
    ? (isSub ? "Total " + catRenames[r.gk] : catRenames[r.gk])
    : r.label;
  const startVal = isLeaf ? renames[r.label] || r.label : catRenames[r.gk] || r.label.replace(/^Total /, "");
  const commit = () => { if (onRename && edit != null && edit.trim()) onRename(r, edit.trim()); setEdit(null); };
  const cls = (isHier ? "bg-muted font-semibold" : isSub ? "font-semibold" : r.type === "other" ? "italic text-muted-foreground" : "") + (selected ? " bg-muted" : "");
  return (
    <tr className={"group border-b border-border hover:bg-muted/50 " + cls}
      draggable={isLeaf && !!onRowMove && edit == null && selected}
      onDragStart={isLeaf && onRowMove ? (e) => { rowDragRef.ri = ri; e.dataTransfer.setData("text/plain", "row"); e.dataTransfer.effectAllowed = "move"; } : undefined}
      onDragEnd={isLeaf && onRowMove ? () => { rowDragRef.ri = null; if (setOverRow) setOverRow(null); } : undefined}
      onMouseDown={isLeaf && onSweepStart && edit == null ? (e) => {
        if (e.button !== 0) return;
        if (e.target.closest && e.target.closest("button, input")) return;
        if (selected && !e.shiftKey && !e.metaKey && !e.ctrlKey) return;
        e.preventDefault();
        onSweepStart(ri, e);
      } : undefined}
      onMouseEnter={isLeaf && onSweepMove ? () => onSweepMove(ri) : undefined}
      onClick={isLeaf && onSelRow && edit == null ? (e) => { if (e.detail > 1) return; if (selected && !e.shiftKey && !e.metaKey && !e.ctrlKey) onSelRow(r.label); } : undefined}
      onContextMenu={isLeaf && onCtx ? (e) => { e.preventDefault(); onCtx({ x: e.clientX, y: e.clientY, label: r.label, atom: r.ms[0] }); } : undefined}
      onDragOver={isLeaf && onRowMove ? (e) => { if (rowDragRef.ri == null) return; e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (setOverRow && (!overRow || overRow.ri !== ri)) setOverRow({ ri, below: rowDragRef.ri < ri }); } : undefined}
      onDrop={isLeaf && onRowMove ? (e) => { if (rowDragRef.ri == null) return; e.preventDefault(); const from = rowDragRef.ri; rowDragRef.ri = null; if (setOverRow) setOverRow(null); onRowMove(from, ri); } : undefined}
      style={{
        ...(isSub ? { borderTop: "1px solid hsl(var(--foreground))" } : isHier || isBand ? { borderTop: "1px solid hsl(var(--muted-foreground))" } : {}),
        ...(isLeaf && onSelRow ? { cursor: "pointer" } : {}),
        ...(selected ? { boxShadow: "inset 2.5px 0 0 hsl(var(--foreground))" } : {}),
        ...(overRow && overRow.ri === ri ? (overRow.below ? { boxShadow: "inset 0 -2.5px 0 hsl(var(--foreground))" } : { boxShadow: "inset 0 2.5px 0 hsl(var(--foreground))" }) : {}),
      }}>
      <td className="py-1 pr-2" style={{ paddingLeft: pad, fontSize: isBand ? 9 : 11 }}>
        {r.chev && (
          <button onClick={() => onOpen(r.path)} title={r.open ? "Collapse under an outline" : "Expand"}
            className="align-middle mr-1 text-muted-foreground hover:text-foreground">
            {r.open ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
          </button>
        )}
        {edit != null ? (
          <input autoFocus value={edit} onChange={(e) => setEdit(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEdit(null); }}
            onBlur={commit} onClick={(e) => e.stopPropagation()} onFocus={(e) => e.target.select()}
            className="rounded-md border border-foreground bg-card px-2 py-1 shadow-sm outline-none font-normal not-italic"
            style={{ fontSize: 12, width: Math.min(380, Math.max(230, edit.length * 6.8 + 44)) }} />
        ) : (
          <span className={isBand ? "uppercase tracking-wider text-muted-foreground font-medium" : ""}
            onDoubleClick={canRename ? (e) => { e.stopPropagation(); setEdit(startVal); } : undefined}
            title={canRename ? "Double click to rename" : undefined}>
            {disp}
          </span>
        )}
        {(isSub || isHier || isBand) && r.gk && onCycle && (
          <button onClick={() => onCycle(r.gk, r.vis)}
            title={isSub ? "Remove this subtotal line, restore it in the Subtotals panel" : isHier ? "Show as label only" : "Show as hierarchy header"}
            className="ml-1.5 opacity-0 group-hover:opacity-100 align-middle border border-border text-muted-foreground hover:bg-foreground hover:text-background p-0.5">
            <Sigma size={9} strokeWidth={2.5} />
          </button>
        )}
      </td>
      {measures.map((mk, bi) =>
        rep.periods.map((pi, i) => (
          <td key={mk + pi} className="px-2 py-1 text-right tabular-nums"
            style={{ ...num, borderLeft: i === 0 && bi >= 0 ? "1px solid hsl(var(--border))" : undefined }}>
            {isBand ? "" : fmtBy(mdef[mk].kind)(mval(ds, r.agg, mk, pi))}
          </td>
        ))
      )}
    </tr>
  );
}

function AxisColsTable({ ds, rows, spec, source, axis, scoped }) {
  const num = { ...MONO, fontSize: 11.5, minWidth: 66 };
  const pi = spec.colPeriod;
  const members = axisMembers(ds, axis, scoped, pi);
  const mlabel = (v) => (v === "__u__" ? "No " + dimLabel(ds, axis).toLowerCase() : memberLabel(ds, axis, v));
  const match = (a, v) => String(getv(a, axis) == null ? "__u__" : getv(a, axis)) === v;
  const cell = (ms, v) => ms.filter((a) => match(a, v)).reduce((s, a) => s + a.v[ds.primary][pi], 0);
  const colTotal = (v) => scoped.filter((a) => match(a, v)).reduce((s, a) => s + a.v[ds.primary][pi], 0);
  return (
    <div className="border border-border rounded-lg overflow-auto inline-block" style={{ maxHeight: 460 }}>
      <table className="border-collapse">
        <thead className="sticky top-0">
          <tr>
            <th className="bg-foreground text-background text-left px-2 py-1 font-semibold" style={{ fontSize: 11.5, minWidth: 200 }}>
              {ds.measures[0].label}, {P[pi]}
            </th>
            {members.map((e) => (
              <th key={e} className="bg-foreground text-background text-right px-2 py-1 font-semibold" style={{ fontSize: 11.5, minWidth: 66 }}>{mlabel(e)}</th>
            ))}
            <th className="bg-foreground text-background text-right px-2 py-1 font-semibold" style={{ fontSize: 11.5, minWidth: 72, borderLeft: "2px solid hsl(var(--background))" }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.filter((r) => r.type !== "band").map((r, ri) => (
            <tr key={ri} className={"border-b border-border hover:bg-muted/50 " + (r.type === "hier" ? "bg-muted font-semibold" : r.type === "sub" ? "font-semibold border-t border-foreground" : r.type === "other" ? "italic text-muted-foreground" : "")}>
              <td className="py-1 pr-2" style={{ paddingLeft: 8 + r.depth * 13, fontSize: 12 }}>{(spec.renames || {})[r.label] || r.label}</td>
              {members.map((e) => (
                <td key={e} className="px-2 py-1 text-right tabular-nums" style={num}>{fmtN(cell(r.ms, e))}</td>
              ))}
              <td className="px-2 py-1 text-right tabular-nums font-semibold" style={{ ...num, borderLeft: "1px solid hsl(var(--border))" }}>
                {fmtN(r.agg[ds.primary][pi])}
              </td>
            </tr>
          ))}
          <tr style={{ borderTop: "1.5px solid hsl(var(--foreground))" }}>
            <td className="px-2 py-1 font-bold" style={{ fontSize: 12, borderBottom: "3px double hsl(var(--foreground))" }}>Total</td>
            {members.map((e) => (
              <td key={e} className="px-2 py-1 text-right tabular-nums font-bold" style={{ ...num, borderBottom: "3px double hsl(var(--foreground))" }}>{fmtN(colTotal(e))}</td>
            ))}
            <td className="px-2 py-1 text-right tabular-nums font-bold" style={{ ...num, borderLeft: "1px solid hsl(var(--border))", borderBottom: "3px double hsl(var(--foreground))" }}>
              {fmtN(source[ds.primary][pi])}
            </td>
          </tr>
          <tr>
            <td className="px-2 pt-1.5 pb-1.5 italic" style={{ fontSize: 11, color: "hsl(var(--destructive))" }}>Check to source</td>
            {members.map((e) => <td key={e} />)}
            <td className="px-2 pt-1.5 pb-1.5 text-right italic tabular-nums" style={{ ...MONO, fontSize: 11, color: "hsl(var(--destructive))" }}>-</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ==================================================================
// Pivot panel widgets and controls
// ==================================================================
function AreaBox({ title, icon, hover, children, ...drop }) {
  return (
    <div {...drop} className={"rounded-lg p-2 " + (hover ? "bg-muted" : "bg-muted")} style={{ minHeight: 76 }}>
      <div className="flex items-center gap-1 text-muted-foreground mb-1" style={{ fontSize: 10 }}>
        {icon}<span className="uppercase tracking-wider">{title}</span>
      </div>
      {children}
    </div>
  );
}

function FieldChip({ label, sub, drag, onRemove, menu, small }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative border border-border rounded-md bg-card px-2 py-1 mb-1 cursor-grab flex items-center gap-1"
      draggable onDragStart={drag} style={{ fontSize: small ? 9.5 : 10 }}>
      <GripVertical size={9} className="text-muted-foreground/50" />
      <span className="flex-1 truncate">{label}</span>
      {sub && <span className="text-muted-foreground/70" style={{ fontSize: 9 }}>{sub}</span>}
      {menu && menu.length > 0 && (
        <button onClick={() => setOpen(!open)} className="text-muted-foreground/70 hover:text-foreground"><MoreHorizontal size={10} /></button>
      )}
      <button onClick={onRemove} className="text-muted-foreground/70 hover:text-foreground"><X size={9} /></button>
      {open && (
        <div className="pop absolute right-0 top-full z-10 bg-card border border-border min-w-40" onMouseLeave={() => setOpen(false)}>
          {menu.map(([lbl, fn, ticked], i) => (
            <button key={i} onClick={() => { fn(); setOpen(false); }}
              className="w-full text-left px-2 py-1 hover:bg-muted flex items-center gap-1.5" style={{ fontSize: 11 }}>
              <span className="w-3">{ticked ? <Check size={9} /> : null}</span>{lbl}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const CovBar = ({ pct }) => (
  <span className="flex items-center gap-1">
    <span className="inline-block w-7 h-1 bg-muted">
      <span className="block h-1 bg-foreground" style={{ width: pct + "%" }} />
    </span>
    <span className="text-muted-foreground/70" style={{ fontSize: 9.5 }}>{pct}%</span>
  </span>
);

const ProvDot = ({ ds, dim }) => {
  if (dim === "sku" || dim === "entity" || dim === "site")
    return <span className="inline-block w-1.5 h-1.5 rounded-full bg-foreground" title="Structural field" />;
  const stated = ds.dims[dim] && ds.dims[dim].prov === "stated";
  return (
    <span className={"inline-block w-1.5 h-1.5 rounded-full " + (stated ? "bg-foreground" : "border border-muted-foreground bg-card")}
      title={stated ? "Stated in source" : "Inferred by classifier"} />
  );
};

const Seg = ({ value, opts, onChange }) => (
  <span className="inline-flex border border-border rounded-md overflow-hidden">
    {opts.map(([v, l]) => (
      <button key={v} onClick={() => onChange(v)}
        className={"px-2 py-1 " + (value === v ? "bg-foreground text-background" : "hover:bg-muted")} style={{ fontSize: 10.5 }}>{l}</button>
    ))}
  </span>
);

const Pill = ({ st, inverse }) => {
  if (!st || st === "auto") return null;
  const label = st === "review" ? "Review" : st === "custom" ? "Custom" : st === "draft" ? "Draft" : "Auto";
  const cls = inverse ? "border-white text-background"
    : st === "review" ? "bg-foreground text-background border-foreground"
    : st === "custom" ? "border-foreground text-foreground font-semibold"
    : st === "draft" ? "border-foreground border-dashed text-foreground"
    : "border-border text-muted-foreground/70";
  return <span className={"border rounded-sm px-1 " + cls} style={{ fontSize: 9, lineHeight: "13px" }}>{label}</span>;
};

// ==================================================================
// Agentic build pane
// ==================================================================
function AgentPane({ rail, onBuild }) {
  const [files, setFiles] = useState(ALLFILES);
  const [sel, setSel] = useState([ALLFILES[2] ? ALLFILES[2].id : ALLFILES[0].id]);
  const toggleSel = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const [fileQ, setFileQ] = useState("");
  const [previewId, setPreviewId] = useState<any>(null);
  const [prompt, setPrompt] = useState("");
  const [section, setSection] = useState(rail[2] ? rail[2][0] : rail[0][0]);
  const [steps, setSteps] = useState<any[]>([]);
  const [running, setRunning] = useState(false);
  // User-picked template — overrides the agent's inferred one when set.
  const [tplChoice, setTplChoice] = useState<any>(null);
  const shown = files.filter((f) => f.label.toLowerCase().includes(fileQ.toLowerCase()));
  const stores = [...new Set(shown.map((f) => f.store))];
  const addUpload = () => {
    const nf = { id: "up_" + Date.now(), label: "uploaded_new_file.xlsx", periods: [4], dsKey: "sales", store: "Uploaded, pending extraction", uploaded: true };
    setFiles((fs) => [...fs, nf]);
    setSel((s) => [...s, nf.id]);
    setPreviewId(nf.id);
  };
  const run = () => {
    if (!prompt.trim() || running || sel.length === 0) return;
    const chosen = sel.map((id) => files.find((x) => x.id === id)).filter(Boolean);
    const f = chosen[0];
    const rec = inferAgent(prompt, f.dsKey);
    // If the user picked a template explicitly, honour it; otherwise use the agent's inference.
    const effectiveTpl = tplChoice || rec.tpl;
    const tplName = (TPLMAP[effectiveTpl] || { name: effectiveTpl }).name;
    const trace = [
      chosen.length === 1 ? "Reading attached file, " + f.label : "Reading " + chosen.length + " attached files, " + chosen.map((x) => x.label).join(", "),
      "Classified as " + DS[rec.ds].label + " table, mapping spec proposed",
      "Distinct members deduplicated, attributes parsed, coverage computed",
      "Report spec drafted, template " + tplName + (tplChoice ? " (your choice)" : ""),
      "Deterministic engine built the table, ties to source",
      "Draft added to " + section + " for your review",
    ];
    setRunning(true);
    setSteps([]);
    trace.forEach((t, i) =>
      setTimeout(() => {
        setSteps((s) => [...s, t]);
        if (i === trace.length - 1) {
          agentSeq += 1;
          const nm = prompt.trim();
          const def = {
            id: "agent_" + agentSeq,
            name: nm.length > 44 ? nm.slice(0, 44) + "..." : nm,
            ds: rec.ds, tpl: effectiveTpl, periods: [0, 1, 2, 3, 4],
            measures: rec.measures, agent: true,
          };
          setTimeout(() => { setRunning(false); onBuild(def, section); }, 500);
        }
      }, 450 * (i + 1))
    );
  };
  return (
    <div className="max-w-xl">
      <div className="flex items-center gap-2">
        <Sparkles size={14} />
        <span className="font-bold" style={{ fontSize: 15 }}>Agentic build</span>
      </div>
      <div className="text-muted-foreground mt-1 mb-3" style={{ fontSize: 11.5 }}>
        Attach a file, describe the analysis, choose where it belongs. The agent classifies the file and proposes
        a mapping and a report spec. The deterministic engine computes every number, and the draft lands in the
        databook rail with a Draft pill for review. The agent never touches the figures.
      </div>

      <div className="text-muted-foreground uppercase tracking-wider mb-1" style={{ fontSize: 9.5 }}>Attach a source file</div>
      <div className="flex items-center gap-1 bg-muted rounded-lg px-2.5 py-1.5 mb-1">
        <Search size={11} className="text-muted-foreground/70" />
        <input value={fileQ} onChange={(e) => setFileQ(e.target.value)} placeholder="Search file names" className="w-full outline-none bg-transparent" style={{ fontSize: 11.5 }} />
      </div>
      <div className="bg-card border border-border rounded-lg overflow-y-auto" style={{ maxHeight: 170 }}>
        {stores.map((st) => (
          <div key={st}>
            <div className="px-2 pt-1 text-muted-foreground/70 uppercase tracking-wider" style={{ fontSize: 8.5 }}>{st}</div>
            {shown.filter((f) => f.store === st).map((f) => (
              <div key={f.id} className="flex items-center gap-1.5 px-2 py-1 hover:bg-muted/50">
                <input type="checkbox" checked={sel.includes(f.id)} onChange={() => toggleSel(f.id)} />
                <FIcon label={f.label} size={9} />
                <span className="flex-1 truncate" style={{ fontSize: 11 }}>{f.label}</span>
                <span className="text-muted-foreground/70" style={{ fontSize: 9.5 }}>{perRange(f.periods)}</span>
                <button onClick={() => setPreviewId(previewId === f.id ? null : f.id)} title="Preview this file"
                  className={"p-0.5 " + (previewId === f.id ? "text-foreground" : "text-muted-foreground/70 hover:text-foreground")}>
                  <Eye size={10} />
                </button>
              </div>
            ))}
          </div>
        ))}
        <button onClick={addUpload}
          className="w-full text-left px-2 py-1 text-muted-foreground border-t border-border flex items-center gap-1 hover:bg-muted/50"
          style={{ fontSize: 10.5 }}>
          <Plus size={9} /> Add a new file
        </button>
      </div>
      {previewId && <FilePreview f={files.find((x) => x.id === previewId)} />}
      <div className="mt-2 mb-3">
        <div className="text-muted-foreground mb-1" style={{ fontSize: 10.5 }}>{sel.length} file{sel.length === 1 ? "" : "s"} selected</div>
        <div className="flex flex-wrap gap-1.5">
          {sel.map((id) => {
            const f = files.find((x) => x.id === id);
            if (!f) return null;
            return (
              <span key={id} className="flex items-center gap-1.5 rounded-lg border border-border bg-card px-2 py-1" style={{ fontSize: 10.5 }}>
                <FIcon label={f.label} size={10} />
                <span className="truncate" style={{ maxWidth: 220 }}>{f.label}</span>
                <span className="text-muted-foreground/70" style={{ fontSize: 8.5 }}>{fileExt(f.label)}</span>
                <button onClick={() => toggleSel(id)} className="text-muted-foreground/70 hover:text-foreground"><X size={10} /></button>
              </span>
            );
          })}
          {sel.length === 0 && <span className="text-muted-foreground/70 italic" style={{ fontSize: 10.5 }}>Tick at least one file above.</span>}
        </div>
      </div>

      <div className="text-muted-foreground uppercase tracking-wider mb-1" style={{ fontSize: 9.5 }}>What should the agent build</div>
      <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3}
        placeholder="For example: build gross margin by product group with subtotals, or revenue by customer channel, or entities side by side"
        className="w-full border border-border p-2 outline-none focus:border-foreground mb-3"
        style={{ fontSize: 12, resize: "vertical" }} />
      <div className="flex flex-wrap gap-1.5 mb-3">
        {["Gross margin by product group with subtotals", "Revenue by customer channel", "Entities side by side"].map((s) => (
          <button key={s} onClick={() => setPrompt(s)}
            className="border border-border px-2 py-1 text-muted-foreground hover:border-foreground" style={{ fontSize: 10 }}>{s}</button>
        ))}
      </div>

      <div className="flex items-center gap-2 mb-3">
        <span className="text-muted-foreground" style={{ fontSize: 11.5 }}>Add to databook section</span>
        <select value={section} onChange={(e) => setSection(e.target.value)}
          className="border border-border px-2 py-1 bg-card" style={{ fontSize: 11.5 }}>
          {rail.map(([s]) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={run} disabled={!prompt.trim() || running || sel.length === 0}
          className={"flex items-center gap-1.5 px-3 py-1 border " +
            (!prompt.trim() || running || sel.length === 0 ? "border-border text-muted-foreground/50" : "border-foreground bg-foreground text-background hover:bg-card hover:text-foreground")}
          style={{ fontSize: 11.5 }}>
          <Sparkles size={11} /> Build draft
        </button>
      </div>

      {steps.length > 0 && (
        <div className="border border-border p-2 space-y-1">
          {steps.map((s, i) => (
            <div key={i} className="flex items-center gap-1.5" style={{ fontSize: 11.5 }}>
              <Check size={10} strokeWidth={3} className="text-foreground" /> {s}
            </div>
          ))}
          {running && <div className="text-muted-foreground/70 italic" style={{ fontSize: 11 }}>working</div>}
        </div>
      )}
      <div className="mt-2 text-muted-foreground/70" style={{ fontSize: 10 }}>
        Simulated trace in this prototype. The split holds in the product: probabilistic classification, deterministic calculation.
      </div>
    </div>
  );
}

// ==================================================================
// Raw file preview, spreadsheet look
// ==================================================================
function FilePreview({ f }) {
  if (!f) return null;
  if (f.uploaded)
    return (
      <div className="border border-border border-t-0 rounded-b-md p-2 text-muted-foreground/70 italic" style={{ fontSize: 10.5 }}>
        {f.label}: queued for extraction. In the product, classification and mapping run on upload.
      </div>
    );
  const ds = DS[f.dsKey];
  const byName = new Map();
  ds.atoms.forEach((a) => {
    if (!byName.has(a.name)) byName.set(a.name, { idx: a.idx, v: P.map(() => 0) });
    const e = byName.get(a.name);
    P.forEach((_, i) => (e.v[i] += a.v[ds.primary][i]));
  });
  const rows = [...byName.entries()].sort((x, y) => x[1].idx - y[1].idx);
  const gcell = { fontFamily: "'IBM Plex Mono', monospace", fontSize: 9.5, border: "1px solid hsl(var(--border))" };
  return (
    <div className="border border-border border-t-0 rounded-b-md p-1.5">
      <div className="text-muted-foreground mb-1 truncate" style={{ fontSize: 9.5 }}>Preview, {f.label}</div>
      <div className="overflow-y-auto" style={{ maxHeight: 190 }}>
      <table className="border-collapse w-full">
        <thead>
          <tr>
            <td style={{ ...gcell, width: 18 }} className="bg-muted/50" />
            <td style={gcell} className="bg-muted/50 px-1 text-muted-foreground">A</td>
            {f.periods.map((pi, i) => (
              <td key={pi} style={gcell} className="bg-muted/50 px-1 text-muted-foreground text-right">{String.fromCharCode(66 + i)}</td>
            ))}
          </tr>
          <tr>
            <td style={gcell} className="bg-muted/50 text-center text-muted-foreground/70">1</td>
            <td style={gcell} className="px-1 font-medium">Item</td>
            {f.periods.map((pi) => (
              <td key={pi} style={gcell} className="px-1 text-right font-medium">{P[pi]}</td>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(([n, e], ri) => (
            <tr key={n}>
              <td style={gcell} className="bg-muted/50 text-center text-muted-foreground/70">{ri + 2}</td>
              <td style={{ ...gcell, maxWidth: 150 }} className="px-1 truncate">{n}</td>
              {f.periods.map((pi) => (
                <td key={pi} style={gcell} className="px-1 text-right tabular-nums">{fmtN(e.v[pi])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      <div className="mt-1 text-muted-foreground/70" style={{ fontSize: 9.5 }}>
        Tables detected: item listing, in use. Monthly summary, missed by extraction, open it from the report's source chip to include it. Notes block, not extractable.
      </div>
    </div>
  );
}

// ==================================================================
// Variance reconciliation modal
// ==================================================================
function ReconModal({ ds, pi, spec, patch, onClose }) {
  const existing = (spec.recons || {})[pi];
  const [fq, setFq] = useState("");
  const [fid, setFid] = useState(existing ? existing.file : (ALLFILES.find((f) => f.dsKey === "pnl") || ALLFILES[0]).id);
  const off = PL_OFFSET[pi];
  const a = Math.round(off * 0.56), b = Math.round(off * 0.31), c = off - a - b;
  const attach = () => patch({ recons: { ...(spec.recons || {}), [pi]: { file: fid } } });
  const fileOf = (id) => ALLFILES.find((f) => f.id === id) || { label: id };
  return (
    <div className="fixed inset-0 z-20 flex items-center justify-center" style={{ background: "rgba(23,23,23,0.35)" }} onClick={onClose}>
      <div className="bg-card border border-border rounded-xl shadow-2xl p-4 w-full mx-4" style={{ maxWidth: 460, maxHeight: "90vh", overflowY: "auto" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <span className="font-bold" style={{ fontSize: 14 }}>Reconcile variance, {P[pi]}</span>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={14} /></button>
        </div>
        <div className="mb-3" style={{ fontSize: 12 }}>
          Databook total vs Reported P&L: <span className="italic" style={{ color: "hsl(var(--destructive))" }}>({off.toLocaleString("en-NZ")})</span> USD 000s
        </div>
        {!existing && (
          <>
            <div className="text-muted-foreground uppercase tracking-wider mb-1" style={{ fontSize: 9.5 }}>Attach a file to reconcile against</div>
            <div className="flex items-center gap-1 bg-muted rounded-lg px-2.5 py-1.5 mb-1">
              <Search size={11} className="text-muted-foreground/70" />
              <input value={fq} onChange={(e) => setFq(e.target.value)} placeholder="Search file names" className="w-full outline-none bg-transparent" style={{ fontSize: 11.5 }} />
            </div>
            <div className="bg-card border border-border rounded-lg overflow-y-auto mb-2" style={{ maxHeight: 140 }}>
              {ALLFILES.filter((f) => f.label.toLowerCase().includes(fq.toLowerCase())).map((f) => (
                <label key={f.id} className="flex items-center gap-1.5 px-2 py-1 hover:bg-muted/50 cursor-pointer">
                  <input type="radio" name="reconfile" checked={fid === f.id} onChange={() => setFid(f.id)} />
                  <FileSpreadsheet size={9} className="text-muted-foreground" />
                  <span style={{ fontSize: 11 }}>{f.label}</span>
                  <span className="text-muted-foreground/70" style={{ fontSize: 9.5 }}>{perRange(f.periods)}</span>
                </label>
              ))}
            </div>
            <button onClick={attach}
              className="border border-foreground bg-foreground text-background px-3 py-1 hover:bg-card hover:text-foreground" style={{ fontSize: 11.5 }}>
              Attach and reconcile
            </button>
          </>
        )}
        {existing && (
          <>
            <div className="flex items-center gap-1.5 mb-2" style={{ fontSize: 11 }}>
              <Check size={11} strokeWidth={3} /> Reconciled against {fileOf(existing.file).label}
            </div>
            <table className="w-full border-collapse" style={{ fontSize: 11.5 }}>
              <tbody>
                <tr className="border-b border-border">
                  <td className="py-0.5">Timing of customer credit notes</td>
                  <td className="py-0.5 text-right tabular-nums">{a.toLocaleString("en-NZ")}</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="py-0.5">Freight and recharges outside product sales</td>
                  <td className="py-0.5 text-right tabular-nums">{b.toLocaleString("en-NZ")}</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="py-0.5">Rounding and FX</td>
                  <td className="py-0.5 text-right tabular-nums">{c.toLocaleString("en-NZ")}</td>
                </tr>
                <tr style={{ borderTop: "1.5px solid hsl(var(--foreground))" }}>
                  <td className="py-0.5 font-bold">Variance explained</td>
                  <td className="py-0.5 text-right tabular-nums font-bold">{off.toLocaleString("en-NZ")}</td>
                </tr>
              </tbody>
            </table>
            <div className="mt-2 text-muted-foreground/70" style={{ fontSize: 10 }}>
              The agent itemised the bridge from the attached file, the engine verified it sums to the variance. Sample lines in this prototype.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ==================================================================
// Source preview with full table list and row selection
// ==================================================================
function SourceModal({ ds, sid, spec, patch, onClose }) {
  const src = ds.sources.find((s) => s.id === sid);
  const byName = new Map();
  ds.atoms.forEach((a) => {
    if (!byName.has(a.name)) byName.set(a.name, { idx: a.idx, v: P.map(() => 0) });
    const e = byName.get(a.name);
    P.forEach((_, i) => (e.v[i] += a.v[ds.primary][i]));
  });
  const members = [...byName.entries()].sort((x, y) => x[1].idx - y[1].idx);
  const gkey = Object.keys(ds.dims)[0];
  const gmap = new Map();
  ds.atoms.forEach((a) => {
    const g = getv(a, gkey) == null ? "Other" : String(getv(a, gkey));
    gmap.set(g, (gmap.get(g) || 0) + (src ? src.periods.reduce((s2, pi) => s2 + a.v[ds.primary][pi], 0) : 0));
  });
  const groups = [...gmap.entries()].sort((x, y) => Math.abs(y[1]) - Math.abs(x[1])).slice(0, 6);
  const existing = (spec.srcSel || {})[sid] || {};
  const [excl, setExcl] = useState(existing.excluded ? [...existing.excluded] : []);
  const [queued, setQueued] = useState(existing.tables ? [...existing.tables] : []);
  const [active, setActive] = useState("t1");
  const tables = [
    { id: "t1", name: "Item listing with periods", range: "B5:H" + (5 + members.length), status: "used" },
    { id: "t2", name: "Monthly summary by " + dimLabel(ds, gkey).toLowerCase(), range: "K5:P" + (5 + groups.length), status: queued.includes("t2") ? "queued" : "missed" },
    { id: "t3", name: "Header and notes block", range: "B1:H4", status: "noext" },
  ];
  const toggleRow = (n) => setExcl((s) => (s.includes(n) ? s.filter((x) => x !== n) : [...s, n]));
  const toggleTable = (tid) => setQueued((s) => (s.includes(tid) ? s.filter((x) => x !== tid) : [...s, tid]));
  const apply = () => {
    patch({ srcSel: { ...(spec.srcSel || {}), [sid]: { excluded: excl, tables: queued } } });
    onClose();
  };
  const gcell = { fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, border: "1px solid hsl(var(--border))" };
  const StatusTag = ({ st }) => {
    const map = {
      used: ["In use", "bg-foreground text-background border-foreground"],
      missed: ["Missed by extraction", "border-border border-dashed text-muted-foreground"],
      queued: ["Queued for extraction", "border-foreground text-foreground font-semibold"],
      noext: ["Not extractable", "border-border text-muted-foreground/50"],
    };
    const pair = map[st];
    return <span className={"border rounded-sm px-1 " + pair[1]} style={{ fontSize: 8.5, lineHeight: "13px", whiteSpace: "nowrap" }}>{pair[0]}</span>;
  };
  if (!src) return null;
  const mon = (x, f) => fmtN((x / 12) * f);
  return (
    <div className="fixed inset-0 z-20 flex items-center justify-center" style={{ background: "rgba(23,23,23,0.35)" }} onClick={onClose}>
      <div className="bg-card border border-border rounded-xl shadow-2xl p-4 w-full mx-4" style={{ maxWidth: 620, maxHeight: "92vh", overflowY: "auto" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-2">
          <span className="font-bold truncate" style={{ fontSize: 14 }}>Preview and select, {src.label}</span>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={14} /></button>
        </div>

        <div className="text-muted-foreground uppercase tracking-wider mb-1" style={{ fontSize: 9.5 }}>Tables detected in this file, click to preview</div>
        <div className="border border-border rounded-md mb-2 overflow-hidden">
          {tables.map((t) => (
            <div key={t.id}
              className={"flex items-center gap-2 px-2 py-1.5 border-b border-border/60 cursor-pointer " + (active === t.id ? "bg-muted" : "hover:bg-muted/50")}
              onClick={() => setActive(t.id)}>
              <input type="checkbox"
                checked={t.status === "used" || t.status === "queued"}
                disabled={t.id === "t1" || t.id === "t3"}
                onClick={(e) => e.stopPropagation()}
                onChange={() => t.id === "t2" && toggleTable("t2")}
                title={t.id === "t1" ? "Primary extracted table, always in use" : t.id === "t3" ? "No tabular data to extract" : "Use this table"} />
              <span className="flex-1 truncate" style={{ fontSize: 11.5 }}>{t.name}</span>
              <span className="text-muted-foreground/70" style={{ ...MONO, fontSize: 9.5 }}>{t.range}</span>
              <StatusTag st={t.status} />
            </div>
          ))}
        </div>

        {active === "t1" && (
          <>
            <div className="text-muted-foreground uppercase tracking-wider mb-1" style={{ fontSize: 9.5 }}>Item listing, full contents, tick the rows to use</div>
            <div className="bg-card border border-border rounded-lg overflow-y-auto" style={{ maxHeight: 300 }}>
              <table className="border-collapse w-full">
                <thead className="sticky top-0 bg-card">
                  <tr>
                    <td style={{ ...gcell, width: 24 }} className="bg-muted/50 text-center text-muted-foreground/70">Use</td>
                    <td style={{ ...gcell, width: 18 }} className="bg-muted/50" />
                    <td style={gcell} className="bg-muted/50 px-1 font-medium">Item</td>
                    {src.periods.map((pi) => (
                      <td key={pi} style={gcell} className="bg-muted/50 px-1 text-right font-medium">{P[pi]}</td>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {members.map(([n, e], ri) => {
                    const on = !excl.includes(n);
                    return (
                      <tr key={n} className={on ? "" : "text-muted-foreground/50"}>
                        <td style={gcell} className="text-center">
                          <input type="checkbox" checked={on} onChange={() => toggleRow(n)} />
                        </td>
                        <td style={gcell} className="bg-muted/50 text-center text-muted-foreground/70">{ri + 2}</td>
                        <td style={{ ...gcell, maxWidth: 220 }} className="px-1 truncate">{n}</td>
                        {src.periods.map((pi) => (
                          <td key={pi} style={gcell} className="px-1 text-right tabular-nums">{fmtN(e.v[pi])}</td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {active === "t2" && (
          <>
            <div className="text-muted-foreground uppercase tracking-wider mb-1" style={{ fontSize: 9.5 }}>
              Monthly summary, {queued.includes("t2") ? "queued, the pipeline extracts and maps it on apply" : "missed by extraction, tick Use above to include it"}
            </div>
            <div className="bg-card border border-border rounded-lg overflow-y-auto" style={{ maxHeight: 300 }}>
              <table className="border-collapse w-full">
                <thead>
                  <tr>
                    <td style={{ ...gcell, width: 18 }} className="bg-muted/50" />
                    <td style={gcell} className="bg-muted/50 px-1 font-medium">Group</td>
                    {["M1", "M2", "M3", "M4"].map((m) => (
                      <td key={m} style={gcell} className="bg-muted/50 px-1 text-right font-medium">{m}</td>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {groups.map((g, ri) => (
                    <tr key={g[0]}>
                      <td style={gcell} className="bg-muted/50 text-center text-muted-foreground/70">{ri + 2}</td>
                      <td style={{ ...gcell, maxWidth: 200 }} className="px-1 truncate">{g[0]}</td>
                      <td style={gcell} className="px-1 text-right tabular-nums">{mon(g[1], 0.92)}</td>
                      <td style={gcell} className="px-1 text-right tabular-nums">{mon(g[1], 1.05)}</td>
                      <td style={gcell} className="px-1 text-right tabular-nums">{mon(g[1], 0.98)}</td>
                      <td style={gcell} className="px-1 text-right tabular-nums">{mon(g[1], 1.07)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {active === "t3" && (
          <div className="border border-border rounded-md p-2 text-muted-foreground" style={{ ...MONO, fontSize: 10.5 }}>
            <div>In USD</div>
            <div>Prepared by management, unaudited</div>
            <div>Draft, subject to review</div>
            <div className="text-muted-foreground/50 mt-1" style={UIF}>Free text, nothing tabular to extract</div>
          </div>
        )}

        <div className="flex items-center gap-2 mt-2 flex-wrap" style={{ fontSize: 11 }}>
          <span className="text-muted-foreground">
            {members.length - excl.length} of {members.length} rows included{queued.length ? ", 1 extra table queued" : ""}, feeding {perRange(src.periods)}
          </span>
          <button onClick={() => setExcl([])} className="border border-border px-2 py-1 hover:border-foreground">Include all rows</button>
          <button onClick={apply} className="border border-foreground bg-foreground text-background px-3 py-1 hover:bg-card hover:text-foreground">Apply</button>
        </div>
        <div className="mt-2 text-muted-foreground/70" style={{ fontSize: 10 }}>
          Excluded rows drop from this source's periods only and every check recomputes. A queued table goes through classification and mapping in the product, then its rows land in the same fact store.
        </div>
      </div>
    </div>
  );
}

function LibraryModal({ added, onAdd, onClose }) {
  const [q, setQ] = useState("");
  const shown = LIBRARY.filter((l) => (l.name + " " + l.desc).toLowerCase().includes(q.toLowerCase()));
  const sections = [...new Set(shown.map((l) => l.section))];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: "rgba(23,23,23,0.35)" }} onClick={onClose}>
      <div className="bg-card border border-border rounded-xl shadow-2xl p-4 flex flex-col" style={{ width: 560, maxHeight: "82vh" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-2.5">
          <div className="font-semibold" style={{ fontSize: 14 }}>Add reports</div>
          <button onClick={onClose} className="text-muted-foreground/70 hover:text-foreground px-1" style={{ fontSize: 14 }}>&times;</button>
        </div>
        <div className="bg-muted/50 rounded-lg p-3 mb-2.5 text-muted-foreground" style={{ fontSize: 11, lineHeight: 1.55 }}>
          The extracted data is complete, so every breakdown below computes from the same facts and ties to the same checks as the built reports. The set that matters most for this deal is already built. You can add more, but it is not recommended, typical databooks balloon past 100 tabs and there are usually three of them.
        </div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search breakdowns"
          className="w-full bg-muted rounded-lg px-2.5 py-1.5 mb-2.5 outline-none" style={{ fontSize: 12 }} />
        <div className="overflow-y-auto" style={{ minHeight: 0 }}>
          {sections.map((s) => (
            <div key={s} className="mb-2">
              <div className="uppercase tracking-wide text-muted-foreground/70 font-semibold px-1 py-1" style={{ fontSize: 9.5 }}>{s}</div>
              {shown.filter((l) => l.section === s).map((l) => {
                const isAdded = added.includes(l.id);
                return (
                  <div key={l.id} className="flex items-center gap-2.5 rounded-lg px-2 py-2 hover:bg-muted/50">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-foreground" style={{ fontSize: 12 }}>{l.name}</div>
                      <div className="text-muted-foreground truncate" style={{ fontSize: 10.5 }}>{l.desc}</div>
                    </div>
                    {isAdded ? (
                      <span className="flex items-center gap-1 text-muted-foreground/70 px-2.5 py-1" style={{ fontSize: 11 }}>
                        <Check size={11} /> Added
                      </span>
                    ) : (
                      <button onClick={() => onAdd(l)}
                        className="rounded-lg border border-border px-2.5 py-1 text-foreground/80 hover:border-foreground hover:text-foreground"
                        style={{ fontSize: 11 }}>
                        Add
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
          {shown.length === 0 && <div className="text-muted-foreground/70 px-1 py-3" style={{ fontSize: 11.5 }}>Nothing matches.</div>}
        </div>
      </div>
    </div>
  );
}

// ==================================================================
// Files stage, triage of the whole data room before the databook
// ==================================================================
function dlFile(f) {
  const seed = [...f.name].reduce((a, c) => a + c.charCodeAt(0), 0);
  let csv = '"File","' + f.name + '"\n"Folder","' + drFolderName(f.folder) + '"\n"Period","' + (f.m || "") + '"\n\n"Line","Value"\n';
  for (let r = 1; r <= 12; r++) csv += '"Item ' + (seed % 97 + r) + '","' + (((seed * (r + 3)) % 897 + 103) * 10) + '"\n';
  const blob = new Blob([csv], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = f.name.replace(/\.[a-z0-9]+$/i, "") + ".csv";
  a.click();
  URL.revokeObjectURL(a.href);
}
function dlManifest(files, label) {
  let csv = '"File","Folder","Period","Status"\n';
  files.forEach((f) => { csv += '"' + f.name + '","' + drFolderName(f.folder) + '","' + (f.m || "") + '","' + (f.status === "use" ? "In use" : "Set aside") + '"\n'; });
  const blob = new Blob([csv], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = label + ".csv";
  a.click();
  URL.revokeObjectURL(a.href);
}
/**
 * GroupedFileReview — cleaner primary view for the File review step.
 * Files organised into: Questions first, then In use / Duplicated /
 * Not for FDD / Currently not supported. Each row has inline Chat
 * ("why was this in/out?") and Preview quick actions. Reason renders
 * as a multi-sentence text block, not a bare chip.
 */
function GroupedFileReview({ qAns, answerQ, fileOv, onToggleFile, onOpenFile }) {
  const [openBucket, setOpenBucket] = useState({ q: true, use: true, dup: false, scope: false, nodata: false, super: false });
  const [q, setQ] = useState("");
  const [chatFor, setChatFor] = useState(null);
  const [previewFor, setPreviewFor] = useState(null);
  const [chatDraft, setChatDraft] = useState("");
  const [chatLog, setChatLog] = useState([]);

  const eff = (f) => (fileOv[f.id] ? (fileOv[f.id].use ? "use" : "excl") : f.status);
  const match = (name) => q === "" || name.toLowerCase().includes(q.toLowerCase());

  // Wrap DR_QUESTIONS into the same file-row shape everything else uses.
  // A question row has `q` pointing at the raw DR_QUESTIONS entry so the
  // FileRow rendering below can pull `.q.q` (question text), `.q.a`
  // (answer options) and `.q.id` uniformly.
  const questionFiles = DR_QUESTIONS
    .filter((x) => !qAns[x.id])
    .map((x) => ({ id: x.id, folder: x.folder, name: x.file, status: "use", q: x }))
    .filter((f) => match(f.name));
  const inUseFiles = DR_FILES.filter((f) => eff(f) === "use").filter((f) => match(f.name));
  const setAsideByYou = DR_FILES.filter((f) => fileOv[f.id] && fileOv[f.id].use === false).filter((f) => match(f.name));

  const bucketFor = (catId) => {
    const cat = DR_EXCL_CATS.find((c) => c.id === catId);
    const all = [...DR_EXCLUDED, ...DR_EXCL_MORE].filter((f) => f.cat === catId);
    return { cat, all: all.filter((f) => match(f.name)), total: cat ? cat.n : all.length };
  };
  const dup = bucketFor("dup");
  const scope = bucketFor("scope");
  const nodata = bucketFor("nodata");
  const supers = bucketFor("super");

  const openChat = (f) => {
    setChatFor(f);
    setPreviewFor(null);
    // Defensive: any of f.q / f.q.q / f.reason / f.note may be missing
    // depending on how the file was ingested; fall through cleanly.
    const introText =
      f && f.q && typeof f.q.q === "string"
        ? "This file is queued as a question because " + f.q.q.toLowerCase().replace(/\.$/, "") + ". You can pick the answer below, or ask me anything about it."
        : eff(f) === "use"
          ? "This one's in use" + (f && f.note ? " — " + f.note + "." : ".") + " Ask me anything about how it feeds the databook."
          : "Set aside" + (f && f.reason ? " because " + f.reason.toLowerCase() : " for a reason I can explain") + ". Ask me why, or ask to bring it back in.";
    setChatLog([{ role: "a", text: introText }]);
    setChatDraft("");
  };
  const sendChat = () => {
    const t = chatDraft.trim();
    if (!t) return;
    setChatLog((p) => [...p, { role: "u", text: t }]);
    setChatDraft("");
    setTimeout(() => {
      const f = chatFor;
      let reply = "Noted — I'll factor that in.";
      const low = t.toLowerCase();
      if (low.includes("why") && f) {
        reply = f.reason
          ? "It's set aside because " + f.reason.toLowerCase() + ". If that's wrong, you can bring it back into the databook from here."
          : f.note
            ? "It's in use because " + f.note.toLowerCase() + "."
            : "It's in scope by default. If it shouldn't be, take it out.";
      }
      if (low.includes("preview")) reply = "Opening a preview.";
      if (low.includes("include") || low.includes("bring back")) {
        if (f && eff(f) !== "use") onToggleFile(f.id, { use: true });
        reply = "Brought it back into the databook.";
      }
      if (low.includes("exclude") || low.includes("take out") || low.includes("remove")) {
        if (f && eff(f) === "use") onToggleFile(f.id, { use: false });
        reply = "Taken out of the databook.";
      }
      setChatLog((p) => [...p, { role: "a", text: reply }]);
    }, 350);
  };

  const FileRow = ({ f, kind }) => {
    const isQ = kind === "q";
    const isUse = kind === "use";
    const isAside = kind === "aside" || kind === "excl";
    return (
      <div className="border-t border-border/40 first:border-t-0 hover:bg-muted/30 transition-colors">
        <div className="flex items-center gap-2 px-4 py-2.5">
          <FileText size={12} className="text-muted-foreground shrink-0" />
          <button onClick={() => onOpenFile && onOpenFile(f.id)}
            className="text-left flex-1 min-w-0"
            title="Open in the browse view">
            <div className="font-medium text-foreground truncate" style={{ fontSize: 11.5 }}>{f.name}</div>
            <div className="text-muted-foreground/80 truncate" style={{ fontSize: 9.5 }}>
              {drFolderName(f.folder)}{f.m ? " · " + f.m : ""}
              {isQ && f.q && <> · <span className="text-foreground/80 font-semibold">needs a decision</span></>}
              {isUse && f.note && <> · {f.note}</>}
            </div>
          </button>
          {isQ ? (
            <span className="rounded-full bg-foreground text-background px-2 py-0.5 shrink-0 font-semibold" style={{ fontSize: 9 }}>Answer needed</span>
          ) : isUse ? (
            <span className="rounded-full bg-emerald-500/10 text-emerald-700 border border-emerald-500/30 px-2 py-0.5 shrink-0 font-semibold" style={{ fontSize: 9 }}>In use</span>
          ) : (
            <span className="rounded-full bg-muted text-muted-foreground px-2 py-0.5 shrink-0 font-semibold" style={{ fontSize: 9 }}>Set aside</span>
          )}
          <button onClick={() => openChat(f)}
            className="inline-flex items-center gap-1 rounded-md border border-foreground/30 bg-foreground/[0.03] text-foreground hover:bg-foreground/10 px-2 py-0.5 shrink-0 font-semibold"
            style={{ fontSize: 10 }}
            title="Ask why this file was in or out — chat about this specific file">
            <Sparkles size={11} /> Ask why
          </button>
          <button onClick={() => setPreviewFor(f)}
            className="inline-flex items-center gap-1 rounded-md border border-border text-muted-foreground hover:text-foreground hover:border-foreground px-2 py-0.5 shrink-0"
            style={{ fontSize: 10 }}
            title="Preview file">
            <Eye size={11} /> Preview
          </button>
          {isAside && (
            <button onClick={() => onToggleFile(f.id, { use: true })}
              className="ml-1 rounded-md border border-border text-muted-foreground hover:text-foreground hover:border-foreground px-2 py-0.5 shrink-0" style={{ fontSize: 10 }}>
              Bring back
            </button>
          )}
          {isUse && (
            <button onClick={() => onToggleFile(f.id, { use: false })}
              className="ml-1 rounded-md border border-border text-muted-foreground hover:text-destructive hover:border-destructive px-2 py-0.5 shrink-0" style={{ fontSize: 10 }}>
              Set aside
            </button>
          )}
        </div>
        {(f.reason || f.note) && (
          <div className="px-4 pb-2.5 pl-9 text-muted-foreground/80 leading-relaxed" style={{ fontSize: 10.5 }}>
            {f.reason || f.note}
          </div>
        )}
        {isQ && f.q && typeof f.q.q === "string" && (
          <div className="px-4 pb-2.5 pl-9 space-y-1.5">
            <p className="text-foreground/80 leading-relaxed" style={{ fontSize: 10.5 }}>{f.q.q}</p>
            <div className="flex flex-wrap gap-1.5">
              {(f.q.a || []).map((label) => (
                <button key={label} onClick={() => answerQ(f.q.id, label)}
                  className="rounded-md border border-border text-foreground hover:border-foreground hover:bg-muted px-2 py-1 font-medium" style={{ fontSize: 10.5 }}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  /**
   * Bucket — every bucket now leads with its verdict (Used / Set aside /
   * Needs a decision) and a plain-English WHY explanation right in the
   * header, so users don't have to expand a bucket of 1,000+ files to
   * understand why they're on that side of the fence.
   */
  const Bucket = ({ k, title, why, whyLong, count, tone, verdict, children, isEmpty }) => {
    const verdictStyle =
      verdict === "in"
        ? "bg-emerald-500/10 text-emerald-700 border border-emerald-500/30"
        : verdict === "question"
          ? "bg-foreground text-background"
          : "bg-amber-500/10 text-amber-800 border border-amber-500/30";
    const verdictLabel =
      verdict === "in" ? "Used in the databook"
      : verdict === "question" ? "Decision needed"
      : "Not used";
    return (
      <section className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="px-4 py-3">
          <div className="flex items-start gap-2.5">
            <span className={"w-1.5 h-9 rounded-full shrink-0 " + tone} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-foreground" style={{ fontSize: 13 }}>{title}</span>
                <span className="text-muted-foreground tabular-nums" style={{ fontSize: 11 }}>{count}</span>
                <span className={"rounded-full px-2 py-0.5 font-semibold whitespace-nowrap " + verdictStyle} style={{ fontSize: 9.5 }}>
                  {verdictLabel}
                </span>
              </div>
              {why && (
                <p className="text-foreground/80 mt-1.5 leading-relaxed" style={{ fontSize: 11 }}>
                  <span className="font-semibold text-foreground">Why:</span> {why}
                </p>
              )}
              {whyLong && (
                <p className="text-muted-foreground mt-1 leading-relaxed" style={{ fontSize: 10.5 }}>
                  {whyLong}
                </p>
              )}
            </div>
            <button
              onClick={() => setOpenBucket((p) => ({ ...p, [k]: !p[k] }))}
              className="p-1 rounded hover:bg-muted text-muted-foreground shrink-0 self-start"
              title={openBucket[k] ? "Hide files" : "Show files"}
            >
              {openBucket[k] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
          </div>
        </div>
        {openBucket[k] && (
          <div className="border-t border-border/60">
            {isEmpty ? (
              <p className="text-muted-foreground italic px-4 py-3 text-center" style={{ fontSize: 10.5 }}>Nothing to show here.</p>
            ) : children}
          </div>
        )}
      </section>
    );
  };

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="mx-auto space-y-3" style={{ maxWidth: 920, padding: "20px 24px 48px" }}>
        <div className="flex items-center gap-2 mb-1">
          <div className="flex items-center gap-1.5 bg-muted rounded-lg px-2.5 py-1.5 flex-1">
            <Search size={12} className="text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a file across every bucket"
              className="flex-1 outline-none bg-transparent" style={{ fontSize: 12 }} />
          </div>
        </div>

        {/* Questions bucket removed — now a dedicated step 2 (QuestionsStage). */}

        <Bucket
          k="use"
          title="In use"
          verdict="in"
          why="Every number in the databook currently traces back to one of these files."
          whyLong="This is the working set the pivot engine reads: management accounts, trial balances, transaction extracts and any supporting schedules that survived deduping. Set any file aside here to stop it feeding the numbers — the databook will recompute."
          count={inUseFiles.length.toLocaleString("en-GB") + (inUseFiles.length >= 24 ? "+ (showing first 24)" : "")}
          tone="bg-emerald-500"
          isEmpty={inUseFiles.length === 0}
        >
          {inUseFiles.slice(0, 24).map((f) => <FileRow key={f.id} f={f} kind="use" />)}
        </Bucket>

        {setAsideByYou.length > 0 && (
          <Bucket
            k="aside"
            title="Set aside by you"
            verdict="out"
            why="You've excluded these — they're kept for reference but don't feed any number."
            whyLong="This is only the ones you set aside manually. To see what the system auto-excluded, use the four buckets below (Duplicated / Not for FDD / Currently not supported / Superseded)."
            count={setAsideByYou.length}
            tone="bg-amber-500"
            isEmpty={false}
          >
            {setAsideByYou.map((f) => <FileRow key={f.id} f={f} kind="aside" />)}
          </Bucket>
        )}

        <Bucket
          k="dup"
          title="Duplicated"
          verdict="out"
          why="Byte-for-byte or content-identical copies of a file already in use — including them would double-count."
          whyLong="Kept in the data room for audit trail. Bring one back only if the file it duplicates was itself wrong — then take out the original."
          count={dup.total}
          tone="bg-stone-500"
          isEmpty={dup.all.length === 0}
        >
          {dup.all.map((f) => <FileRow key={f.id} f={f} kind="excl" />)}
        </Bucket>

        <Bucket
          k="scope"
          title="Not for FDD"
          verdict="out"
          why="Not financial content or outside the review window — these can't drive the databook."
          whyLong="Legal packs, HR files, marketing decks, and financials from periods outside the diligence window. Bring one back if you think it actually contains a number we should be reading."
          count={scope.total}
          tone="bg-blue-500"
          isEmpty={scope.all.length === 0}
        >
          {scope.all.map((f) => <FileRow key={f.id} f={f} kind="excl" />)}
        </Bucket>

        <Bucket
          k="nodata"
          title="Currently not supported"
          verdict="out"
          why="Nothing extractable — blank templates, images, scans below extraction quality."
          whyLong="These need a human to re-supply the data (e.g. a re-scan at higher resolution, or the underlying workbook rather than the PDF export). Preview them to confirm, then request a re-issue from management."
          count={nodata.total}
          tone="bg-rose-500"
          isEmpty={nodata.all.length === 0}
        >
          {nodata.all.map((f) => <FileRow key={f.id} f={f} kind="excl" />)}
        </Bucket>

        {supers.total > 0 && (
          <Bucket
            k="super"
            title="Superseded"
            verdict="out"
            why="A newer version of the same file is in use — the old one would contradict the fresh figures."
            whyLong="Preview a superseded file only to understand what changed between versions. If you need the older number in a bridge, ask in chat and it'll be surfaced."
            count={supers.total}
            tone="bg-violet-500"
            isEmpty={supers.all.length === 0}
          >
            {supers.all.map((f) => <FileRow key={f.id} f={f} kind="excl" />)}
          </Bucket>
        )}
      </div>

      {/* Chat popover for the selected file */}
      {chatFor && (
        <div className="fixed inset-0 z-40 flex items-end justify-end" onClick={() => setChatFor(null)}>
          <div className="bg-background border border-border rounded-2xl shadow-xl m-6 flex flex-col overflow-hidden"
            style={{ width: 380, maxHeight: "70vh" }}
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-2 px-4 py-3 border-b border-border/60">
              <div className="min-w-0">
                <div className="font-semibold truncate" style={{ fontSize: 12 }}>Ask about {chatFor.name}</div>
                <div className="text-muted-foreground truncate" style={{ fontSize: 10 }}>{drFolderName(chatFor.folder)}</div>
              </div>
              <button onClick={() => setChatFor(null)} className="text-muted-foreground hover:text-foreground p-1">
                <X size={14} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {chatLog.map((m, i) => (
                <div key={i} className={"flex " + (m.role === "u" ? "justify-end" : "justify-start")}>
                  <div className={"rounded-2xl px-3 py-1.5 max-w-[80%] leading-relaxed " + (m.role === "u" ? "bg-foreground text-background" : "bg-muted text-foreground")}
                    style={{ fontSize: 11 }}>{m.text}</div>
                </div>
              ))}
            </div>
            {chatLog.length <= 1 && (
              <div className="border-t border-border/60 px-3 py-2 flex flex-wrap gap-1.5">
                {["Why is this in / out?", "Bring it back in", "Take it out"].map((s) => (
                  <button
                    key={s}
                    onClick={() => { setChatDraft(s); setTimeout(sendChat, 10); }}
                    className="rounded-md border border-border text-foreground/80 hover:text-foreground hover:border-foreground px-2 py-0.5"
                    style={{ fontSize: 10 }}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
            <div className="border-t border-border/60 p-2 flex items-end gap-1.5">
              <textarea value={chatDraft} onChange={(e) => setChatDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendChat(); } }}
                placeholder="Ask why this is in or out, or tell me what to do…"
                rows={2}
                className="flex-1 resize-none bg-muted rounded-lg px-2 py-1.5 outline-none" style={{ fontSize: 11 }} />
              <button onClick={sendChat} disabled={!chatDraft.trim()}
                className={"rounded-lg px-2.5 py-1.5 font-medium " + (chatDraft.trim() ? "bg-foreground text-background" : "bg-muted text-muted-foreground")}
                style={{ fontSize: 11 }}>Send</button>
            </div>
          </div>
        </div>
      )}

      {/* File preview modal */}
      {previewFor && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-foreground/40" onClick={() => setPreviewFor(null)}>
          <div className="bg-background rounded-2xl shadow-xl mx-4 flex flex-col overflow-hidden"
            style={{ maxWidth: 720, width: "100%", maxHeight: "80vh" }}
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 p-4 border-b border-border/60">
              <div className="min-w-0">
                <div className="font-semibold truncate" style={{ fontSize: 13 }}>{previewFor.name}</div>
                <div className="text-muted-foreground truncate" style={{ fontSize: 11 }}>
                  {drFolderName(previewFor.folder)}{previewFor.m ? " · " + previewFor.m : ""}
                </div>
              </div>
              <button onClick={() => setPreviewFor(null)} className="text-muted-foreground hover:text-foreground p-1">
                <X size={16} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              <div className="rounded-lg border border-border bg-muted/30 p-4 text-center" style={{ fontSize: 12 }}>
                <FileText size={32} className="mx-auto text-muted-foreground/60 mb-2" />
                <p className="text-foreground font-medium mb-1">Preview isn't wired to the file blob yet.</p>
                <p className="text-muted-foreground leading-relaxed">
                  In a real project this pane would render the first sheet / page of{" "}
                  <span className="font-mono">{previewFor.name}</span>. For now, use the browse view to inspect the underlying rows.
                </p>
              </div>
              {(previewFor.reason || previewFor.note) && (
                <div className="mt-3 rounded-lg border border-border p-3">
                  <div className="text-muted-foreground uppercase tracking-wider font-semibold mb-1" style={{ fontSize: 9 }}>Reason</div>
                  <p className="text-foreground leading-relaxed" style={{ fontSize: 12 }}>{previewFor.reason || previewFor.note}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FilesWorkspace({ qAns, answerQ, fileOv, onToggleFile, recompNote, goBook, ctx, goEnts, tblOv, onTblOv }) {
  const [mode, setMode] = useState(ctx ? "browse" : "grouped");
  const [reasonF, setReasonF] = useState("all");
  const [fq, setFq] = useState("");
  const [folder, setFolder] = useState(ctx && ctx.folder ? ctx.folder : "all");
  const [selId, setSelId] = useState(() => {
    if (ctx && ctx.folder) {
      const first = [...DR_EXCLUDED, ...DR_FILES].find((f) => f.folder === ctx.folder);
      if (first) return first.id;
    }
    return "q1";
  });
  const [shut, setShut] = useState({ dup: true, nodata: true, scope: true, use: true });
  const [showAll, setShowAll] = useState<any>({});
  const openQ = DR_QUESTIONS.filter((x) => !qAns[x.id]);
  const qFiles = DR_QUESTIONS.map((x) => ({ id: x.id, folder: x.folder, name: x.file, status: "use", q: x }));
  const eff = (f) => (fileOv[f.id] ? (fileOv[f.id].use ? "use" : "excl") : f.status);
  const match = (f) => (folder === "all" || f.folder === folder) && (fq === "" || f.name.toLowerCase().includes(fq.toLowerCase()));
  const userAside = DR_FILES.filter((f) => fileOv[f.id] && fileOv[f.id].use === false);
  const inUseSample = DR_FILES.filter((f) => eff(f) === "use").slice(0, 24);
  const isBulk = selId.startsWith("bulk:");
  const allFiles = [...qFiles, ...DR_EXCLUDED, ...DR_EXCL_MORE, ...DR_FILES];
  const selF = allFiles.find((f) => f.id === selId) || qFiles[0];
  let inUseN = 8214;
  Object.entries(fileOv).forEach(([id, v]) => {
    const base = id.startsWith("f") ? "use" : "excl";
    if (base === "excl" && v.use) inUseN++;
    if (base === "use" && !v.use) inUseN--;
  });
  const fmtN = (n) => n.toLocaleString("en-GB");

  const Row = ({ f, sub }) => {
    const isQ = !!f.q, a = isQ && qAns[f.q.id];
    return (
      <button onClick={() => setSelId(f.id)}
        className={"w-full text-left px-2.5 py-1.5 rounded-lg " + (selId === f.id ? "bg-muted" : "hover:bg-muted/50")}>
        <span className="flex items-center gap-1.5">
          <span className="font-medium text-foreground truncate flex-1" style={{ fontSize: 11.5 }}>{f.name}</span>
          {isQ && (a ? <Check size={10} className="text-muted-foreground/70 shrink-0" />
            : <span className="rounded-full bg-foreground text-background px-1.5 py-0.5 shrink-0 font-medium" style={{ fontSize: 8.5 }}>Answer needed</span>)}
          {!isQ && fileOv[f.id] && <span className="text-muted-foreground/70 shrink-0" style={{ fontSize: 8.5 }}>you</span>}
        </span>
        {isQ ? (
          <>
            <span className="block text-muted-foreground truncate" style={{ fontSize: 10 }}>{f.q.short}</span>
            <span className="block text-muted-foreground/70 truncate" style={{ fontSize: 9 }}>{a ? "answered, " + a.toLowerCase() : drFolderName(f.folder)}</span>
          </>
        ) : (
          <span className="block text-muted-foreground/70 truncate" style={{ fontSize: 9.5 }}>{sub || drFolderName(f.folder) + (f.note ? ", " + f.note : "") + (f.reason ? ", " + f.reason.toLowerCase() : "")}</span>
        )}
      </button>
    );
  };

  const Section = ({ k, title, count, hint, action, children }) => (
    <div className="mb-1">
      <div className="w-full flex items-center gap-1.5 px-2 py-1 rounded-lg hover:bg-muted/50 group">
        <button onClick={() => setShut((p) => ({ ...p, [k]: !p[k] }))} className="flex items-center gap-1.5 flex-1 min-w-0 text-left">
          {shut[k] ? <ChevronRight size={11} className="text-muted-foreground/70 shrink-0" /> : <ChevronDown size={11} className="text-muted-foreground/70 shrink-0" />}
          <span className="font-semibold truncate" style={{ fontSize: 11 }}>{title}</span>
          <span className="text-muted-foreground/70 shrink-0" style={{ fontSize: 10 }}>{count}</span>
        </button>
        {action}
      </div>
      {!shut[k] && children}
      {!shut[k] && hint}
    </div>
  );

  return (
    <div className="flex-1 min-h-0 flex flex-col bg-muted/50">
      {ctx && (
        <div className="px-6 pt-3 shrink-0 flex items-center gap-2.5">
          <button onClick={goEnts}
            className="rounded-lg border border-foreground bg-foreground text-background px-3 py-1.5 hover:bg-foreground/90 flex items-center gap-1.5 font-semibold shadow-sm"
            style={{ fontSize: 11 }}>
            <ChevronLeft size={13} /> Back to entities
          </button>
          <span className="text-foreground/80" style={{ fontSize: 11 }}>
            Viewing <span className="font-semibold text-foreground">{drFolderName(ctx.folder)}</span>{ctx.entName ? <> · opened from <span className="font-semibold text-foreground">{ctx.entName}</span></> : null}
          </span>
        </div>
      )}
      <div className="px-6 pt-3 shrink-0 flex items-center gap-1">
        {[["grouped", "Grouped"], ["review", "Review"], ["board", "Board"], ["browse", "Browse"]].map(([k, l]) => (
          <button key={k} onClick={() => setMode(k)}
            className={"rounded-lg px-2.5 py-1 font-medium " + (mode === k ? "bg-foreground text-background" : "text-muted-foreground hover:bg-muted")}
            style={{ fontSize: 11 }}>{l}</button>
        ))}
      </div>
      {mode === "grouped" ? (
        <GroupedFileReview
          qAns={qAns}
          answerQ={answerQ}
          fileOv={fileOv}
          onToggleFile={onToggleFile}
          onOpenFile={(id) => { setSelId(id); setMode("browse"); }}
        />
      ) : mode === "board" ? (
        <FilesBoard qAns={qAns} answerQ={answerQ} fileOv={fileOv} onToggleFile={onToggleFile}
          onOpenFile={(id) => { setSelId(id); setMode("browse"); }} onFeed={() => setMode("review")} />
      ) : mode === "review" ? (
        <ReviewFeed qAns={qAns} answerQ={answerQ} fileOv={fileOv} onToggleFile={onToggleFile} recompNote={recompNote} goBook={goBook}
          onBrowse={(q) => { if (q != null) setFq(q); setMode("browse"); }}
          onOpenFile={(id) => { setSelId(id); setMode("browse"); }}
          onBoard={() => setMode("board")}
          onReviewAside={() => {
            setReasonF("super");
            const first = drCatFiles("super")[0];
            if (first) setSelId(first.id);
            setMode("browse");
          }}
          onBrowseFolder={(fid) => {
            setFolder(fid);
            const first = [...DR_EXCLUDED, ...DR_EXCL_MORE, ...DR_FILES].find((f) => f.folder === fid);
            if (first) setSelId(first.id);
            setMode("browse");
          }} />
      ) : (
      <>
      <div className="px-6 pt-4 pb-3 shrink-0 flex items-center gap-6 flex-wrap">

        {[["Files received", fmtN(8412)], ["In use", fmtN(inUseN)], ["Set aside", fmtN(8412 - inUseN)], ["Open questions", String(openQ.length)]].map(([k, v]) => (
          <div key={k}>
            <div className="font-semibold tracking-tight" style={{ fontSize: 18 }}>{v}</div>
            <div className="text-muted-foreground/70" style={{ fontSize: 9.5 }}>{k}</div>
          </div>
        ))}
        <div className="flex-1" />
        {recompNote && (
          <span className="flex items-center gap-1 text-muted-foreground whitespace-nowrap" style={{ fontSize: 11, animation: "popin .2s ease-out" }}>
            <Check size={11} strokeWidth={3} /> Recomputed, checks tie
          </span>
        )}
        <span className="text-muted-foreground hidden lg:block" style={{ fontSize: 11 }}>The databook is already built, review is optional.</span>
      </div>

      <div className="flex-1 min-h-0 flex flex-row">
        <div className="w-72 shrink-0 bg-card border-r border-border/60 p-2.5 overflow-y-auto">
          <div className="flex items-center gap-1.5 mb-2">
            <div className="flex items-center gap-1 bg-muted rounded-lg px-2.5 py-1.5 flex-1">
              <Search size={11} className="text-muted-foreground/70" />
              <input value={fq} onChange={(e) => setFq(e.target.value)} placeholder="Find a file" className="w-full outline-none bg-transparent" style={{ fontSize: 11 }} />
            </div>
          </div>

          <div className="flex flex-wrap gap-1 mb-2">
            {[["all", "All"], ["super", "Superseded 38"], ["dup", "Duplicates 121"], ["nodata", "No data 22"], ["scope", "Out of scope 13"]].map(([k, l]) => (
              <button key={k} onClick={() => setReasonF(k)}
                className={"rounded-lg px-2 py-0.5 font-medium " + (reasonF === k ? "bg-foreground text-background" : "text-muted-foreground hover:bg-muted")}
                style={{ fontSize: 9.5 }}>{l}</button>
            ))}
          </div>

          {reasonF !== "all" ? (
            <div>
              <div className="text-muted-foreground/70 px-2 py-1" style={{ fontSize: 9.5 }}>
                {(DR_EXCL_CATS.find((c) => c.id === reasonF) || {}).name}, every file with this reason, click one to review it
              </div>
              {drCatFiles(reasonF).filter(match).map((f) => <Row key={f.id} f={f} sub={drFolderName(f.folder) + ", " + f.reason.toLowerCase()} />)}
              {drCatFiles(reasonF).filter(match).length === 0 && <div className="text-muted-foreground/70 italic px-2 py-2" style={{ fontSize: 10 }}>Nothing matches the folder or search.</div>}
            </div>
          ) : (
          <div>
          <Section k="fold" title="Folders" count={DR_FOLDERS.length}>
            {[{ id: "all", name: "All folders", n: 8412 }, ...DR_FOLDERS].map((fo) => {
              const aside = fo.id === "all" ? 194 : DR_ASIDE_BY[fo.id] || 0;
              return (
                <button key={fo.id} onClick={() => setFolder(fo.id)}
                  className={"w-full flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-left " + (folder === fo.id ? "bg-muted font-semibold" : "text-muted-foreground hover:bg-muted/50")}
                  style={{ fontSize: 10.5 }}>
                  <span className="truncate flex-1">{fo.name}</span>
                  {aside > 0 && <span className="rounded-full bg-muted text-muted-foreground px-1.5 shrink-0" style={{ fontSize: 9 }}>{aside} aside</span>}
                  <span className="text-muted-foreground/70 shrink-0" style={{ fontSize: 9 }}>{fo.n.toLocaleString("en-GB")}</span>
                </button>
              );
            })}
          </Section>
          {folder !== "all" && (
            <button onClick={() => dlManifest([...DR_EXCLUDED, ...DR_FILES].filter((f) => f.folder === folder), drFolderName(folder))}
              className="w-full flex items-center justify-center gap-1.5 rounded-lg border border-border px-2 py-1.5 text-muted-foreground hover:border-foreground mb-2" style={{ fontSize: 10.5 }}>
              <Download size={11} /> Download this folder
            </button>
          )}

          <Section k="q" title="Questions from processing" count={openQ.length + " open"}>
            {qFiles.filter(match).map((f) => <Row key={f.id} f={f} />)}
          </Section>

          {userAside.length > 0 && (
            <Section k="you" title="Set aside by you" count={userAside.length}>
              {userAside.filter(match).map((f) => <Row key={f.id} f={f} />)}
            </Section>
          )}

          {DR_EXCL_CATS.map((cat) => {
            const catAll = drCatFiles(cat.id);
            const items = (showAll[cat.id] ? catAll : DR_EXCLUDED.filter((f) => f.cat === cat.id)).filter(match);
            return (
              <Section key={cat.id} k={cat.id} title={cat.name} count={cat.n}
                action={
                  <button onClick={() => setSelId("bulk:" + cat.id)}
                    className="opacity-0 group-hover:opacity-100 rounded-md border border-border px-1.5 py-0.5 text-muted-foreground hover:border-foreground hover:text-foreground shrink-0"
                    style={{ fontSize: 8.5 }}>
                    Add all
                  </button>
                }
                hint={
                  <button onClick={() => setShowAll((p) => ({ ...p, [cat.id]: !p[cat.id] }))}
                    className="text-muted-foreground/70 hover:text-foreground underline underline-offset-2 px-2.5 py-1 text-left" style={{ fontSize: 9.5 }}>
                    {showAll[cat.id] ? "Show named only" : "Show all " + cat.n + " with reasons"}
                  </button>
                }>
                {items.map((f) => <Row key={f.id} f={f} sub={drFolderName(f.folder) + ", " + f.reason.toLowerCase()} />)}
              </Section>
            );
          })}

          <Section k="use" title="In use" count={fmtN(inUseN)}
            hint={<div className="text-muted-foreground/70 px-2.5 py-1" style={{ fontSize: 9.5 }}>a sample, search to reach the rest</div>}>
            {inUseSample.filter(match).map((f) => <Row key={f.id} f={f} />)}
          </Section>
          </div>
          )}
        </div>

        <div className="flex-1 min-w-0 overflow-hidden">
          {isBulk ? (
            <BulkDetail catId={selId.slice(5)} fileOv={fileOv} onToggleFile={onToggleFile} onPick={(id) => setSelId(id)} />
          ) : (
            <FileDetail f={selF} eff={eff(selF)} ov={fileOv[selF.id]} qa={selF.q ? qAns[selF.q.id] : null}
              answerQ={answerQ} onToggleFile={onToggleFile} tblOv={tblOv} onTblOv={onTblOv} />
          )}
        </div>
      </div>
      </>
      )}
      </div>
  );
}

function CompareModal({ aName, bName, aLabel, bLabel, summary, options, onDecide, onClose }) {
  const [view, setView] = useState("diff");
  const base = bName.replace(/ ?\(.*\)| ?v\d|_v\d(_final)?| draft/gi, "");
  const seed = [...base].reduce((a, c) => a + c.charCodeAt(0), 0);
  const money = (x) => x.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const acc = [["1000", "Trade receivables"], ["1200", "Inventory, finished goods"], ["1400", "Prepayments"], ["1500", "Leasehold improvements at cost"], ["1510", "Accumulated depreciation, leasehold"], ["2000", "Trade payables"], ["2100", "GST payable"], ["2200", "PAYE payable"], ["2400", "Accrued expenses"], ["4000", "Revenue, domestic"], ["4100", "Revenue, export"], ["5000", "Cost of goods sold"], ["6010", "Freight out"], ["6100", "Salaries and wages"], ["6200", "Rent and outgoings"], ["6300", "Repairs and maintenance"], ["6400", "Insurance"], ["6450", "Other operating expenses"]];
  const rows = acc.map(([c, n], i) => {
    const vB = 12000 + ((seed * (i + 3) * 11) % 8971) / 8971 * 880000;
    const vA = c === "6450" ? vB + 12400 : vB;
    return { c, n, vA, vB, diff: vA - vB };
  });
  const totA = rows.reduce((a, r) => a + r.vA, 0), totB = rows.reduce((a, r) => a + r.vB, 0);
  const diffs = rows.filter((r) => r.diff !== 0);
  const shown = view === "diff" ? diffs : rows;
  const cell = { fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, borderBottom: "1px solid hsl(var(--border))", padding: "3px 8px", whiteSpace: "nowrap" };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6" style={{ background: "rgba(23,23,23,0.35)" }} onClick={onClose}>
      <div className="bg-card rounded-xl border border-border shadow-2xl flex flex-col" style={{ width: 860, maxHeight: "84vh" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 p-4 pb-3">
          <div className="min-w-0">
            <div className="font-semibold" style={{ fontSize: 13.5 }}>Comparing {aName} with {bName}</div>
            <div className="text-muted-foreground mt-0.5" style={{ fontSize: 11, lineHeight: 1.5 }}>{summary}</div>
          </div>
          <button onClick={onClose} className="text-muted-foreground/70 hover:text-foreground shrink-0"><X size={14} /></button>
        </div>
        <div className="flex items-center gap-1.5 px-4 pb-2">
          {[["diff", "Differences only, " + diffs.length + " of " + rows.length], ["all", "Every line side by side"]].map(([k, l]) => (
            <button key={k} onClick={() => setView(k)}
              className={"rounded-lg px-2.5 py-1 font-medium " + (view === k ? "bg-foreground text-background" : "text-muted-foreground hover:bg-muted")}
              style={{ fontSize: 10.5 }}>{l}</button>
          ))}
          {view === "diff" && <span className="text-muted-foreground/70" style={{ fontSize: 10 }}>{rows.length - diffs.length} identical lines hidden</span>}
        </div>
        <div className="flex-1 min-h-0 overflow-auto px-4">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th style={{ ...cell, borderBottom: "1px solid hsl(var(--foreground))" }} className="text-left font-semibold sticky top-0 bg-card">Code</th>
                <th style={{ ...cell, borderBottom: "1px solid hsl(var(--foreground))" }} className="text-left font-semibold sticky top-0 bg-card">Line</th>
                <th style={{ ...cell, borderBottom: "1px solid hsl(var(--foreground))" }} className="text-right font-semibold sticky top-0 bg-card">{aLabel}</th>
                <th style={{ ...cell, borderBottom: "1px solid hsl(var(--foreground))" }} className="text-right font-semibold sticky top-0 bg-card">{bLabel}</th>
                <th style={{ ...cell, borderBottom: "1px solid hsl(var(--foreground))" }} className="text-right font-semibold sticky top-0 bg-card">Difference</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.c} className={r.diff !== 0 ? "bg-muted" : ""}>
                  <td style={cell}>{r.c}</td>
                  <td style={{ ...cell, fontWeight: r.diff !== 0 ? 700 : 400 }}>{r.n}</td>
                  <td style={cell} className="text-right">{money(r.vA)}</td>
                  <td style={cell} className="text-right">{money(r.vB)}</td>
                  <td style={{ ...cell, fontWeight: r.diff !== 0 ? 700 : 400 }} className="text-right">{r.diff === 0 ? "" : money(r.diff)}</td>
                </tr>
              ))}
              <tr>
                <td style={cell} />
                <td style={{ ...cell, fontWeight: 700, borderTop: "1px solid hsl(var(--foreground))" }}>Total</td>
                <td style={{ ...cell, fontWeight: 700, borderTop: "1px solid hsl(var(--foreground))" }} className="text-right">{money(totA)}</td>
                <td style={{ ...cell, fontWeight: 700, borderTop: "1px solid hsl(var(--foreground))" }} className="text-right">{money(totB)}</td>
                <td style={{ ...cell, fontWeight: 700, borderTop: "1px solid hsl(var(--foreground))" }} className="text-right">{money(totA - totB)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        {options && (
          <div className="flex items-center gap-2 p-4 pt-3 border-t border-border/60">
            {options.map((opt, i) => (
              <button key={opt} onClick={() => onDecide(opt)}
                className={"rounded-lg px-3 py-1.5 font-medium " + (i === 0 ? "bg-foreground text-background hover:bg-foreground/90" : "border border-border text-foreground/80 hover:border-foreground")}
                style={{ fontSize: 11.5 }}>{opt}</button>
            ))}
            <span className="text-muted-foreground/70" style={{ fontSize: 10.5 }}>Deciding recomputes everything and re-runs the checks.</span>
          </div>
        )}
      </div>
    </div>
  );
}

const PVM_GROUPS = ["Peach Teat", "Milk Tankers", "Troughs", "Drinkers", "Feeders", "Traps"];
const PVM_COLS = [
  ["rev0", "Revenue, from period"], ["vol", "Volume effect"], ["price", "Price effect"],
  ["mix", "Mix effect"], ["rev1", "Revenue, to period"], ["chg", "Change %"],
];
function pvmRows(srcLabel, from, to) {
  const h = (s, i) => (([...s].reduce((a, c) => a * 31 + c.charCodeAt(0) >>> 0, 7) * (i + 3)) % 8971) / 8971;
  return PVM_GROUPS.map((g) => {
    const s = srcLabel + g + from + to;
    const q0 = 22000 + h(s, 1) * 16000, p0 = 7 + h(s, 2) * 8;
    const q1 = q0 * (0.88 + h(s, 3) * 0.28), p1 = p0 * (0.94 + h(s, 4) * 0.14);
    const rev0 = q0 * p0;
    const vol = (q1 - q0) * p0, price = (p1 - p0) * q0, mix = (p1 - p0) * (q1 - q0);
    return { g, rev0, vol, price, mix, rev1: rev0 + vol + price + mix };
  });
}
function PVMTable({ spec }) {
  const rows = pvmRows(spec.pvmSrc || "product_sales", spec.pvmFrom, spec.pvmTo);
  const tot = rows.reduce((a, r) => ({ rev0: a.rev0 + r.rev0, vol: a.vol + r.vol, price: a.price + r.price, mix: a.mix + r.mix, rev1: a.rev1 + r.rev1 }), { rev0: 0, vol: 0, price: 0, mix: 0, rev1: 0 });
  const cols = PVM_COLS.filter(([k]) => (spec.pvmCols || []).includes(k));
  const fmt = (x) => Math.round(x).toLocaleString("en-GB");
  const cell = (r, k) => k === "chg" ? ((r.rev1 / r.rev0 - 1) * 100).toFixed(1) + "%" : fmt(r[k]);
  const lbl = (k, l) => k === "rev0" ? "Revenue " + P[spec.pvmFrom] : k === "rev1" ? "Revenue " + P[spec.pvmTo] : l;
  return (
    <div>
      <table className="w-full" style={{ fontSize: 11.5 }}>
        <thead>
          <tr className="bg-foreground text-background">
            <th className="text-left font-semibold px-2 py-1.5" style={{ fontSize: 10.5 }}>Product group</th>
            {cols.map(([k, l]) => <th key={k} className="text-right font-semibold px-2 py-1.5" style={{ fontSize: 10.5, minWidth: 92 }}>{lbl(k, l)}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.g} className="border-b border-border hover:bg-muted/50">
              <td className="px-2 py-1">{r.g}</td>
              {cols.map(([k]) => <td key={k} className="text-right px-2 py-1" style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10.5 }}>{cell(r, k)}</td>)}
            </tr>
          ))}
          <tr className="font-bold" style={{ borderTop: "1px solid hsl(var(--foreground))" }}>
            <td className="px-2 py-1.5">Total</td>
            {cols.map(([k]) => <td key={k} className="text-right px-2 py-1.5" style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10.5 }}>{cell(tot, k)}</td>)}
          </tr>
        </tbody>
      </table>
      <div className="text-muted-foreground/70 mt-1.5 flex items-center gap-1" style={{ fontSize: 10 }}>
        <Check size={10} strokeWidth={3} /> Volume, price and mix sum exactly to the revenue change, populated from {spec.pvmSrc}. Change the template or the source at any time.
      </div>
    </div>
  );
}
function TemplateModal({ init, onSave, onClose }) {
  const [cols, setCols] = useState(init.pvmCols || ["rev0", "vol", "price", "mix", "rev1"]);
  const [from, setFrom] = useState(init.pvmFrom != null ? init.pvmFrom : 2);
  const [to, setTo] = useState(init.pvmTo != null ? init.pvmTo : 3);
  const [srcF, setSrcF] = useState(init.pvmSrc || "product_sales_fy24");
  const files = ALLFILES.filter((f) => f.dsKey === "sales");
  const flip = (k) => setCols((c) => (c.includes(k) ? c.filter((x) => x !== k) : [...c, k]));
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6" style={{ background: "rgba(23,23,23,0.35)" }} onClick={onClose}>
      <div className="bg-card rounded-xl border border-border shadow-2xl p-4" style={{ width: 560, maxHeight: "86vh", overflowY: "auto" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <div className="font-semibold" style={{ fontSize: 13.5 }}>{init.editId ? "Change the template" : "New report from a template"}</div>
          <button onClick={onClose} className="text-muted-foreground/70 hover:text-foreground"><X size={13} /></button>
        </div>
        <div className="text-muted-foreground mb-3" style={{ fontSize: 10.5, lineHeight: 1.5 }}>
          Pick the shape, pick the columns, attach a file. The deterministic engine populates every number, and you can change any of this after the report is built.
        </div>
        <div className="text-muted-foreground/70 mb-1" style={{ fontSize: 9.5 }}>Template</div>
        <div className="flex gap-1.5 mb-3 flex-wrap">
          <span className="rounded-lg bg-foreground text-background px-2.5 py-1 font-medium" style={{ fontSize: 10.5 }}>Price volume mix bridge</span>
          <span className="rounded-lg border border-border text-muted-foreground/50 px-2.5 py-1" style={{ fontSize: 10.5 }}>Customer bridge, coming</span>
          <span className="rounded-lg border border-border text-muted-foreground/50 px-2.5 py-1" style={{ fontSize: 10.5 }}>Working capital bridge, coming</span>
        </div>
        <div className="text-muted-foreground/70 mb-1" style={{ fontSize: 9.5 }}>Columns in the table</div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 mb-3">
          {PVM_COLS.map(([k, l]) => (
            <label key={k} className="flex items-center gap-1.5 cursor-pointer" style={{ fontSize: 11 }}>
              <input type="checkbox" checked={cols.includes(k)} onChange={() => flip(k)} />
              {k === "rev0" ? "Revenue, " + P[from] : k === "rev1" ? "Revenue, " + P[to] : l}
            </label>
          ))}
        </div>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-muted-foreground/70" style={{ fontSize: 9.5 }}>Bridge</span>
          <select value={from} onChange={(e) => setFrom(Number(e.target.value))} className="bg-muted rounded-lg px-2 py-1 outline-none" style={{ fontSize: 11 }}>
            {P.map((p, i) => <option key={p} value={i}>{p}</option>)}
          </select>
          <span className="text-muted-foreground/70" style={{ fontSize: 10.5 }}>to</span>
          <select value={to} onChange={(e) => setTo(Number(e.target.value))} className="bg-muted rounded-lg px-2 py-1 outline-none" style={{ fontSize: 11 }}>
            {P.map((p, i) => <option key={p} value={i}>{p}</option>)}
          </select>
        </div>
        <div className="text-muted-foreground/70 mb-1" style={{ fontSize: 9.5 }}>Attached file, the template re-populates from whichever is selected</div>
        <div className="border border-border/60 rounded-lg mb-3 overflow-y-auto" style={{ maxHeight: 150 }}>
          {files.map((f) => (
            <label key={f.id} className="flex items-center gap-1.5 px-2 py-1 hover:bg-muted/50 cursor-pointer" style={{ fontSize: 10.5 }}>
              <input type="radio" name="pvmsrc" checked={srcF === f.label} onChange={() => setSrcF(f.label)} />
              <FIcon label={f.label} size={9} />
              <span className="truncate flex-1">{f.label}</span>
              <span className="text-muted-foreground/70" style={{ fontSize: 9 }}>{(f.periods || []).map((pi) => P[pi]).join(", ")}</span>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-border px-3 py-1.5 text-muted-foreground hover:border-foreground" style={{ fontSize: 11.5 }}>Cancel</button>
          <button disabled={cols.length === 0} onClick={() => onSave({ pvmCols: cols, pvmFrom: from, pvmTo: to, pvmSrc: srcF })}
            className={"rounded-lg px-3 py-1.5 font-medium " + (cols.length ? "bg-foreground text-background hover:bg-foreground/90" : "bg-muted text-muted-foreground/70 cursor-not-allowed")}
            style={{ fontSize: 11.5 }}>
            {init.editId ? "Save, the report re-populates" : "Build the report"}
          </button>
        </div>
      </div>
    </div>
  );
}

const boardDragRef = { id: null };
function FilesBoard({ qAns, answerQ, fileOv, onToggleFile, onOpenFile, onFeed }) {
  const [over, setOver] = useState<any>(null);
  const openQ = DR_QUESTIONS.filter((x) => !qAns[x.id]);
  const doneQ = DR_QUESTIONS.filter((x) => qAns[x.id]);
  const toReview = DR_EXCLUDED.filter((f) => !(fileOv[f.id] && fileOv[f.id].use));
  const added = [...DR_EXCLUDED, ...DR_EXCL_MORE].filter((f) => fileOv[f.id] && fileOv[f.id].use);
  const verb = { super: "Use instead", dup: "Add anyway", nodata: "Add as reference", scope: "Add as reference" };
  const how = { super: "swapped for the version in use", dup: "de-duplicated on the way in", nodata: "reference only, no numbers", scope: "reference only, no numbers" };
  const dropAct = (target) => {
    const id = boardDragRef.id;
    if (!id) return;
    if (target === "add") {
      const f = DR_EXCLUDED.find((x) => x.id === id) || DR_EXCL_MORE.find((x) => x.id === id);
      onToggleFile(id, { use: true, how: (f && how[f.cat]) || "extracted as a new source", note: "" });
    } else onToggleFile(id, null);
  };
  const Col = ({ title, count, drop, hint, children }) => (
    <div className={"w-64 shrink-0 rounded-2xl p-2.5 flex flex-col transition-colors " + (drop && over === drop ? "bg-muted" : "bg-muted")}
      onDragOver={drop ? (e) => { e.preventDefault(); setOver(drop); } : undefined}
      onDragLeave={drop ? () => setOver(null) : undefined}
      onDrop={drop ? (e) => { e.preventDefault(); dropAct(drop); setOver(null); } : undefined}>
      <div className="flex items-center justify-between px-1 mb-2 shrink-0">
        <span className="font-semibold" style={{ fontSize: 11 }}>{title}</span>
        <span className="text-muted-foreground/70" style={{ fontSize: 9.5 }}>{count}</span>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto space-y-1.5">{children}</div>
      {hint && <div className="text-muted-foreground/70 px-1 pt-1.5 shrink-0" style={{ fontSize: 8.5 }}>{hint}</div>}
    </div>
  );
  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="px-6 pt-4 pb-2 shrink-0 flex items-center gap-2.5 flex-wrap">
        <span className="font-semibold" style={{ fontSize: 12.5 }}>The file work, as a board</span>
        <span className="text-muted-foreground/70" style={{ fontSize: 10.5 }}>cards move when you decide, drag a file card into a column to add it back or undo, click a name for the full file with folders on the left</span>
      </div>
      <div className="flex-1 min-h-0 overflow-x-auto px-6 pb-5">
        <div className="flex gap-3 h-full items-stretch" style={{ minWidth: 1080 }}>

          <Col title="Decisions to make" count={openQ.length}>
            {openQ.map((x) => (
              <div key={x.id} className="bg-card rounded-xl border border-border/60 p-2.5">
                <button onClick={() => onOpenFile(x.id)} className="font-semibold text-left hover:underline underline-offset-2 truncate w-full" style={{ fontSize: 10.5 }}>{x.file}</button>
                <div className="text-muted-foreground/70" style={{ fontSize: 8.5 }}>{drFolderName(x.folder)}</div>
                <div className="text-muted-foreground mt-1" style={{ fontSize: 10, lineHeight: 1.45 }}>{x.short}</div>
                <div className="flex gap-1 mt-1.5 flex-wrap">
                  {x.a.map((opt, i) => (
                    <button key={opt} onClick={() => answerQ(x.id, opt)}
                      className={"rounded-md px-1.5 py-0.5 font-medium " + (i === 0 ? "bg-foreground text-background hover:bg-foreground/90" : "border border-border text-muted-foreground hover:border-foreground")}
                      style={{ fontSize: 9 }}>{opt}</button>
                  ))}
                </div>
              </div>
            ))}
            {openQ.length === 0 && <div className="text-muted-foreground/70 italic px-1" style={{ fontSize: 10 }}>Nothing left to decide.</div>}
          </Col>

          <Col title="Files to review" count={toReview.length + " named of 198"} drop="review" hint="drop a card back here to undo an add">
            {toReview.map((f) => (
              <div key={f.id} draggable
                onDragStart={(e) => { boardDragRef.id = f.id; e.dataTransfer.setData("text/plain", "b"); e.dataTransfer.effectAllowed = "move"; }}
                onDragEnd={() => { boardDragRef.id = null; setOver(null); }}
                className="bg-card rounded-xl border border-border/60 p-2.5" style={{ cursor: "grab" }}>
                <button onClick={() => onOpenFile(f.id)} className="font-semibold text-left hover:underline underline-offset-2 truncate w-full" style={{ fontSize: 10.5 }}>{f.name}</button>
                <div className="text-muted-foreground/70" style={{ fontSize: 8.5 }}>{drFolderName(f.folder)}, {f.reason.toLowerCase()}</div>
                <button onClick={() => onToggleFile(f.id, { use: true, how: how[f.cat] || "extracted as a new source", note: "" })}
                  className="rounded-md border border-border px-1.5 py-0.5 text-muted-foreground hover:border-foreground mt-1.5" style={{ fontSize: 9 }}>
                  {verb[f.cat]}
                </button>
              </div>
            ))}
            <div className="text-muted-foreground/70 px-1 py-1" style={{ fontSize: 8.5 }}>and 181 more counted per reason, all in the export</div>
          </Col>

          <Col title="Added back by you" count={added.length} drop="add" hint="drop a file card here, the default treatment is applied">
            {added.map((f) => (
              <div key={f.id} draggable
                onDragStart={(e) => { boardDragRef.id = f.id; e.dataTransfer.setData("text/plain", "b"); e.dataTransfer.effectAllowed = "move"; }}
                onDragEnd={() => { boardDragRef.id = null; setOver(null); }}
                className="bg-card rounded-xl border border-border/60 p-2.5" style={{ cursor: "grab" }}>
                <span className="flex items-center gap-1.5">
                  <Check size={10} strokeWidth={3} className="text-muted-foreground/70 shrink-0" />
                  <button onClick={() => onOpenFile(f.id)} className="font-semibold text-left hover:underline underline-offset-2 truncate flex-1" style={{ fontSize: 10.5 }}>{f.name}</button>
                </span>
                <div className="text-muted-foreground/70" style={{ fontSize: 8.5 }}>{(fileOv[f.id] || {}).how}</div>
                <button onClick={() => onToggleFile(f.id, null)} className="text-muted-foreground/70 underline underline-offset-2 hover:text-foreground mt-1" style={{ fontSize: 9 }}>undo</button>
              </div>
            ))}
            {added.length === 0 && <div className="text-muted-foreground/70 italic px-1" style={{ fontSize: 10 }}>Drag a file here, or use its button.</div>}
          </Col>

          <Col title="Answered" count={doneQ.length}>
            {doneQ.map((x) => (
              <div key={x.id} className="bg-card rounded-xl border border-border/60 p-2.5">
                <span className="flex items-center gap-1.5">
                  <Check size={10} strokeWidth={3} className="text-muted-foreground/70 shrink-0" />
                  <span className="font-semibold truncate flex-1" style={{ fontSize: 10.5 }}>{x.file}</span>
                </span>
                <div className="text-muted-foreground mt-0.5" style={{ fontSize: 9.5 }}>{x.short} {qAns[x.id]}.</div>
                <button onClick={() => answerQ(x.id, null)} className="text-muted-foreground/70 underline underline-offset-2 hover:text-foreground mt-1" style={{ fontSize: 9 }}>change</button>
              </div>
            ))}
            {doneQ.length === 0 && <div className="text-muted-foreground/70 italic px-1" style={{ fontSize: 10 }}>Answers land here.</div>}
          </Col>
        </div>
      </div>
    </div>
  );
}

function ReviewFeed({ qAns, answerQ, fileOv, onToggleFile, recompNote, goBook, onBrowse, onOpenFile, onBrowseFolder, onReviewAside, onBoard }) {
  const [peek, setPeek] = useState<any>(null);
  const [cmp, setCmp] = useState<any>(null);
  const [asideOpen, setAsideOpen] = useState(false);
  const [q, setQ] = useState("");
  const openQ = DR_QUESTIONS.filter((x) => !qAns[x.id]);
  const verb = { super: "Use instead", dup: "Add anyway", nodata: "Add as reference", scope: "Add as reference" };
  const how = { super: "swapped for the version in use", dup: "de-duplicated on the way in", nodata: "reference only, no numbers", scope: "reference only, no numbers" };
  return (
    <div className="flex-1 min-h-0 overflow-y-auto">
      <div className="mx-auto px-6 py-6" style={{ maxWidth: 720 }}>

        <div className="flex items-start justify-between gap-3 mb-5">
          <div className="text-muted-foreground" style={{ fontSize: 12.5, lineHeight: 1.6 }}>
            <span className="font-semibold text-foreground" style={{ fontSize: 14 }}>8,412 files read, 31,240 tables found inside them, and the databook built from 8,214 files.</span>{" "}
            198 were set aside with reasons, {openQ.length > 0 ? openQ.length + (openQ.length === 1 ? " decision is" : " decisions are") + " yours below" : "no decisions are waiting"}, everything else is finished. Any change recomputes every report and re-runs the checks.
          </div>
          <span className="flex items-center gap-2 shrink-0 pt-1">
            {recompNote && (
              <span className="flex items-center gap-1 text-muted-foreground whitespace-nowrap" style={{ fontSize: 11, animation: "popin .2s ease-out" }}>
                <Check size={11} strokeWidth={3} /> Recomputed
              </span>
            )}
            <button onClick={goBook} className="rounded-lg bg-foreground text-background px-3 py-1.5 font-medium whitespace-nowrap hover:bg-foreground/90" style={{ fontSize: 11.5 }}>
              Open databook
            </button>
          </span>
        </div>

        <div className="flex items-center justify-between mb-1.5">
          <span className="font-semibold" style={{ fontSize: 12.5 }}>Decisions</span>
          <span className="text-muted-foreground/70" style={{ fontSize: 10.5 }}>{DR_QUESTIONS.length - openQ.length} of {DR_QUESTIONS.length} made</span>
        </div>
        <div className="space-y-2 mb-5">
          {DR_QUESTIONS.map((x) => {
            const a = qAns[x.id];
            const unreadable = x.id === "q3" || x.id === "q4";
            if (a)
              return (
                <div key={x.id} className="bg-card rounded-xl border border-border/60 px-4 py-2.5 flex items-center gap-2">
                  <Check size={12} strokeWidth={3} className="text-muted-foreground/70 shrink-0" />
                  <span className="text-muted-foreground flex-1" style={{ fontSize: 11.5 }}>{x.short} {a}.</span>
                  <button onClick={() => answerQ(x.id, null)} className="text-muted-foreground/70 underline underline-offset-2 hover:text-foreground" style={{ fontSize: 10 }}>change</button>
                </div>
              );
            return (
              <div key={x.id} className="bg-card rounded-xl border border-border/60 p-4">
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="font-semibold" style={{ fontSize: 10.5 }}>{x.file}</span>
                  <span className="text-muted-foreground/70" style={{ fontSize: 9.5 }}>{drFolderName(x.folder)}</span>
                </div>
                <div className="text-foreground" style={{ fontSize: 12.5, lineHeight: 1.55 }}>{x.q}</div>
                <div className="flex items-center gap-2 mt-2.5 flex-wrap">
                  {x.a.map((opt, i) => (
                    <button key={opt} onClick={() => answerQ(x.id, opt)}
                      className={"rounded-lg px-3 py-1.5 font-medium " + (i === 0 ? "bg-foreground text-background hover:bg-foreground/90" : "border border-border text-foreground/80 hover:border-foreground")}
                      style={{ fontSize: 11.5 }}>
                      {opt}{i === 0 && <span className="text-muted-foreground/70 font-normal" style={{ fontSize: 9.5 }}> , suggested</span>}
                    </button>
                  ))}
                  {(x.id === "q1" || x.id === "q2") ? (
                    <button onClick={() => onOpenFile(x.id)}
                      className="text-muted-foreground/70 underline underline-offset-2 hover:text-foreground ml-1" style={{ fontSize: 10.5 }}>
                      open both sheets
                    </button>
                  ) : (
                    <button onClick={() => setPeek(peek === x.id ? null : x.id)}
                      className="text-muted-foreground/70 underline underline-offset-2 hover:text-foreground ml-1" style={{ fontSize: 10.5 }}>
                      {peek === x.id ? "hide the file" : "look at the file"}
                    </button>
                  )}
                </div>
                {peek === x.id && (
                  <div className="mt-3 border-t border-border/60 pt-3 overflow-auto" style={{ maxHeight: 260 }}>
                    {unreadable ? (
                      <div className="text-muted-foreground/70 italic py-6 text-center" style={{ fontSize: 11.5 }}>Nothing extractable, the file could not be read. That is the point of the question.</div>
                    ) : (
                      <ExtractGrid f={{ name: x.file, folder: x.folder }} />
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {openQ.length === 0 && (
            <div className="text-muted-foreground/70 px-1" style={{ fontSize: 11 }}>Nothing needs you. Change any answer above at any time.</div>
          )}
        </div>

        <div className="bg-card rounded-xl border border-border/60 p-4 mb-5">
          <div className="text-muted-foreground" style={{ fontSize: 11.5, lineHeight: 1.6 }}>
            <span className="font-semibold text-foreground">198 files were set aside so nothing double counts or goes stale,</span>{" "}
            duplicates of files in use 121, superseded versions 38, nothing readable 22, out of scope 13. Inside the files in use, 2,180 individual tables were also set aside, working notes, blank stubs and duplicated summaries, each visible and reversible on its own sheet. Nothing is deleted, and any of it comes back with one click.
          </div>
          <table className="w-full mt-2.5">
            <thead>
              <tr className="text-muted-foreground/70 border-b border-border/60" style={{ fontSize: 9 }}>
                <th className="text-left font-medium py-1">Folder</th>
                <th className="text-right font-medium">Received</th>
                <th className="text-right font-medium">In use</th>
                <th className="text-right font-medium">Set aside</th>
                <th className="text-left font-medium pl-3">Because</th>
              </tr>
            </thead>
            <tbody>
              {DR_FOLDERS.map((fo) => {
                const aside = DR_ASIDE_BY[fo.id] || 0;
                const cats = {};
                [...DR_EXCLUDED, ...DR_EXCL_MORE].filter((f) => f.folder === fo.id).forEach((f) => { cats[f.cat] = (cats[f.cat] || 0) + 1; });
                const catLbl = { super: "superseded", dup: "duplicates", nodata: "unreadable", scope: "out of scope" };
                const topName = Object.entries(cats).sort((a, b) => b[1] - a[1]).slice(0, 2)
                  .map(([k, n]) => n + " " + catLbl[k]).join(", ");
                return (
                  <tr key={fo.id} onClick={() => onBrowseFolder(fo.id)} className="border-b border-border/40 hover:bg-muted/50 cursor-pointer" title="Open this folder">
                    <td className="py-1 font-medium" style={{ fontSize: 10.5 }}>{fo.name}</td>
                    <td className="text-right text-muted-foreground" style={{ fontSize: 10.5 }}>{fo.n.toLocaleString("en-GB")}</td>
                    <td className="text-right text-muted-foreground" style={{ fontSize: 10.5 }}>{(fo.n - aside).toLocaleString("en-GB")}</td>
                    <td className="text-right font-semibold" style={{ fontSize: 10.5 }}>{aside || ""}</td>
                    <td className="pl-3 text-muted-foreground/70" style={{ fontSize: 9.5 }}>{aside ? topName : ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="text-muted-foreground/70 mt-1.5" style={{ fontSize: 9.5 }}>Click a folder to open it with its set-aside files first.</div>
          <div className="flex items-center gap-3 mt-1.5">
            <button onClick={onReviewAside} className="rounded-lg border border-border bg-card px-2.5 py-1 text-foreground/80 hover:border-foreground font-medium" style={{ fontSize: 10.5 }}>
              Review them one by one
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-card border border-border/60 rounded-lg px-2.5 py-2 flex-1">
            <Search size={12} className="text-muted-foreground/70" />
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && onBrowse(q)}
              placeholder="Find any of the 8,412 files" className="w-full outline-none bg-transparent" style={{ fontSize: 11.5 }} />
          </div>
          <button onClick={() => onBrowse(q)} className="rounded-lg border border-border bg-card px-3 py-2 text-muted-foreground hover:border-foreground font-medium" style={{ fontSize: 11 }}>
            {q ? "Search" : "Browse everything"}
          </button>
        </div>
      </div>
    </div>
  );
}

function BulkDetail({ catId, fileOv, onToggleFile, onPick }) {
  const cat = DR_EXCL_CATS.find((c) => c.id === catId);
  const files = drCatFiles(catId);
  const addedN = files.filter((f) => fileOv[f.id] && fileOv[f.id].use).length;
  const allAdded = addedN === files.length;
  return (
    <div className="h-full flex flex-col px-5 py-4 gap-3 overflow-y-auto">
      <div className="flex items-start justify-between gap-3 shrink-0">
        <div className="min-w-0">
          <div className="font-semibold tracking-tight" style={{ fontSize: 17 }}>All {cat.name.toLowerCase()}</div>
          <div className="text-muted-foreground/70" style={{ fontSize: 10.5 }}>{cat.n} files, {cat.how.toLowerCase()}</div>
        </div>
        <span className="rounded-full px-2.5 py-1 shrink-0 bg-muted text-muted-foreground" style={{ fontSize: 10 }}>
          {addedN > 0 ? addedN + " of " + cat.n + " added" : "Set aside"}
        </span>
      </div>

      {catId === "dup" && !allAdded && (
        <div className="bg-card rounded-xl border border-border/60 px-3.5 py-2.5 shrink-0" style={{ fontSize: 11, lineHeight: 1.5 }}>
          <span className="font-semibold">Worth knowing.</span> <span className="text-muted-foreground">These files carry the same values as files already in use. Adding them back risks double counting, so identical values are de-duplicated and anything that differs is flagged.</span>
        </div>
      )}

      {allAdded ? (
        <div className="bg-card rounded-xl border border-border/60 px-3.5 py-2.5 shrink-0 flex items-center gap-3">
          <div className="flex-1 min-w-0" style={{ fontSize: 11.5 }}>
            <span className="font-semibold">All {cat.n} added to the analysis.</span>{" "}
            {(fileOv[files[0].id] || {}).note ? <span className="text-muted-foreground italic">{(fileOv[files[0].id] || {}).note}</span> : null}
          </div>
          <button onClick={() => onToggleFile(Object.fromEntries(files.map((f) => [f.id, null])))}
            className="rounded-lg border border-border px-2.5 py-1 text-muted-foreground hover:border-foreground shrink-0" style={{ fontSize: 10.5 }}>
            Undo all
          </button>
        </div>
      ) : (
        <ActionCard key={"bulk" + catId} f={{ name: cat.name, cat: catId }} dir="add"
          heading={"Add all " + cat.n + " files to the analysis"}
          onConfirm={(o) => onToggleFile(Object.fromEntries(files.map((f) => [f.id, o])))} />
      )}

      <div className="flex-1 min-h-0 bg-card rounded-xl border border-border/60 p-3.5 overflow-y-auto">
        <div className="text-muted-foreground/70 mb-1.5" style={{ fontSize: 9.5 }}>Every file in this group, click one to review it</div>
        <div className="grid grid-cols-2 gap-x-4">
          {files.map((f) => (
            <button key={f.id} onClick={() => onPick(f.id)} className="text-left rounded-lg px-2 py-1 hover:bg-muted/50 min-w-0">
              <span className="flex items-center gap-1.5">
                <span className="font-medium text-foreground truncate flex-1" style={{ fontSize: 11 }}>{f.name}</span>
                {fileOv[f.id] && fileOv[f.id].use && <Check size={10} className="text-muted-foreground/70 shrink-0" />}
              </span>
              <span className="block text-muted-foreground/70 truncate" style={{ fontSize: 9 }}>{drFolderName(f.folder)}, {f.reason.toLowerCase()}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function exclNarrative(f) {
  const base = f.name.replace(/ ?\((copy \d|conflict copy|email copy|1)\)| ?v\d| draft/gi, "").replace(/\s+\./, ".");
  if (f.cat === "super")
    return "Two versions of " + base + " arrived in the data room and this is the older one. The newer version agrees to the signed accounts, so it feeds the databook and this one was parked with nothing counted from it. Adding it back swaps it in for the version in use and everything recomputes.";
  if (f.cat === "dup")
    return "This file carries the same content as " + base + ", which is already extracted and feeding the databook. Counting both would double every value it contains, so the copy was parked on arrival. It is safe to add back, identical values are de-duplicated so nothing is counted twice.";
  if (f.cat === "nodata")
    return "Nothing machine-readable could be taken from this file, " + (f.reason || "").toLowerCase() + ". No value in the databook depends on it and its absence is noted in the completeness check. Provide a readable copy and it is extracted and placed automatically.";
  return "This is " + (f.reason || "reference material").toLowerCase() + ", not financial data for the review period. It was kept for reference and never feeds a number. Adding it stores it alongside the databook as citable support.";
}

function fileCommentary(f, eff, noData) {
  const n = f.name;
  const kind = /Trial balance|^TB/.test(n) ? "tb" : /Mgmt|Management/.test(n) ? "ma" : /Sales ledger|Credit notes/.test(n) ? "ar"
    : /Purchase/.test(n) ? "ap" : /Bank/.test(n) ? "bank" : /Payroll/.test(n) ? "pay" : /Stock|Warehouse/.test(n) ? "stock" : "tb";
  const entName = (LEGAL_ENTS.find((x) => x.id === f.ent) || {}).name || "the group";
  let what = noData
    ? "Nothing could be read from this file, " + (f.reason ? f.reason.toLowerCase() : "it is locked or image-only") + "."
    : {
        tb: "A full trial balance for " + entName + (f.m ? ", " + f.m : "") + ", 412 accounts, debits equal credits.",
        ma: "Monthly management accounts for " + entName + ", revenue down to EBITDA" + (f.m ? ", the pack ends " + f.m : "") + ".",
        ar: "An invoice-level sales ledger" + (f.m ? " for " + f.m : "") + ", customer, date and amount on every line.",
        ap: "A bill-level purchase ledger" + (f.m ? " for " + f.m : "") + ", supplier, date and amount on every line.",
        bank: "A bank statement with a running balance" + (f.m ? ", " + f.m : "") + ", every movement dated and described.",
        pay: "A payroll summary by employee, gross to net with PAYE and KiwiSaver" + (f.m ? ", " + f.m : "") + ".",
        stock: "A priced inventory listing by SKU" + (f.m ? " at " + f.m : "") + ", quantity, unit cost and value.",
      }[kind];
  const why = f.cat ? f.reason + ". " + (DR_EXCL_CATS.find((c) => c.id === f.cat) || {}).how + "." : null;
  const use = eff !== "use"
    ? { super: "One click on Use this version instead swaps it in and everything recomputes.",
        dup: "Safe to add back, duplicates are removed so nothing double counts.",
        nodata: "Provide a readable copy and it is extracted automatically, until then it can only sit as reference.",
        scope: "Reference material, it sits next to the analysis and never feeds a number." }[f.cat] || "Add it back and it is extracted and placed."
    : noData
    ? "Provide a readable copy and it is extracted automatically."
    : {
        tb: "Feeds every statement, the P&L, the balance sheet and the tie-out checks.",
        ma: "Feeds the monthly P&L, seasonality and the MA-to-TB reconciliation.",
        ar: "Feeds revenue by customer, concentration and DSO.",
        ap: "Feeds cost detail, supplier concentration and DPO.",
        bank: "Feeds net debt, the cash proof and completeness checks.",
        pay: "Feeds FTE, people cost and the payroll reconciliation.",
        stock: "Feeds inventory, DIO and the margin build.",
      }[kind];
  return { what, why, use };
}

function pairInfo(f) {
  if (f.q && f.q.id === "q1") return { otherName: "TB_FY24_v3.xlsx", thisTag: "in use", vThis: "base", vOther: "plus", nDiff: 1, kindHint: "tb" };
  if (f.q && f.q.id === "q2") return { otherName: "May-24 trial balance", thisTag: "", otherTag: "governs", vThis: "plus", vOther: "base", nDiff: 1, kindHint: "ma" };
  if (f.cat === "super") return { otherName: f.name.replace(/ ?v\d| draft/i, "").replace(/\s+\./, ".").replace(/\.(xlsx|pdf|csv)$/i, " (in use).$1"), otherTag: "in use", vThis: "plus", vOther: "base", nDiff: 1 };
  if (f.cat === "dup") return { otherName: f.name.replace(/ ?\((copy \d|conflict copy|email copy|1)\)/i, "").replace(/\s+\./, ".").replace(/\.(xlsx|pdf|csv)$/i, " (in use).$1"), otherTag: "in use", vThis: "base", vOther: "base", nDiff: 0 };
  return null;
}

function FileDetail({ f, eff, ov, qa, answerQ, onToggleFile, tblOv = {}, onTblOv }) {
  const [side, setSide] = useState("this");
  const [selT, setSelT] = useState<any>(null);
  const refsMap = useRef<any>({});
  const [hi, setHi] = useState(true);
  const [about, setAbout] = useState(false);
  const diffRef = useRef(null);
  const noData = f.cat === "nodata" || (f.q && f.q.id === "q3") || (f.q && f.q.id === "q4");
  const c = fileCommentary(f, eff, noData);
  const pair = noData ? null : pairInfo(f);
  const shownName = pair && side === "other" ? pair.otherName : f.name;
  const variant = pair ? (side === "this" ? pair.vThis : pair.vOther) : "base";
  const jump = () => { if (diffRef.current) diffRef.current.scrollIntoView({ behavior: "smooth", block: "center" }); };
  const builtMeta = noData ? null : buildSheet({ ...f, name: shownName }, f.name, "base", pair && pair.kindHint);
  const tKey = (tid) => f.id + ":" + tid;
  const effTbl = (t) => {
    const o = tblOv[tKey(t.tid)] || {};
    return { ...t, status: o.status || t.status, approved: !!o.approved, start: o.start != null ? o.start : t.start, end: o.end != null ? o.end : t.end };
  };
  const defs = builtMeta ? builtMeta.tables.map(effTbl) : [];
  const selDef = defs.find((t) => t.tid === selT);
  const nudge = (which, d) => {
    if (!selDef) return;
    const base0 = builtMeta.tables.find((t) => t.tid === selT);
    const v = (which === "start" ? selDef.start : selDef.end) + d;
    const lo = which === "start" ? base0.start : Math.max(base0.start, base0.end - 2);
    const hiB = which === "start" ? Math.min(base0.end, base0.start + 2) : base0.end + 2;
    onTblOv(tKey(selT), { [which]: Math.max(lo, Math.min(hiB, v)) });
  };
  const quickVerb = { super: "Use this version instead", dup: "Add anyway", nodata: "Add as reference", scope: "Add as reference" };
  const quickHow = { super: "swapped for the version in use", dup: "de-duplicated on the way in", nodata: "reference only, no numbers", scope: "reference only, no numbers" };
  const [howOpen, setHowOpen] = useState(false);
  const [bandNoting, setBandNoting] = useState(false);
  const [bandNote, setBandNote] = useState("");
  const HOW_OPTS = [
    ["Extract as a new source", "extracted as a new source", "It is read and its numbers feed the reports"],
    ["Replace the version in use", "swapped for the version in use", "Swaps in for the file currently extracted, everything recomputes"],
    ["Reference only, no numbers", "reference only, no numbers", "Sits beside the analysis, never feeds a value"],
  ];
  return (
    <div className="h-full flex flex-col px-4 py-3 gap-2">
      <div className="shrink-0">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="font-semibold tracking-tight truncate" style={{ fontSize: 14.5 }}>{shownName}</div>
            <div className="text-muted-foreground/70" style={{ fontSize: 9.5 }}>{drFolderName(f.folder)}{f.m ? ", " + f.m : ""}</div>
          </div>
          <span className="flex items-center gap-1.5 shrink-0">
            <button onClick={() => dlFile({ ...f, name: shownName })} title="Download this file"
              className="rounded-lg border border-border px-2 py-1 text-muted-foreground hover:border-foreground bg-card flex items-center gap-1" style={{ fontSize: 10 }}>
              <Download size={10} /> Download
            </button>
            <span className={"rounded-full px-2 py-1 " + (f.q && !qa ? "bg-card border border-border text-foreground/80" : eff === "use" ? "bg-foreground text-background" : "bg-muted text-muted-foreground")} style={{ fontSize: 9 }}>
              {f.q && !qa ? "Needs a decision" : eff === "use" ? "In use" : "Set aside"}
            </span>
          </span>
        </div>

        <div className="flex items-center justify-between gap-3 mt-1.5 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            {pair ? (
              <>
                <div className="flex items-center bg-muted rounded-lg p-0.5">
                  {[["this", f.name, pair.thisTag], ["other", pair.otherName, pair.otherTag]].map(([k, nm, tag]) => (
                    <button key={k} onClick={() => setSide(k)}
                      className={"rounded-md px-2.5 py-1 font-medium truncate " + (side === k ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground")}
                      style={{ fontSize: 10, maxWidth: 210 }}>
                      {nm}{tag ? <span className={side === k ? " text-muted-foreground/50" : " text-muted-foreground/70"}> · {tag}</span> : null}
                    </button>
                  ))}
                </div>
                {pair.nDiff > 0 ? (
                  <button onClick={() => { setHi(!hi); if (!hi) jump(); }}
                    className={"rounded-lg px-2 py-1 font-medium " + (hi ? "bg-foreground text-background" : "border border-border text-muted-foreground hover:border-foreground")}
                    style={{ fontSize: 10 }}>
                    {pair.nDiff} difference{hi ? ", shown" : ""}
                  </button>
                ) : (
                  <span className="text-muted-foreground/70" style={{ fontSize: 10 }}>identical, flip to verify</span>
                )}
                {pair.nDiff > 0 && <button onClick={jump} className="text-muted-foreground/70 underline underline-offset-2 hover:text-foreground" style={{ fontSize: 10 }}>jump to it</button>}
              </>
            ) : (
              <span className="text-muted-foreground/70" style={{ fontSize: 9.5 }}>single version in the data room</span>
            )}
          </div>

          <span className="flex items-center gap-1.5 shrink-0">
            {f.q ? (
              qa ? (
                <span className="flex items-center gap-1.5 text-muted-foreground" style={{ fontSize: 10.5 }}>
                  <Check size={11} strokeWidth={3} /> {qa}
                  <button onClick={() => answerQ(f.q.id, null)} className="text-muted-foreground/70 underline underline-offset-2 hover:text-foreground" style={{ fontSize: 9.5 }}>change</button>
                </span>
              ) : (
                f.q.a.map((opt, i) => (
                  <button key={opt} onClick={() => answerQ(f.q.id, opt)}
                    className={"rounded-lg px-2.5 py-1 font-medium " + (i === 0 ? "bg-foreground text-background hover:bg-foreground/90" : "border border-border text-foreground/80 hover:border-foreground")}
                    style={{ fontSize: 10.5 }}>{opt}</button>
                ))
              )
            ) : ov ? (
              <span className="flex items-center gap-1.5 text-muted-foreground" style={{ fontSize: 10.5 }}>
                <Check size={11} strokeWidth={3} /> {ov.use ? "added" : "set aside"}
                <button onClick={() => onToggleFile(f.id, null)} className="text-muted-foreground/70 underline underline-offset-2 hover:text-foreground" style={{ fontSize: 9.5 }}>undo</button>
              </span>
            ) : eff === "use" ? (
              <button onClick={() => onToggleFile(f.id, { use: false, how: "excluded from the reports", note: "" })}
                className="rounded-lg border border-border px-2.5 py-1 text-muted-foreground hover:border-foreground bg-card" style={{ fontSize: 10.5 }}>
                Set aside
              </button>
            ) : (
              <button onClick={() => onToggleFile(f.id, { use: true, how: quickHow[f.cat] || "extracted as a new source", note: "" })}
                className="rounded-lg bg-foreground text-background px-2.5 py-1 font-medium hover:bg-foreground/90" style={{ fontSize: 10.5 }}>
                {quickVerb[f.cat] || "Add to the analysis"}
              </button>
            )}
          </span>
        </div>
      </div>

      {eff === "excl" && !ov && (
        <div className="bg-card rounded-xl border border-border/60 px-3.5 py-2.5 shrink-0">
          <div className="text-muted-foreground/70" style={{ fontSize: 9 }}>Why this file is out</div>
          <div className="text-foreground/80" style={{ fontSize: 11.5, lineHeight: 1.55 }}>{exclNarrative(f)}</div>
          <div className="flex items-center gap-2 mt-2 flex-wrap relative">
            <button onClick={() => onToggleFile(f.id, { use: true, how: quickHow[f.cat] || "extracted as a new source", note: bandNote.trim() })}
              className="rounded-lg bg-foreground text-background px-2.5 py-1 font-medium hover:bg-foreground/90" style={{ fontSize: 10.5 }}>
              {quickVerb[f.cat] || "Add to the analysis"}
            </button>
            <button onClick={() => setHowOpen(!howOpen)}
              className="rounded-lg border border-border px-2 py-1 text-muted-foreground hover:border-foreground flex items-center gap-1" style={{ fontSize: 10.5 }}>
              or choose what to do <ChevronDown size={10} />
            </button>
            {!bandNoting && (
              <button onClick={() => setBandNoting(true)} className="text-muted-foreground/70 underline underline-offset-2 hover:text-foreground" style={{ fontSize: 9.5 }}>
                add a note, optional
              </button>
            )}
            {howOpen && (
              <div className="pop absolute left-0 top-8 z-30 bg-card border border-border rounded-xl shadow-xl p-1" style={{ width: 320 }}>
                {HOW_OPTS.map(([label, howV, desc]) => (
                  <button key={label} onClick={() => { onToggleFile(f.id, { use: true, how: howV, note: bandNote.trim() }); setHowOpen(false); }}
                    className="w-full text-left rounded-lg px-2.5 py-1.5 hover:bg-muted/50">
                    <span className="block font-medium" style={{ fontSize: 11 }}>{label}</span>
                    <span className="block text-muted-foreground" style={{ fontSize: 9.5 }}>{desc}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {bandNoting && (
            <textarea value={bandNote} onChange={(e) => setBandNote(e.target.value)} autoFocus placeholder="A line for the file log, applied with whichever action you pick"
              className="w-full bg-muted rounded-lg px-2.5 py-2 outline-none resize-none mt-2" style={{ fontSize: 11, height: 44 }} />
          )}
        </div>
      )}

      <div className="shrink-0">
        <button onClick={() => setAbout(!about)} className="flex items-center gap-1.5 text-left w-full group">
          <span className="text-muted-foreground/70 group-hover:text-foreground shrink-0" style={{ fontSize: 9.5 }}>{about ? "hide" : "about"}</span>
          <span className="text-muted-foreground truncate flex-1" style={{ fontSize: 10.5 }}>{f.q ? f.q.q : c.what + " " + c.use}</span>
        </button>
        {about && (
          <div className="mt-2 space-y-2">
            <div className="bg-card rounded-xl border border-border/60 px-3.5 py-2.5">
              <div className="text-muted-foreground/70" style={{ fontSize: 9 }}>How this file was read</div>
              <div className="text-foreground/80" style={{ fontSize: 11.5, lineHeight: 1.55 }}>
                <span className="font-semibold">What is in it.</span> {c.what}{" "}
                {c.why && eff !== "use" && !ov && (<><span className="font-semibold">Why it is set aside.</span> {c.why}{" "}</>)}
                <span className="font-semibold">{eff === "use" ? "How it is used." : "How it can be used."}</span> {c.use}
              </div>
            </div>
            <FileChat key={f.id + eff} f={f} eff={eff} noData={noData} />
          </div>
        )}
      </div>

      {defs.length > 0 && (
        <div className="bg-card rounded-xl border border-border/60 px-3 py-2 shrink-0 flex items-center gap-2 flex-wrap">
          {!selDef ? (
            <>
              <span className="text-muted-foreground" style={{ fontSize: 10.5 }}>
                <span className="font-semibold">{defs.length} tables on this sheet,</span>{" "}
                {defs.filter((t) => t.status === "use").length} in use, {defs.filter((t) => t.status !== "use").length} set aside or pending.
              </span>
              <span className="text-muted-foreground/70" style={{ fontSize: 10 }}>Click a table label on the sheet to approve, keep or adjust its range.</span>
            </>
          ) : (
            <>
              <span className="font-semibold" style={{ fontSize: 10.5 }}>{selDef.label}.</span>
              <span className="text-muted-foreground flex-1" style={{ fontSize: 10, minWidth: 160 }}>{selDef.reason}, confidence {selDef.conf}%.</span>
              {(() => {
                const base0 = builtMeta.tables.find((t) => t.tid === selT);
                const k = tKey(selT);
                const B = ({ dark, onClick, children }) => (
                  <button onClick={onClick}
                    className={"rounded-lg px-2 py-0.5 font-medium " + (dark ? "bg-foreground text-background hover:bg-foreground/90" : "border border-border text-muted-foreground hover:border-foreground")}
                    style={{ fontSize: 9.5 }}>{children}</button>
                );
                if (selDef.status === "approve")
                  return (<><B dark onClick={() => onTblOv(k, { status: "aside", approved: true })}>Approve removal</B><B onClick={() => onTblOv(k, { status: "use" })}>Keep this table</B></>);
                if (selDef.status === "aside" && selDef.approved)
                  return (<span className="flex items-center gap-1.5 text-muted-foreground" style={{ fontSize: 9.5 }}><Check size={10} strokeWidth={3} /> approved out <button onClick={() => onTblOv(k, null)} className="underline underline-offset-2 text-muted-foreground/70 hover:text-foreground">undo</button></span>);
                if (selDef.status === "aside" && base0.status === "use")
                  return (<span className="flex items-center gap-1.5 text-muted-foreground" style={{ fontSize: 9.5 }}><Check size={10} strokeWidth={3} /> set aside by you <button onClick={() => onTblOv(k, null)} className="underline underline-offset-2 text-muted-foreground/70 hover:text-foreground">undo</button></span>);
                if (selDef.status === "aside")
                  return (<><B dark onClick={() => onTblOv(k, { approved: true })}>Approved, keep it out</B><B onClick={() => onTblOv(k, { status: "use" })}>Extract this table too</B></>);
                if (base0.status !== "use")
                  return (<span className="flex items-center gap-1.5 text-muted-foreground" style={{ fontSize: 9.5 }}><Check size={10} strokeWidth={3} /> kept by you <button onClick={() => onTblOv(k, null)} className="underline underline-offset-2 text-muted-foreground/70 hover:text-foreground">undo</button></span>);
                return (<B onClick={() => onTblOv(k, { status: "aside" })}>Set this table aside</B>);
              })()}
              <span className="flex items-center gap-1 text-muted-foreground/70" style={{ fontSize: 9 }}>
                range rows {selDef.start + 1} to {selDef.end + 1}
                <button onClick={() => nudge("start", -1)} title="Range starts one row earlier" className="rounded border border-border px-1 hover:border-foreground">−</button>
                <button onClick={() => nudge("start", 1)} title="Range starts one row later" className="rounded border border-border px-1 hover:border-foreground">+</button>
                <span className="px-0.5">to</span>
                <button onClick={() => nudge("end", -1)} title="Range ends one row earlier" className="rounded border border-border px-1 hover:border-foreground">−</button>
                <button onClick={() => nudge("end", 1)} title="Range ends one row later" className="rounded border border-border px-1 hover:border-foreground">+</button>
              </span>
              <button onClick={() => setSelT(null)} className="text-muted-foreground/70 hover:text-foreground"><X size={11} /></button>
            </>
          )}
        </div>
      )}

      <div className="flex-1 min-h-0 bg-card rounded-xl border border-border/60 p-3 flex flex-col">
        {noData ? (
          <div className="flex-1 flex items-center justify-center text-muted-foreground/70 italic" style={{ fontSize: 11.5 }}>
            Nothing extractable, {f.q ? "the file could not be read" : "images or blank content only"}.
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-auto">
            <WorkbenchSheet f={{ ...f, name: shownName }} baseName={f.name} variant={variant} hi={hi && pair && pair.nDiff > 0} diffRef={diffRef} kindHint={pair && pair.kindHint}
              tblOv={Object.fromEntries(Object.entries(tblOv).filter(([k]) => k.startsWith(f.id + ":")).map(([k, v]) => [k, v]))}
              selT={selT} onSelT={(tid) => setSelT(selT === tid ? null : tid)} refsMap={refsMap} />
          </div>
        )}
      </div>
    </div>
  );
}

function buildSheet(f, baseName, variant, kindHint) {
  const base = (baseName || f.name).replace(/ ?\((copy \d|conflict copy|email copy|1|in use)\)| v\d|_v\d(_final)?| draft/gi, "");
  const seed = [...base].reduce((a, c) => a + c.charCodeAt(0), 0);
  const rnd = (i, j) => ((seed * (i + 3) * (j + 7)) % 8971) / 8971;
  const money = (x, dp = 2) => x.toLocaleString("en-GB", { minimumFractionDigits: dp, maximumFractionDigits: dp });
  const n = f.name;
  const kind = kindHint || (/Trial balance|^TB/.test(n) ? "tb" : /Mgmt|Management/.test(n) ? "ma" : /Sales ledger|Credit notes/.test(n) ? "ar"
    : /Purchase/.test(n) ? "ap" : /Bank/.test(n) ? "bank" : /Payroll/.test(n) ? "pay" : /Stock/.test(n) ? "stock" : "tb");
  if (!["tb", "ma"].includes(kind)) return null;
  const per = f.m || "Mar-25";
  const entName = (LEGAL_ENTS.find((e) => e.id === f.ent) || { name: "Start Demo NZ Limited" }).name;
  const kindLabel = { tb: "Trial balance", ma: "Management accounts" }[kind];
  const rows = [];
  const tables = [];
  const R = (cells, opt = {}) => rows.push({ cells, ...opt });
  const openT = (t) => { rows.push({ tlabel: t }); t.start = rows.length; };
  const closeT = (t) => { t.end = rows.length - 1; tables.push(t); };
  R([{ t: "Source: " + drFolderName(f.folder) + " - " + f.name, span: 9 }], { band: true });
  R([{ t: "" }]);
  const tHead = { tid: "head", label: "Header block", status: "use", conf: 99, reason: "labels only, no values extracted" };
  openT(tHead);
  R([{ t: "Client", b: 1 }, { t: entName, b: 1 }], { dark: true });
  R([{ t: "Account", b: 1 }, { t: kindLabel }]);
  R([{ t: "Period", b: 1 }, { t: per }]);
  closeT(tHead);
  R([{ t: "" }]);
  const bump = variant === "plus" ? 12400 : 0;
  if (kind === "tb") {
    const tMain = { tid: "main", label: "Trial balance, 412 accounts", status: "use", conf: 98, reason: "feeds every statement and the tie-out checks" };
    openT(tMain);
    const acc = [["1000", "Trade receivables"], ["1200", "Inventory, finished goods"], ["1400", "Prepayments"], ["1500", "Leasehold improvements at cost"], ["1510", "Accumulated depreciation, leasehold"], ["2000", "Trade payables"], ["2100", "GST payable"], ["2200", "PAYE payable"], ["2400", "Accrued expenses"], ["4000", "Revenue, domestic"], ["4100", "Revenue, export"], ["5000", "Cost of goods sold"], ["6010", "Freight out"], ["6100", "Salaries and wages"], ["6200", "Rent and outgoings"], ["6300", "Repairs and maintenance"], ["6400", "Insurance"], ["6450", "Other operating expenses"]];
    let sd = 0, sc = 0;
    R([{ t: "Code", b: 1 }, { t: "Account name", b: 1 }, { t: "Debit", b: 1, r: 1 }, { t: "Credit", b: 1, r: 1 }], { head: true });
    acc.forEach(([code, name], i) => {
      let v = 12000 + rnd(i, 2) * 880000;
      const isDiff = code === "6450";
      if (isDiff) v += bump;
      const isDr = Number(code) < 2000 || Number(code) >= 5000;
      if (isDr) sd += v; else sc += v;
      R([{ t: code }, { t: name }, { t: isDr ? money(v) : "", r: 1, dc: isDiff }, { t: isDr ? "" : money(v), r: 1 }], { diff: isDiff });
    });
    R([{ t: "3000" }, { t: "Retained earnings" }, { t: "", r: 1 }, { t: money(sd - sc), r: 1 }]);
    R([{ t: "" }, { t: "Total", b: 1 }, { t: money(sd), b: 1, r: 1, top: true }, { t: money(sd), b: 1, r: 1, top: true }]);
    closeT(tMain);
    R([{ t: "" }]);
    const tNotes = { tid: "notes", label: "Working notes", status: "aside", conf: 96, reason: "free-text notes, no financial values" };
    openT(tNotes);
    R([{ t: "Prepared by" }, { t: "S. Naidoo, finance" }]);
    R([{ t: "Basis" }, { t: "Accrual, USD, entity ledger" }]);
    R([{ t: "Review" }, { t: "pending sign-off" }]);
    closeT(tNotes);
  } else {
    const ms = ["Nov-24", "Dec-24", "Jan-25", "Feb-25", per];
    const tPl = { tid: "pl", label: "Monthly P&L", status: "use", conf: 97, reason: "feeds the monthly P&L and seasonality" };
    openT(tPl);
    R([{ t: "USD", b: 1 }, ...ms.map((m) => ({ t: m, b: 1, r: 1 }))], { head: true });
    const line = (name, lo, hiV, i, neg, diffLast) => {
      const vs = ms.map((_, j) => {
        let v = (lo + rnd(i, j) * (hiV - lo)) * (neg ? -1 : 1);
        if (diffLast && j === ms.length - 1) v -= bump;
        return v;
      });
      R([{ t: name }, ...vs.map((v, j) => ({ t: money(v, 0), r: 1, dc: diffLast && j === ms.length - 1 }))], { diff: !!diffLast });
      return vs;
    };
    const rev = line("Revenue", 780000, 980000, 1);
    const cogs = line("Cost of sales", 380000, 470000, 2, true);
    R([{ t: "Gross profit", b: 1 }, ...rev.map((v, j) => ({ t: money(v + cogs[j], 0), b: 1, r: 1, top: true }))]);
    line("Distribution", 42000, 58000, 3, true);
    line("People", 120000, 150000, 4, true);
    line("Occupancy", 28000, 33000, 5, true);
    line("Other opex", 22000, 41000, 6, true, true);
    closeT(tPl);
    R([{ t: "" }]);
    const tKpi = { tid: "kpi", label: "KPI mini-table", status: "approve", conf: 71, reason: "looks duplicated from the KPI pack, planned to be set aside" };
    openT(tKpi);
    R([{ t: "KPI", b: 1 }, ...ms.map((m) => ({ t: m, b: 1, r: 1 }))], { head: true });
    R([{ t: "Cases sold" }, ...ms.map((_, j) => ({ t: Math.round(41000 + rnd(9, j) * 9000).toLocaleString("en-GB"), r: 1 }))]);
    R([{ t: "Average price" }, ...ms.map((_, j) => ({ t: money(19 + rnd(8, j) * 4), r: 1 }))]);
    closeT(tKpi);
  }
  return { rows, tables, kind };
}

function WorkbenchSheet({ f, baseName, variant, hi, diffRef, kindHint, tblOv = {}, selT, onSelT, refsMap }) {
  const built = buildSheet(f, baseName, variant, kindHint);
  if (!built) return <ExtractGrid f={f} />;
  const { rows, tables } = built;
  const effT = (t) => {
    const ov = tblOv[f.id + ":" + t.tid] || {};
    return { ...t, status: ov.status || t.status, approved: !!ov.approved, start: ov.start != null ? ov.start : t.start, end: ov.end != null ? ov.end : t.end };
  };
  const ets = tables.map(effT);
  const rowT = (ri) => ets.find((t) => ri >= t.start && ri <= t.end);
  const bStyle = (t) => (t.status === "use" ? "2px solid hsl(var(--foreground))" : t.status === "approve" ? "2px dashed hsl(var(--foreground))" : "2px dashed hsl(var(--muted-foreground))");
  const nCols = Math.max(...rows.map((r) => (r.cells ? r.cells.reduce((a, c) => a + (c.span || 1), 0) : 1)));
  const gcell = { fontFamily: "'IBM Plex Mono', monospace", fontSize: 9.5, border: "1px solid hsl(var(--border))", padding: "2px 7px", whiteSpace: "nowrap" };
  return (
    <table className="border-collapse w-full">
      <thead>
        <tr>
          <th style={{ ...gcell, width: 24 }} className="bg-muted/50 sticky top-0" />
          {Array.from({ length: nCols }, (_, i) => (
            <th key={i} style={gcell} className="bg-muted/50 text-muted-foreground/70 font-normal text-center sticky top-0">{String.fromCharCode(65 + i)}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, ri) => {
          if (r.tlabel) {
            const t = effT(r.tlabel);
            return (
              <tr key={ri} ref={(el) => { if (refsMap) refsMap.current[t.tid] = el; }}>
                <td style={gcell} className="bg-muted/50 text-muted-foreground/70 text-center">{ri + 1}</td>
                <td style={{ ...gcell, border: "1px solid hsl(var(--background))" }} colSpan={nCols}>
                  <button onClick={() => onSelT && onSelT(t.tid)}
                    className={"inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 " + (selT === t.tid ? "bg-foreground text-background" : "bg-muted text-muted-foreground hover:bg-muted")}
                    style={{ fontSize: 8.5, fontFamily: "inherit" }}>
                    <span className={"w-1.5 h-1.5 rounded-full " + (t.status === "use" ? (selT === t.tid ? "bg-card" : "bg-foreground") : t.status === "approve" ? "border border-current" : "bg-muted-foreground")} />
                    {t.label}, {t.status === "use" ? "in use" : t.status === "approve" ? "removal needs approval" : t.approved ? "set aside, approved" : "set aside"}, {t.conf}%
                  </button>
                </td>
              </tr>
            );
          }
          const t = rowT(ri);
          const first = t && ri === t.start, last = t && ri === t.end;
          const selRow = t && selT === t.tid;
          return (
            <tr key={ri} ref={r.diff ? diffRef : undefined} className={(hi && r.diff ? "bg-muted" : "") + (selRow ? " bg-muted/50" : "")}>
              <td style={gcell} className="bg-muted/50 text-muted-foreground/70 text-center">{ri + 1}</td>
              {r.cells.map((cc, ci) => {
                const isLastCell = ci === r.cells.length - 1 && r.cells.reduce((a, x) => a + (x.span || 1), 0) >= nCols;
                return (
                  <td key={ci} colSpan={cc.span || 1}
                    style={{ ...gcell,
                      fontWeight: cc.b || r.head || (hi && r.diff) ? 700 : 400,
                      borderTop: first && t ? bStyle(t) : cc.top ? "1px solid hsl(var(--foreground))" : gcell.border,
                      borderBottom: last && t ? bStyle(t) : gcell.border,
                      borderLeft: t && ci === 0 ? bStyle(t) : gcell.border,
                      borderRight: t && isLastCell ? bStyle(t) : gcell.border,
                      boxShadow: hi && cc.dc ? "inset 0 0 0 1.5px hsl(var(--foreground))" : undefined }}
                    className={(r.band ? "bg-foreground/90 text-background text-center " : r.dark ? "bg-foreground text-background " : r.head ? "bg-muted " : "") + (cc.r ? "text-right" : "text-left")}>
                    {cc.t}
                  </td>
                );
              })}
              {Array.from({ length: nCols - r.cells.reduce((a, cc) => a + (cc.span || 1), 0) }, (_, i, arr) => (
                <td key={"pad" + i} style={{ ...gcell,
                  borderTop: first && t ? bStyle(t) : gcell.border,
                  borderBottom: last && t ? bStyle(t) : gcell.border,
                  borderRight: t && i === arr.length - 1 ? bStyle(t) : gcell.border }}
                  className={r.dark ? "bg-foreground" : ""} />
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function ActionCard({ f, dir, onConfirm, heading }) {
  const [note, setNote] = useState("");
  const [noting, setNoting] = useState(false);
  const [open, setOpen] = useState(dir === "add");
  const plan = dir === "remove"
    ? ["Set aside", "Its values come out of every report and the checks re-run.", "excluded from the reports"]
    : f.cat === "super"
    ? ["Use this version instead", "This swaps it in for the version currently in use and recomputes everything.", "swapped for the version in use"]
    : f.cat === "dup"
    ? ["Add anyway", "Same values as the file already in use, duplicates are removed so nothing double counts.", "de-duplicated on the way in"]
    : f.cat === "nodata" || f.cat === "scope"
    ? ["Add as reference", "It sits alongside the analysis for context and never feeds a number.", "reference only, no numbers"]
    : ["Add to the analysis", "It is extracted, placed where it belongs, and everything recomputes.", "extracted as a new source"];
  if (!open)
    return (
      <div className="shrink-0">
        <button onClick={() => setOpen(true)} className="rounded-lg border border-border px-2.5 py-1.5 text-muted-foreground hover:border-foreground bg-card" style={{ fontSize: 11 }}>
          Set this file aside
        </button>
      </div>
    );
  return (
    <div className="bg-card rounded-xl border border-border/60 px-3.5 py-3 shrink-0">
      {heading && <div className="font-semibold mb-1.5" style={{ fontSize: 12 }}>{heading}</div>}
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={() => onConfirm({ use: dir === "add", how: plan[2], note: note.trim() })}
          className="rounded-lg bg-foreground text-background px-3 py-1.5 font-medium hover:bg-foreground/90 whitespace-nowrap" style={{ fontSize: 11.5 }}>
          {plan[0]}
        </button>
        <span className="text-muted-foreground flex-1" style={{ fontSize: 11, lineHeight: 1.5, minWidth: 220 }}>{plan[1]}</span>
        {!noting && (
          <button onClick={() => setNoting(true)} className="text-muted-foreground/70 hover:text-foreground underline underline-offset-2 whitespace-nowrap" style={{ fontSize: 10 }}>
            add a note, optional
          </button>
        )}
      </div>
      {noting && (
        <textarea value={note} onChange={(e) => setNote(e.target.value)} autoFocus placeholder="A line for the file log, optional"
          className="w-full bg-muted rounded-lg px-2.5 py-2 outline-none resize-none mt-2" style={{ fontSize: 11.5, height: 46 }} />
      )}
    </div>
  );
}

function ExtractGrid({ f }) {
  const base = f.name.replace(/ ?\((copy \d|conflict copy|email copy|1)\)| v\d| draft/gi, "");
  const vm = f.name.match(/ v(\d)/);
  const seed = [...base].reduce((a, c) => a + c.charCodeAt(0), 0) + (f.cat === "super" && vm ? Number(vm[1]) : 0);
  const rnd = (i, j) => ((seed * (i + 3) * (j + 7)) % 8971) / 8971;
  const money = (x, dp = 2) => x.toLocaleString("en-GB", { minimumFractionDigits: dp, maximumFractionDigits: dp });
  const n = f.name;
  const kind = /Trial balance|^TB/.test(n) ? "tb" : /Mgmt|Management/.test(n) ? "ma" : /Sales ledger|Credit notes/.test(n) ? "ar"
    : /Purchase/.test(n) ? "ap" : /Bank/.test(n) ? "bank" : /Payroll/.test(n) ? "pay" : /Stock/.test(n) ? "stock" : "tb";
  const per = f.m || "Mar-25";
  const entName = (LEGAL_ENTS.find((e) => e.id === f.ent) || { name: "Start Demo NZ Limited" }).name;
  const kindLabel = { tb: "Trial balance", ma: "Management accounts", ar: "Sales ledger", ap: "Purchase ledger", bank: "Bank statement, ANZ 00-4471", pay: "Payroll summary", stock: "Inventory listing" }[kind];

  const rows = [];
  const R = (cells, opt = {}) => rows.push({ cells, ...opt });
  R([{ t: "Source: " + drFolderName(f.folder) + " - " + f.name, span: 9 }], { band: true });
  R([{ t: "" }], {});
  R([{ t: "Client", b: 1 }, { t: entName, b: 1 }], { dark: true });
  R([{ t: "Account", b: 1 }, { t: kindLabel }]);
  R([{ t: "Period", b: 1 }, { t: per }]);
  R([{ t: "" }]);

  if (kind === "tb") {
    const acc = [["1000", "Trade receivables"], ["1200", "Inventory, finished goods"], ["1400", "Prepayments"], ["1500", "Leasehold improvements at cost"], ["1510", "Accumulated depreciation, leasehold"], ["2000", "Trade payables"], ["2100", "GST payable"], ["2200", "PAYE payable"], ["2400", "Accrued expenses"], ["4000", "Revenue, domestic"], ["4100", "Revenue, export"], ["5000", "Cost of goods sold"], ["6010", "Freight out"], ["6100", "Salaries and wages"], ["6200", "Rent and outgoings"], ["6300", "Repairs and maintenance"], ["6400", "Insurance"]];
    R([{ t: "Code", b: 1 }, { t: "Account name", b: 1 }, { t: "Debit", b: 1, r: 1 }, { t: "Credit", b: 1, r: 1 }], { head: true });
    let sd = 0, sc = 0;
    acc.forEach(([c, name], i) => {
      const v = 12000 + rnd(i, 2) * 880000;
      const isDr = Number(c) < 2000 || Number(c) >= 5000;
      if (isDr) sd += v; else sc += v;
      R([{ t: c }, { t: name }, { t: isDr ? money(v) : "", r: 1 }, { t: isDr ? "" : money(v), r: 1 }]);
    });
    R([{ t: "3000" }, { t: "Retained earnings" }, { t: "", r: 1 }, { t: money(sd - sc), r: 1 }]);
    R([{ t: "" }, { t: "Total", b: 1 }, { t: money(sd), b: 1, r: 1, top: true }, { t: money(sd), b: 1, r: 1, top: true }]);
  } else if (kind === "ma") {
    const ms = ["Nov-24", "Dec-24", "Jan-25", "Feb-25", per];
    R([{ t: "USD", b: 1 }, ...ms.map((m) => ({ t: m, b: 1, r: 1 }))], { head: true });
    const line = (name, lo, hi, i, neg) => { const vs = ms.map((_, j) => (lo + rnd(i, j) * (hi - lo)) * (neg ? -1 : 1)); R([{ t: name }, ...vs.map((v) => ({ t: money(v, 0), r: 1 }))]); return vs; };
    const rev = line("Revenue", 780000, 980000, 1);
    const cogs = line("Cost of sales", 380000, 470000, 2, true);
    R([{ t: "Gross profit", b: 1 }, ...rev.map((v, j) => ({ t: money(v + cogs[j], 0), b: 1, r: 1, top: true }))]);
    const ops = [line("Distribution", 42000, 58000, 3, true), line("People", 120000, 150000, 4, true), line("Occupancy", 28000, 33000, 5, true), line("Other opex", 22000, 41000, 6, true)];
    R([{ t: "EBITDA", b: 1 }, ...rev.map((v, j) => ({ t: money(v + cogs[j] + ops.reduce((a, o) => a + o[j], 0), 0), b: 1, r: 1, top: true }))]);
  } else if (kind === "ar" || kind === "ap") {
    const names = kind === "ar" ? ["Foodstuffs NI", "Foodstuffs SI", "Woolworths NZ", "Bunnings AU", "RD1 Farm Supplies", "Farmlands Co-op", "PGG Wrightson", "Elders Rural AU"] : ["Fonterra Farm Source", "Mainfreight Ltd", "Genesis Energy", "Visy Packaging", "Ballance Agri", "OfficeMax NZ", "Toll Group AU", "Hirepool Ltd"];
    R([{ t: "Date", b: 1 }, { t: kind === "ar" ? "Invoice" : "Bill", b: 1 }, { t: kind === "ar" ? "Customer" : "Supplier", b: 1 }, { t: "Amount incl GST", b: 1, r: 1 }], { head: true });
    let s = 0;
    for (let i = 0; i < 16; i++) {
      const v = 800 + rnd(i, 3) * 42000; s += v;
      R([{ t: (1 + (i * 2) % 28) + " " + per.slice(0, 3) }, { t: (kind === "ar" ? "INV-" : "BILL-") + (10240 + seed % 400 + i * 7) }, { t: names[i % names.length] }, { t: money(v), r: 1 }]);
    }
    R([{ t: "" }, { t: "" }, { t: "Total", b: 1 }, { t: money(s), b: 1, r: 1, top: true }]);
  } else if (kind === "bank") {
    const ds = ["Foodstuffs NI remittance", "Payroll batch", "IRD GST", "Mainfreight Ltd", "Genesis Energy", "Fonterra Farm Source", "Woolworths NZ remittance", "Transfer to call account", "Visy Packaging", "Rent, Hamilton site"];
    R([{ t: "Date", b: 1 }, { t: "Description", b: 1 }, { t: "Money out", b: 1, r: 1 }, { t: "Money in", b: 1, r: 1 }, { t: "Balance", b: 1, r: 1 }], { head: true });
    let bal = 180000 + rnd(0, 9) * 120000;
    for (let i = 0; i < 16; i++) {
      const isIn = i % 3 === 0;
      const v = 1200 + rnd(i, 5) * 68000;
      bal += isIn ? v : -v;
      R([{ t: (1 + (i * 2) % 28) + " " + per.slice(0, 3) }, { t: ds[i % ds.length] }, { t: isIn ? "" : money(v), r: 1 }, { t: isIn ? money(v) : "", r: 1 }, { t: money(bal), r: 1 }]);
    }
  } else if (kind === "pay") {
    const emp = [["A. Kaur", "Machine operator"], ["T. Ngata", "Line lead"], ["S. Williams", "Warehouse"], ["M. Chen", "Sales rep"], ["J. Parata", "Accounts"], ["R. Singh", "Production manager"], ["L. Thompson", "Machine operator"], ["H. van Dyk", "Warehouse"], ["K. O'Brien", "Sales rep"], ["P. Fa'alogo", "Line lead"]];
    R([{ t: "Employee", b: 1 }, { t: "Role", b: 1 }, { t: "Gross", b: 1, r: 1 }, { t: "PAYE", b: 1, r: 1 }, { t: "KiwiSaver", b: 1, r: 1 }, { t: "Net", b: 1, r: 1 }], { head: true });
    let sg = 0;
    emp.forEach(([nm, role], i) => {
      const g = 4200 + rnd(i, 4) * 4800; sg += g;
      R([{ t: nm }, { t: role }, { t: money(g), r: 1 }, { t: money(g * 0.21), r: 1 }, { t: money(g * 0.03), r: 1 }, { t: money(g * 0.76), r: 1 }]);
    });
    R([{ t: "" }, { t: "Total", b: 1 }, { t: money(sg), b: 1, r: 1, top: true }, { t: money(sg * 0.21), b: 1, r: 1, top: true }, { t: money(sg * 0.03), b: 1, r: 1, top: true }, { t: money(sg * 0.76), b: 1, r: 1, top: true }]);
  } else {
    const sk = ["Peach Teat Pink, 10 pack", "Peach Teat Red, single", "Peach Teat Pink, single", "Screw-on 20 pack", "Pull-on multi fit", "Big Softy, single", "Excal fitting kit", "Peach Teat display carton", "Lamb teat, 5 pack", "Calf feeder 6 port"];
    R([{ t: "SKU", b: 1 }, { t: "Description", b: 1 }, { t: "Qty", b: 1, r: 1 }, { t: "Unit cost", b: 1, r: 1 }, { t: "Value", b: 1, r: 1 }], { head: true });
    let sv = 0;
    sk.forEach((d, i) => {
      const q = Math.round(200 + rnd(i, 6) * 5200), c = 1.4 + rnd(i, 7) * 6.5, v = q * c; sv += v;
      R([{ t: "PT-" + (1100 + i * 3 + (seed % 9)) }, { t: d }, { t: q.toLocaleString("en-GB"), r: 1 }, { t: money(c), r: 1 }, { t: money(v), r: 1 }]);
    });
    R([{ t: "" }, { t: "Total", b: 1 }, { t: "", r: 1 }, { t: "", r: 1 }, { t: money(sv), b: 1, r: 1, top: true }]);
  }

  const nCols = Math.max(...rows.map((r) => r.cells.reduce((a, c) => a + (c.span || 1), 0)));
  const gcell = { fontFamily: "'IBM Plex Mono', monospace", fontSize: 9.5, border: "1px solid hsl(var(--border))", padding: "2px 7px", whiteSpace: "nowrap" };
  return (
    <table className="border-collapse w-full">
      <thead>
        <tr>
          <th style={{ ...gcell, width: 24 }} className="bg-muted/50 sticky top-0" />
          {Array.from({ length: nCols }, (_, i) => (
            <th key={i} style={gcell} className="bg-muted/50 text-muted-foreground/70 font-normal text-center sticky top-0">{String.fromCharCode(65 + i)}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, ri) => (
          <tr key={ri}>
            <td style={gcell} className="bg-muted/50 text-muted-foreground/70 text-center">{ri + 1}</td>
            {r.cells.map((c, ci) => (
              <td key={ci} colSpan={c.span || 1}
                style={{ ...gcell, fontWeight: c.b || r.head ? 700 : 400, borderTop: c.top ? "1px solid hsl(var(--foreground))" : gcell.border.split(" ").slice(-1) && c.top ? "1px solid hsl(var(--foreground))" : undefined }}
                className={(r.band ? "bg-foreground/90 text-background text-center " : r.dark ? "bg-foreground text-background " : r.head ? "bg-muted " : "") + (c.r ? "text-right" : "text-left")}>
                {c.t}
              </td>
            ))}
            {Array.from({ length: nCols - r.cells.reduce((a, c) => a + (c.span || 1), 0) }, (_, i) => (
              <td key={"pad" + i} style={gcell} className={r.dark ? "bg-foreground" : ""} />
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
